// when creating new portfolio actions, make sure to add them to portfolioActions array in Common.js to make sure subuser suffix logic works correctly
import { batch } from "react-redux";
import eventBus from "../../eventBus";
import { store } from "../../store";
import { ApiClient } from "../../api/ApiClient";
import merge from "lodash.merge";
import mergeWith from "lodash.mergewith";
import {
  getUuid,
  getSortKeyBetween,
  getFriendlyDateString,
  setSortKey,
  months,
  getDateInKuberaFormat,
  getCustodianHistoryFormattedDateString,
  sanitizeIrr,
  getValueOfPropFromStringifiedJSON,
  getPercentageValue,
  getPercentageValueForRecap,
  getTodayMidnight,
  parseKuberaDateString,
  getKuberaDateString,
  converUIDateFormatToCustodianHistoryApiDateFormat
} from "../../utilities/Number";
import { CUSTODIAN_SYNC_API_KEYS, CUSTODIAN_CASHFLOW_API_KEYS, FUND_SCHEDULE_API_KEYS } from "./CustodianSyncApiKeys";
import { isMobile, parseParams } from "../../utilities/Location";
import { checkIfBannerPreferenceShouldBeSetNull } from "../../utilities/Auth";
import requestIdleCallback from "utilities/requestIdleCallback";
import {
  enqueueItem,
  SyncItem,
  SyncItemType,
  addPendingDocumentUploadAction,
  removePendingDocumentUploadAction,
  updatePendingDocumentUploadAction
} from "./SyncActions";
import { Toast, showToastAction, dismissToastAction, updateToastAction } from "./ToastActions";
import {
  custodianPortfolioSelector,
  portfolioSelector,
  portfolioLastForceUpdateTsSelector,
  sectionSelector,
  copyParentInfoToChild,
  custodiansWithSameParentIdSelector,
  sectionCustodiansSelector,
  sectionAssociatedCustodiansSelector,
  sheetSectionsSelector,
  sheetAssociatedCustodiansSelector,
  sheetCustodiansSelector,
  getNetWorthChartStartDateForPortfolio,
  showRefreshingSelector,
  sheetSelector,
  getTotalForSection,
  getTotalForSheet,
  initialFetchSuccessSelector,
  undoQueue,
  redoQueue,
  getChangeTotalsWithContributorsForCurrentPortfolio,
  fetchPortfolioPendingSelector,
  getCustodiansLinkedWithProviderAccountId,
  getNonEmptyCustodiansInSection,
  getEmptyCustodiansInSection,
  connectivityCenterDataForPortfolioSelector,
  sectionPortfolioSelector,
  sheetPortfolioSelector,
  isCustodianEmpty,
  portfolioChangeDataLastForceUpdateTsSelector,
  dashboardChartIdsSelector,
  connectedPortfoliosSelector,
  portfolioPredecessorsSelector,
  primaryResidenceCustodianSelector,
  currentPortfolioChangeDataSelector,
  portfolioTreeDataSelector,
  portfolioNetWorth,
  verifiedConnectionKeysSelector,
  getRecapChartOptionForPortfolio,
  getRecapChartTimeRangeForPortfolio
} from "../reducers/PortfolioReducer";
import {
  getPortfolioSessionUserId,
  userPreferencesSelector,
  portfolioCurrencySelector,
  currentPortfolioSelector,
  custodianSelector,
  portfoliosSelector,
  setPortfolioSessionUserId,
  getLastUsedPortfolioUserId,
  setLastUsedPortfolioUserId,
  recapDataSelector,
  recapDataForPortfolioSelector,
  chartTimeRange,
  checkIfAChartOptionHasNoDataToShow,
  recapChartOptions,
  recapReportNameSelector,
  UNKNOWN_TICKER_SHORT_NAME,
  UNKNOWN_TICKER_ID,
  chartContent,
  convertCurrency,
  getContentsTabTitle,
  getExchangeRate,
  getTickerUsingId,
  getTickerUsingShortName,
  parseNumberStringToFloat,
  getComparisonReportsTabTitleSelector,
  reportPreferencesSelector,
  supportedTickerIdMapSelector,
  RECAP_CATEGORY_TYPE_ASSET,
  RECAP_CATEGORY_TYPE_DEBT,
  RECAP_CATEGORY_TYPE_FIAT_ASSET,
  recapRawDataSelector,
  userSelector,
  isSharedPortfolioUserSelector,
  currentPortfolioCurrencySelector,
  findPortfolioUsingCustodianIdSelector,
  custodianSheetSelector,
  getCustodianValue,
  getCustodianValueById,
  PVST_VALUE_TICKER_ID,
  categoryType,
  calcCustodianOwnershipValue,
  isCryptoLinkingService,
  accountLinkingService,
  VERIFIED_CONNECTION_MILESTONES
} from "../reducers/Common";
import { getPreviousSyncUpdateSelector } from "../reducers/SyncReducer";
import { wlClientContextSelector } from "../reducers/WhiteLabelReducer";
import {
  isReadOnlyWlClient,
  accountSubscriptionIsActiveSelector,
  sharedPortfolioUsersSelector
} from "../reducers/AuthReducer";
import { fetchWlDashboard } from "./WhiteLabelActions";
import {
  updateUserTimezoneAndOffset,
  setUserPreferencesAction,
  updateUserPreferences,
  getMultiuserList,
  switchToSharedPortfolioUserAccount,
  deferUntilUserIsSet
} from "./AuthActions";
import { updateBeneficiaryAction } from "./BeneficiaryActions";
import {
  updateTickerDataAction,
  getTickersForText,
  addTickerInfoAction,
  refreshTickerData,
  getTickersForIds,
  convertPVSTRateToValueExchangeRate
} from "./TickerActions";
import { apiErrorCodes } from "../../api/ApiResponse";
import i18n from "i18next";
import { captureError } from "../../utilities/CaptureError";
import {
  isAppInViewMode,
  isAppInWhiteLabelMode,
  isAppInProofViewMode,
  isAppInExtensionMode
} from "../../utilities/Common";
import { runShareChallenge, PROOF_CHALLENGE_ACTION } from "../../utilities/ShareChallenge";
import { custodianDetailsSelector } from "../reducers/CustodianDetailsReducer";
import RacePromiseWrapper from "../../utilities/RacePromiseWrapper";
import { markDirtyField, dirtyItemTypes } from "../../utilities/DirtyFieldTracker";
import {
  LAST_FETCH_MAX_AGE,
  updateCustodianDetailsAction,
  getAccountLinkingService,
  isZaboToInHouseApiCandidate,
  isZaboToInHouseOauthCandidate,
  toastType,
  shortFormatNumberWithCurrency,
  SET_SLIDE_DIRECTION,
  UPDATE_CURRENT_PORTFOLIO,
  FETCH_PORTFOLIOS_PENDING,
  UPDATE_FETCH_HOLDINGS_STATUS,
  UPDATE_ALL_CONNECTED_CUSTODIANS_REFRESH_STATUS,
  INITIAL_FETCH_PORTFOLIOS_SUCCESS,
  FETCH_PORTFOLIOS_ERROR,
  sortPortfolio,
  FETCH_PORTFOLIO_CHANGE_DATA_PENDING,
  FETCH_PORTFOLIOS_SUCCESS,
  UPDATE_CUSTODIAN_BULK,
  FETCH_PORTFOLIO_CHANGE_DATA_ERROR,
  updateDashboardAction,
  FETCH_NET_WORTH_DATA_PENDING,
  FETCH_RECAP_DATA_PENDING,
  getChartDataFromResponse,
  GET_CONNECTIVITY_CENTER_DATA_ERROR,
  getRecapWorkerConsts,
  UPDATE_RECAP,
  FETCH_NET_WORTH_DATA_ERROR,
  getYTDStartDate,
  getYTDStartDateString,
  getCurrentDateDifferenceFromYTD,
  FETCH_NET_WORTH_DATA_SUCCESS,
  REFRESH_CUSTODIAN_DONE,
  SET_PORTFOLIO_LAST_FORCE_REFRESH_TS,
  SET_PORTFOLIO_CHANGE_DATA_LAST_FORCE_REFRESH_TS,
  FETCH_RECAP_DATA_SUCCESS,
  FETCH_VANITY_STAT_SUCCESS,
  SET_LAST_VIEWED_NETWORTH_VANITY,
  UPDATE_SECTION,
  INSERT_PORTFOLIO,
  DELETE_PORTFOLIO,
  INSERT_CUSTODIAN_BULK,
  BULK_CHANGE_CUSTODIAN_STAR_STATUS,
  BULK_CHANGE_CUSTODIAN_UPDATED_STATUS,
  DELETE_CUSTODIAN,
  DELETE_CUSTODIAN_BULK,
  DELETE_HOLDING_BULK,
  INSERT_HOLDING_BULK,
  INSERT_SECTION,
  MOVE_SECTION,
  DELETE_SECTION,
  UPDATE_SHEET,
  MOVE_SHEET,
  DELETE_SHEET,
  INSERT_SHEET,
  INSERT_DOCUMENT,
  UPDATE_DOCUMENT,
  DELETE_DOCUMENT,
  SET_PROOFS,
  UPSERT_PROOF,
  REMOVE_PROOF,
  GET_CONNECTIVITY_CENTER_DATA_PENDING,
  GET_CONNECTIVITY_CENTER_DATA_SUCCESS,
  FETCH_PORTFOLIO_CHANGE_DATA_SUCCESS,
  FETCH_CHART_AND_CAGR_DATA_PENDING,
  FETCH_CHART_AND_CAGR_DATA_SUCCESS,
  FETCH_RECAP_DATA_ERROR,
  FETCH_INITIAL_RECAP_DATA_PENDING,
  FETCH_INITIAL_RECAP_DATA_ERROR,
  SET_LINK_TYPE,
  REMOVE_CONNECTION_ERRORS,
  UPDATE_CONNECTIVITY_CENTER_DATA,
  ADD_SCENARIO,
  UPDATE_SCENARIO,
  DELETE_SCENARIO,
  ADD_RULE,
  UPDATE_RULE,
  UPDATE_RULE_BULK,
  DELETE_RULE,
  UPDATE_CASH_FORECAST_DURATION,
  ADD_REPORT_PREFERENCE,
  ADD_DIY_CHART,
  DELETE_DIY_CHART,
  THEME_SUFFIX_STR,
  LOCK_PREFERENCE_STR,
  SET_SECTION_UPDATED,
  REMOVE_SECTION_UPDATED,
  SET_SHOW_REFRESHING,
  setPortfoliosAction,
  custodiansLinkedToSameAccountSelector,
  getExchangeRateDetails,
  getYTDIndexInTimeRangeArray,
  timeRanges,
  chartStyle,
  GET_FUND_SCHEDULE_DATA_SUCCESS,
  UPDATE_SHOW_CHARTS_MODAL_LOADER,
  UPDATE_RECAP_REPORT_CALCULATION_PENDING,
  FETCH_PERCENTAGE_ALLOCATION_DATA_PENDING,
  FETCH_PERCENTAGE_ALLOCATION_DATA_SUCCESS,
  recapChartTypes,
  reconstructData,
  custodianSubTypes,
  holdingSubTypes,
  getQTDStartDateString,
  getCurrentDateDifferenceFromQTD,
  UPDATE_SCENARIO_BULK,
  resolveWhenCounterReachesZero,
  UPDATE_RECAP_RAW_DATA_ACTION,
  UPDATE_PORTFOLIO_CHANGE_DATA_ACTION,
  updatePortfolioAction,
  CALCULATE_DASHBOARD_DATA_FOR_PORTFOLIO_SUCCESS,
  CALCULATE_DASHBOARD_DATA_FOR_PORTFOLIO_PENDING,
  custodianTaxTypes,
  irrTypes,
  tickerAdditionalMetaDataSelector,
  tickerSubTypes,
  getSymbolForTickerUsingShortName,
  getPortfoliosDataStorageKey,
  getPortfoliosStorePortfolioSessionUserId,
  buildGlobalClubbingNames,
  getClubbingKeyForCustodian,
  findPortfolioInTree,
  assetClassesListSelector,
  REAL_ESTATE_ASSET_CLASS_INDEX,
  defaultCustodianTickerSelector,
  isFiatTicker
} from "./Common";
import localforage from "localforage";
import { decodeArrayBufferToObject, decompressObject } from "../../utilities/CompressedSerialize";
import {
  cryptoSectorListSelector,
  stockMarketCapListSelector,
  stockRegionListSelector,
  stockSectorListSelector
} from "../reducers";
import { toUserTimezoneDate, toCivilDateString, getInstantForCivilDate } from "../../utilities/Timezone";

const isMobileDevice = isMobile();

export const isFiatManualCustodian = (state, custodian) => {
  if (
    !custodian ||
    !custodian.isManual ||
    custodian.valueTickerId === PVST_VALUE_TICKER_ID ||
    custodian.parentId ||
    custodian.linkType ||
    [tickerSubTypes.HOME, tickerSubTypes.PRIMARY_RESIDENCE, tickerSubTypes.CARS, tickerSubTypes.DOMAINS].includes(
      custodian.subType
    )
  ) {
    return false;
  }

  return isFiatTicker(getTickerUsingId(state, custodian.valueTickerId));
};

export const isRealEstateFiatManualCustodian = (state, custodian) => {
  if (!custodian?.assetClassId || !isFiatManualCustodian(state, custodian)) {
    return false;
  }

  const realEstateAssetClass = assetClassesListSelector(state)[REAL_ESTATE_ASSET_CLASS_INDEX];
  return !!realEstateAssetClass && custodian.assetClassId === realEstateAssetClass.id;
};

export const defaultLinkedPortfolioDebtSheetName = "Linked Portfolio Debts";

const getLinkCustodianPlaceholderName = (providerName, category) => {
  return providerName
    ? `${providerName}`
    : category === categoryType.ASSET
    ? "Bank/Brokerage Account"
    : "Loan/Credit Card";
};

export const kuberaNumberFormatList = [
  { label: i18n.t("kuberaNumberFormat.systemDefault.label"), locale: "systemDefault" },
  { label: i18n.t("kuberaNumberFormat.northAmerica.label"), locale: "en-US" },
  { label: i18n.t("kuberaNumberFormat.europe.label"), locale: "en-BE" },
  { label: i18n.t("kuberaNumberFormat.norwegian.label"), locale: "fr-FR" },
  { label: i18n.t("kuberaNumberFormat.switzerland.label"), locale: "de-CH" }
];

export const costType = {
  AUTO: "auto",
  MANUAL: "manual"
};

export const cashflowTypes = {
  CASHFLOW_IN: 0,
  CASHFLOW_OUT: 1
};

export const RECAP_EXPANDED_VIEW_COLUMNS = 6;

export const UPDATE_REPORT_PREFERENCE = "UPDATE_REPORT_PREFERENCE";
export const DELETE_REPORT_PREFERENCE = "DELETE_REPORT_PREFERENCE";

export const UPDATE_DIY_CHART = "UPDATE_DIY_CHART";

export const NEW_FEATURE_BANNER_RELEASE_TS = 1784021448630;
export const AI_SYNC_RELEASE_TS = 1782313640000;
export const OWNERSHIP_RELEASE_TS = 1786534799000;

export const CLARITY_URL =
  "https://www.clarity.com/?utm_source=kubera&utm_medium=affiliate&utm_campaign=kubera_platform";

export const DASHBOARD_CHART_COLUMN_NAMES = Object.freeze({
  COLUMN_1: "column1",
  COLUMN_2: "column2"
});

export const fetchPortfoliosPendingAction = isBackgroundRefresh => ({
  type: FETCH_PORTFOLIOS_PENDING,
  isBackgroundRefresh
});

export const updateFetchHoldingsStatusAction = isPending => ({
  type: UPDATE_FETCH_HOLDINGS_STATUS,
  isPending
});

export const updateAllConnectedCustodiansRefreshStatusAction = isPending => ({
  type: UPDATE_ALL_CONNECTED_CUSTODIANS_REFRESH_STATUS,
  isPending
});

export const initialFetchPortfoliosSuccessAction = () => ({
  type: INITIAL_FETCH_PORTFOLIOS_SUCCESS
});

export const fetchPortfoliosSuccessAction = portfolios => ({
  type: FETCH_PORTFOLIOS_SUCCESS,
  portfolios
});

export const fetchPortfoliosErrorAction = error => ({
  type: FETCH_PORTFOLIOS_ERROR,
  error
});

export const insertPortfolioAction = portfolio => ({
  type: INSERT_PORTFOLIO,
  portfolio
});

export const deletePortfolioAction = portfolio => ({
  type: DELETE_PORTFOLIO,
  portfolio
});

export const updateCurrentPortfolioAction = portfolioId => ({
  type: UPDATE_CURRENT_PORTFOLIO,
  portfolioId
});

export const insertCustodianInBulkAction = (portfolioId, custodians) => ({
  type: INSERT_CUSTODIAN_BULK,
  portfolioId,
  custodians
});

export const updateCustodianInBulkAction = (
  portfolioId = null,
  custodians = null,
  includeNotFoundCustodians = true,
  portfolioCustodianMap = null,
  propertiesToUpdate = null
) => ({
  type: UPDATE_CUSTODIAN_BULK,
  portfolioId,
  custodians,
  includeNotFoundCustodians,
  portfolioCustodianMap,
  propertiesToUpdate
});

export const bulkChangeCustodianStarStatusAction = (portfolioId, custodianIdArray, isStarred) => ({
  type: BULK_CHANGE_CUSTODIAN_STAR_STATUS,
  portfolioId,
  custodianIdArray,
  isStarred
});

export const bulkChangeCustodianUpdatedStatusAction = (portfolioId, custodianIdArray, isUpdated) => ({
  type: BULK_CHANGE_CUSTODIAN_UPDATED_STATUS,
  portfolioId,
  custodianIdArray,
  isUpdated
});

export const deleteCustodianAction = (portfolioId, custodianId) => ({
  type: DELETE_CUSTODIAN,
  portfolioId,
  custodianId
});

// Holding-specific: removes the given holdings from the open custodian's
// details.holdings (custodian details reducer) without touching the portfolio.
export const deleteHoldingBulkAction = holdingIds => ({
  type: DELETE_HOLDING_BULK,
  holdingIds
});

// Holding-specific: re-adds the given holdings to the open custodian's
// details.holdings (e.g. undo of a holding archive).
export const insertHoldingBulkAction = holdings => ({
  type: INSERT_HOLDING_BULK,
  holdings
});

export const deleteCustodianBulkAction = (portfolioId, custodianIds) => ({
  type: DELETE_CUSTODIAN_BULK,
  portfolioId,
  custodianIds
});

export const insertSectionAction = (portfolioId, section) => ({
  type: INSERT_SECTION,
  portfolioId,
  section
});

export const updateSectionAction = (portfolioSectionsMap, propertiesToUpdate = null) => ({
  type: UPDATE_SECTION,
  portfolioSectionsMap,
  propertiesToUpdate
});

export const moveSectionAction = (portfolioId, section, targetPortfolioId) => ({
  type: MOVE_SECTION,
  portfolioId,
  section,
  targetPortfolioId
});

export const deleteSectionAction = (portfolioId, sectionId) => ({
  type: DELETE_SECTION,
  portfolioId,
  sectionId
});

export const updateSheetAction = (portfolioSheetsMap, propertiesToUpdate = null) => ({
  type: UPDATE_SHEET,
  portfolioSheetsMap,
  propertiesToUpdate
});

export const moveSheetAction = (portfolioId, sheet, targetPortfolioId) => ({
  type: MOVE_SHEET,
  portfolioId,
  sheet,
  targetPortfolioId
});

export const deleteSheetAction = (portfolioId, sheetId) => ({
  type: DELETE_SHEET,
  portfolioId,
  sheetId
});

export const insertSheetAction = (portfolioId, sheet) => ({
  type: INSERT_SHEET,
  portfolioId,
  sheet
});

export const insertDocumentAction = (portfolioId, document) => ({
  type: INSERT_DOCUMENT,
  portfolioId,
  document
});

export const updateDocumentAction = (portfolioId, document) => ({
  type: UPDATE_DOCUMENT,
  portfolioId,
  document
});

export const deleteDocumentAction = (portfolioId, document) => ({
  type: DELETE_DOCUMENT,
  portfolioId,
  document
});

export const setProofsAction = (portfolioId, proofs) => ({
  type: SET_PROOFS,
  portfolioId,
  proofs
});

export const upsertProofAction = (portfolioId, proof) => ({
  type: UPSERT_PROOF,
  portfolioId,
  proof
});

export const removeProofAction = (portfolioId, proofId) => ({
  type: REMOVE_PROOF,
  portfolioId,
  proofId
});

export const setPortfolioLastForceRefreshTsAction = (portfolioId, timestamp) => ({
  type: SET_PORTFOLIO_LAST_FORCE_REFRESH_TS,
  portfolioId,
  timestamp
});

export const setPortfolioChangeDataLastForceRefreshTsAction = (portfolioId, timestamp) => ({
  type: SET_PORTFOLIO_CHANGE_DATA_LAST_FORCE_REFRESH_TS,
  portfolioId,
  timestamp
});

export const fetchNetWorthDataPendingAction = portfolioId => ({
  type: FETCH_NET_WORTH_DATA_PENDING,
  portfolioId
});

export const fetchNetWorthDataSuccessAction = (portfolioId, data) => ({
  type: FETCH_NET_WORTH_DATA_SUCCESS,
  portfolioId,
  data
});

export const fetchNetWorthDataErrorAction = (portfolioId, error) => ({
  type: FETCH_NET_WORTH_DATA_ERROR,
  portfolioId,
  error
});

export const getConnectivityCenterDataPendingAction = portfolioId => ({
  type: GET_CONNECTIVITY_CENTER_DATA_PENDING,
  portfolioId
});

export const getConnectivityCenterDataErrorAction = (portfolioId, error) => ({
  type: GET_CONNECTIVITY_CENTER_DATA_ERROR,
  portfolioId,
  error
});

export const getConnectivityCenterDataSuccessAction = data => ({
  type: GET_CONNECTIVITY_CENTER_DATA_SUCCESS,
  data
});

export const fetchRecapDataPendingAction = portfolioId => ({
  type: FETCH_RECAP_DATA_PENDING,
  portfolioId
});

export const updateShowChartsModalLoader = showLoader => ({
  type: UPDATE_SHOW_CHARTS_MODAL_LOADER,
  showLoader
});

export const updateRecapReportCalculationPending = isCalculating => ({
  type: UPDATE_RECAP_REPORT_CALCULATION_PENDING,
  isCalculating
});

export const fetchPercentageAllocationDataPendingAction = showLoader => ({
  type: FETCH_PERCENTAGE_ALLOCATION_DATA_PENDING,
  showLoader
});

export const fetchPercentageAllocationDataSuccessAction = (chartOption, timeRange, data) => ({
  type: FETCH_PERCENTAGE_ALLOCATION_DATA_SUCCESS,
  chartOption,
  timeRange,
  data
});

export const fetchPortfolioChangeDataPendingAction = portfolioId => ({
  type: FETCH_PORTFOLIO_CHANGE_DATA_PENDING,
  portfolioId
});

export const fetchPortfolioChangeDataSuccessAction = (portfolioId, isDayChangeData, data) => ({
  type: FETCH_PORTFOLIO_CHANGE_DATA_SUCCESS,
  portfolioId,
  isDayChangeData,
  data
});

export const updatePortfolioChangeDataAction = (portfolioId, data) => ({
  type: UPDATE_PORTFOLIO_CHANGE_DATA_ACTION,
  portfolioId,
  data
});

export const fetchChartAndCagrDataPendingAction = portfolioId => ({
  type: FETCH_CHART_AND_CAGR_DATA_PENDING,
  portfolioId
});

export const fetchChartAndCagrDataSuccessAction = (portfolioId, data) => ({
  type: FETCH_CHART_AND_CAGR_DATA_SUCCESS,
  portfolioId,
  data
});

export const fetchRecapDataSuccessAction = (portfolioId, data) => ({
  type: FETCH_RECAP_DATA_SUCCESS,
  portfolioId,
  data
});

export const fetchVanityStatSuccessAction = (portfolioId, stat) => ({
  type: FETCH_VANITY_STAT_SUCCESS,
  portfolioId,
  stat
});

export const calculateDashboardDataForPortfolioPendingAction = () => ({
  type: CALCULATE_DASHBOARD_DATA_FOR_PORTFOLIO_PENDING
});

export const calculateDashboardDataForPortfolioSuccessAction = (portfolioId, dashboardData) => ({
  type: CALCULATE_DASHBOARD_DATA_FOR_PORTFOLIO_SUCCESS,
  portfolioId,
  dashboardData
});

export const setLastViewedNetworthVanityAction = (portfolioId, networthVanity) => ({
  type: SET_LAST_VIEWED_NETWORTH_VANITY,
  portfolioId,
  networthVanity
});

export const fetchRecapDataErrorAction = (portfolioId, error) => ({
  type: FETCH_RECAP_DATA_ERROR,
  portfolioId,
  error
});

export const fetchInitialRecapDataPendingAction = portfolioId => ({
  type: FETCH_INITIAL_RECAP_DATA_PENDING,
  portfolioId
});

export const fetchInitialRecapDataErrorAction = (portfolioId, error) => ({
  type: FETCH_INITIAL_RECAP_DATA_ERROR,
  portfolioId,
  error
});
export const fetchPortfolioChangeDataErrorAction = (portfolioId, isDayChangeData, error) => ({
  type: FETCH_PORTFOLIO_CHANGE_DATA_ERROR,
  portfolioId,
  isDayChangeData,
  error
});

export const setLinkTypeAction = connectedLinkType => ({
  type: SET_LINK_TYPE,
  connectedLinkType
});

export const removeConnectionErrorsAction = (portfolioId, custodianIds) => ({
  type: REMOVE_CONNECTION_ERRORS,
  portfolioId,
  custodianIds
});

export const setLinkType = connectedLinkType => {
  return dispatch => {
    dispatch(setLinkTypeAction(connectedLinkType));
  };
};

export const refreshCustodianDone = () => ({
  type: REFRESH_CUSTODIAN_DONE
});

export const updateConnectivityCenterDataForAPortfolioAction = (portfolioId, connectivityCenterDataForAPortfolio) => ({
  type: UPDATE_CONNECTIVITY_CENTER_DATA,
  portfolioId,
  connectivityCenterDataForAPortfolio
});

export const createPlanningScenarioAction = (portfolioId, scenario, scenarioIndex) => ({
  type: ADD_SCENARIO,
  portfolioId,
  scenario,
  scenarioIndex
});

export const updatePlanningScenarioAction = (portfolioId, scenario) => ({
  type: UPDATE_SCENARIO,
  portfolioId,
  scenario
});

export const updatePlanningScenariosBulkAction = (portfolioId, scenarios) => ({
  type: UPDATE_SCENARIO_BULK,
  portfolioId,
  scenarios
});

export const deletePlanningScenarioAction = (portfolioId, scenarioId) => ({
  type: DELETE_SCENARIO,
  portfolioId,
  scenarioId
});

export const createPlanningRuleAction = (portfolioId, scenarioId, rule) => ({
  type: ADD_RULE,
  portfolioId,
  scenarioId,
  rule
});

export const updatePlanningRuleAction = (portfolioId, scenarioId, ruleId, dataToBeUpdated) => ({
  type: UPDATE_RULE,
  portfolioId,
  scenarioId,
  ruleId,
  dataToBeUpdated
});

export const updatePlanningRuleBulkAction = (portfolioId, scenarioId, rules) => ({
  type: UPDATE_RULE_BULK,
  portfolioId,
  scenarioId,
  rules
});

export const deletePlanningRuleAction = (portfolioId, ruleId) => ({
  type: DELETE_RULE,
  portfolioId,
  ruleId
});

export const updateCashForecastDurationAction = (portfolioId, duration) => ({
  type: UPDATE_CASH_FORECAST_DURATION,
  portfolioId,
  duration
});

export const updateReportPreferenceAction = (portfolioId, reportType, reportPreference) => ({
  type: ADD_REPORT_PREFERENCE,
  portfolioId,
  reportType,
  reportPreference
});

export const deleteReportPreferenceAction = (portfolioId, reportPreferenceId) => ({
  type: DELETE_REPORT_PREFERENCE,
  portfolioId,
  reportPreferenceId
});

export const updateDiyChartsAction = (portfolioId, updatedDiyChartForPortfolio) => ({
  type: ADD_DIY_CHART,
  portfolioId,
  updatedDiyChartForPortfolio
});

export const deleteDiyChartsAction = portfolioId => ({
  type: DELETE_DIY_CHART,
  portfolioId
});

export const getFundScheduleDataSuccessAction = (portfolioId, fundSchedule) => ({
  type: GET_FUND_SCHEDULE_DATA_SUCCESS,
  portfolioId,
  fundSchedule
});

export const setThemeMode = mode => {
  return dispatch => {
    localStorage.setItem(`theme${THEME_SUFFIX_STR}`, mode);

    const themePeference = {};
    themePeference[`currentThemeMode${THEME_SUFFIX_STR}`] = mode;

    dispatch(updateUserPreferences(themePeference));
  };
};

export const setLockMode = mode => {
  return dispatch => {
    localStorage.setItem(LOCK_PREFERENCE_STR, mode);

    const lockPreference = {};
    lockPreference[LOCK_PREFERENCE_STR] = mode;

    dispatch(updateUserPreferences(lockPreference));
  };
};

export const setSectionUpdated = sectionId => {
  return dispatch => {
    dispatch({
      type: SET_SECTION_UPDATED,
      sectionId
    });
  };
};

export const removeSectionUpdated = sectionId => {
  return dispatch => {
    dispatch({
      type: REMOVE_SECTION_UPDATED,
      sectionId
    });
  };
};

export const setShowRefreshing = bool => {
  return dispatch => {
    dispatch({
      type: SET_SHOW_REFRESHING,
      isRefreshing: bool
    });
  };
};

let slideIsInProgress = false;
export const setSlideDirection = dir => {
  if (slideIsInProgress) {
    return () => null;
  }

  slideIsInProgress = true;

  setTimeout(() => {
    slideIsInProgress = false;
  }, 200);
  return dispatch => {
    dispatch({
      type: SET_SLIDE_DIRECTION,
      slideDirection: dir
    });
  };
};

export const createPortfolio = (name, onSuccess, onError) => {
  return (dispatch, getState) => {
    ApiClient.createPortfolio(getUuid(), name)
      .then(apiData => {
        const portfolio = apiData.payload;
        return Promise.all([portfolio]);
      })
      .then(([portfolio]) => {
        sortPortfolio(portfolio);

        for (const section of portfolio.details.section) {
          const custodians = portfolio.details.custodian.filter(custodian => custodian.sectionId === section.id);
          // Insert 3 empty custodians if none present for a section
          if (custodians.length === 0) {
            var defaultCustodians = [
              { id: getUuid(), sectionId: section.id, sortKey: "1", autoAddedForEmptySections: true },
              { id: getUuid(), sectionId: section.id, sortKey: "2", autoAddedForEmptySections: true },
              { id: getUuid(), sectionId: section.id, sortKey: "3", autoAddedForEmptySections: true }
            ];
            portfolio.details.custodian.push(...defaultCustodians);
          }
        }

        dispatch(insertPortfolioAction(portfolio));
        onSuccess(portfolio);
      })
      .catch(apiError => {
        captureError(getState(), apiError);
        onError(apiError);
      });
  };
};

export const updatePortfolio = (updatedPortfolio, delay = 0) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatch(updatePortfolioAction(updatedPortfolio));

      if (isAppInViewMode() === false && updatedPortfolio.write === 1) {
        const user = userSelector(getState());
        const isSharedPortfolioUser = isSharedPortfolioUserSelector(getState());

        let sharedUserNotificationOption =
          isSharedPortfolioUser && updatedPortfolio.notificationOption
            ? JSON.parse(updatedPortfolio.notificationOption)?.[user.id]
            : null;
        let notificationOption = !sharedUserNotificationOption
          ? updatedPortfolio.notificationOption
          : JSON.stringify(sharedUserNotificationOption);

        const request = idempotentId =>
          ApiClient.updatePortfolio(
            idempotentId,
            updatedPortfolio.id,
            updatedPortfolio.name,
            updatedPortfolio.currency,
            updatedPortfolio.description,
            updatedPortfolio.picture,
            notificationOption,
            updatedPortfolio.taxRate,
            updatedPortfolio.tsPlanningTargetDate,
            updatedPortfolio.tsStartDate,
            updatedPortfolio.networthChartScenario
          );
        const syncItem = new SyncItem(
          SyncItemType.UPDATE,
          updatedPortfolio.id,
          request,
          delay,
          !delay === false,
          apiData => {
            resolve(apiData.payload);
          },
          apiError => {
            reject(apiError);
          }
        );
        dispatch(enqueueItem(syncItem));
      } else {
        resolve();
      }
    });
  };
};

export const updatePortfolioStartDateTs = date => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    currentPortfolio.tsStartDate = getInstantForCivilDate(getKuberaDateString(date)) / 1000;
    return dispatch(updatePortfolio(currentPortfolio));
  };
};

export const createPlanningScenario = (scenario, scenarioIndex) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    if (isAppInViewMode() === false && currentPortfolio.write === 1) {
      const queueUpdate = scenario => {
        const request = idempotentId => ApiClient.createPlanningScenario(idempotentId, currentPortfolio.id, scenario);
        const syncItem = new SyncItem(
          SyncItemType.CREATE,
          currentPortfolio.id + "addScenario" + scenario.id,
          request,
          3000,
          true,
          apiData => {}
        );
        dispatch(enqueueItem(syncItem));
      };
      dispatch(createPlanningScenarioAction(currentPortfolio.id, scenario, scenarioIndex));
      const updatedScenerio = { ...scenario };
      updatedScenerio.rule = updatedScenerio.rule.map(rule => {
        return {
          ...rule,
          data: JSON.stringify(rule.data)
        };
      });
      queueUpdate(updatedScenerio);
    }
  };
};

export const updatePlanningScenario = updatedScenario => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    if (isAppInViewMode() === false && currentPortfolio.write === 1) {
      const queueUpdate = scenario => {
        const request = idempotentId => ApiClient.updatePlanningScenario(idempotentId, currentPortfolio.id, scenario);
        const syncItem = new SyncItem(
          SyncItemType.UPDATE,
          currentPortfolio.id + "updateScenario" + updatedScenario.id,
          request,
          3000,
          true,
          apiData => {}
        );
        dispatch(enqueueItem(syncItem));
      };
      dispatch(updatePlanningScenarioAction(currentPortfolio.id, updatedScenario));
      queueUpdate(updatedScenario);
    }
  };
};

export const updatePlanningScenarioBulk = (portfolioId, updatedScenarios) => {
  return (dispatch, getState) => {
    const portfolio = portfolioSelector(getState(), portfolioId);

    if (isAppInViewMode() === false && portfolio.write === 1) {
      const queueUpdate = updatedScenarios => {
        const request = idempotentId =>
          ApiClient.updatePlanningScenarioBulk(idempotentId, portfolioId, updatedScenarios);
        const syncItem = new SyncItem(
          SyncItemType.BULK_UPDATE,
          portfolioId + "options",
          request,
          3000,
          true,
          apiData => {}
        );
        dispatch(enqueueItem(syncItem));
      };
      dispatch(updatePlanningScenariosBulkAction(portfolioId, updatedScenarios));
      queueUpdate(updatedScenarios);
    }
  };
};

export const deletePlanningScenario = scenarioId => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    if (isAppInViewMode() === false && currentPortfolio.write === 1) {
      const queueUpdate = scenarioId => {
        const request = idempotentId => ApiClient.deletePlanningScenario(idempotentId, currentPortfolio.id, scenarioId);
        const syncItem = new SyncItem(
          SyncItemType.DELETE,
          currentPortfolio.id + "options",
          request,
          0,
          false,
          apiData => {}
        );
        dispatch(enqueueItem(syncItem));
      };
      const scenarios = currentPortfolio.planning.scenario;
      const scenarioIndex = scenarios.findIndex(scenario => scenario.id === scenarioId);
      if (scenarioIndex !== -1) {
        const deletedScenario = scenarios[scenarioIndex];
        const toast = new Toast(
          toastType.UNDO,
          "Removed",
          undefined,
          () => dispatch(createPlanningScenario(deletedScenario, scenarioIndex)),
          () => {}
        );
        dispatch(showToastAction(toast));
      }
      dispatch(deletePlanningScenarioAction(currentPortfolio.id, scenarioId));
      queueUpdate(scenarioId);
    }
  };
};

export const createPlanningRule = rule => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    rule.portfolioId = currentPortfolio.id;

    if (isAppInViewMode() === false && currentPortfolio.write === 1) {
      const queueUpdate = rule => {
        const request = idempotentId => ApiClient.createPlanningRule(idempotentId, rule.scenarioId, rule);
        const syncItem = new SyncItem(
          SyncItemType.CREATE,
          currentPortfolio.id + "addRule" + rule.id,
          request,
          3000,
          true,
          apiData => {}
        );
        dispatch(enqueueItem(syncItem));
      };
      dispatch(createPlanningRuleAction(currentPortfolio.id, rule.scenarioId, rule));
      queueUpdate(rule);
    }
  };
};

export const updatePlanningRule = (scenarioId, ruleId, dataToBeUpdated, isVariableUpdate = false) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    if (isAppInViewMode() === false && currentPortfolio.write === 1) {
      const queueUpdate = () => {
        const updatedData = isVariableUpdate
          ? { ...dataToBeUpdated, data: JSON.stringify(dataToBeUpdated.data) }
          : dataToBeUpdated;
        const request = idempotentId => ApiClient.updatePlanningRule(idempotentId, scenarioId, ruleId, updatedData);
        const syncItem = new SyncItem(
          SyncItemType.UPDATE,
          currentPortfolio.id + "updateRule" + ruleId,
          request,
          3000,
          true,
          apiData => {}
        );
        dispatch(enqueueItem(syncItem));
      };
      dispatch(updatePlanningRuleAction(currentPortfolio.id, scenarioId, ruleId, dataToBeUpdated));
      queueUpdate();
    }
  };
};

export const updatePlanningRuleBulk = (scenarioId, updatedRules) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    if (isAppInViewMode() === false && currentPortfolio.write === 1) {
      const queueUpdate = updatedRules => {
        const request = idempotentId => ApiClient.updatePlanningRuleBulk(idempotentId, scenarioId, updatedRules);
        const syncItem = new SyncItem(
          SyncItemType.BULK_UPDATE,
          currentPortfolio.id + "updateRuleBulk",
          request,
          3000,
          true,
          apiData => {}
        );
        dispatch(enqueueItem(syncItem));
      };
      dispatch(updatePlanningRuleBulkAction(currentPortfolio.id, scenarioId, updatedRules));
      queueUpdate(updatedRules);
    }
  };
};

export const deletePlanningRule = rule => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());

    if (isAppInViewMode() === false && currentPortfolio.write === 1) {
      const queueUpdate = rule => {
        const request = idempotentId => ApiClient.deletePlanningRule(idempotentId, rule);
        const syncItem = new SyncItem(
          SyncItemType.DELETE,
          currentPortfolio.id + "deleteRule" + rule.id,
          request,
          0,
          false,
          apiData => {}
        );
        dispatch(enqueueItem(syncItem));
      };
      dispatch(deletePlanningRuleAction(currentPortfolio.id, rule.id));
      queueUpdate(rule);
    }
  };
};

export const updateReportPreference = (portfolioId, reportType, updatedReportPreferences) => {
  return (dispatch, getState) => {
    const portfolio = portfolioSelector(getState(), portfolioId);

    if (isAppInViewMode() === false && portfolio.write === 1) {
      const queueUpdate = reportType => {
        const request = idempotentId =>
          ApiClient.updateReportPreferences(idempotentId, portfolioId, reportType, {
            ...updatedReportPreferences,
            data: JSON.stringify(updatedReportPreferences.data)
          });
        const syncItem = new SyncItem(
          SyncItemType.UPDATE,
          portfolioId + "reportPreferences",
          request,
          3000,
          true,
          apiData => {}
        );
        dispatch(enqueueItem(syncItem));
      };
      dispatch(updateReportPreferenceAction(portfolioId, reportType, updatedReportPreferences));
      queueUpdate(reportType);
    }
  };
};
export const deleteReportPreference = () => {};

export const deletePortfolio = (portfolio, onDelete = () => null, onSuccess = () => null, onError) => {
  return (dispatch, getState) => {
    dispatch(deletePortfolioAction(portfolio));
    const currentPortfolio = currentPortfolioSelector(getState());
    onDelete(currentPortfolio.id);
    const request = idempotentId =>
      ApiClient.deletePortfolio(idempotentId, portfolio.id).then(() => {
        const currentPortfolio = currentPortfolioSelector(getState());
        if (!currentPortfolio === false) {
          dispatch(fetchNetWorthDataForPortfolio(currentPortfolio.id, true));
        }
        dispatch(getConnectivityCenterData());
        onSuccess();
      });
    const syncItem = new SyncItem(SyncItemType.DELETE, portfolio.id, request, 0, false, apiData => {});
    dispatch(enqueueItem(syncItem));
  };
};

const getPortfolios = (_, getState) => cancelToken => {
  return new Promise((resolve, reject) => {
    const mergePayload = (currentPayload, newPayload) => {
      if (!currentPayload === true) {
        return newPayload;
      }
      for (const portfolio of newPayload.portfolio) {
        const temp = currentPayload.portfolio.find(item => item.id === portfolio.id);
        if (!temp === false) {
          temp.details.custodian.push(...portfolio.details.custodian);
          if (temp.details.document && portfolio.details.document) {
            temp.details.document.push(...portfolio.details.document);
          }
        }
      }
      return currentPayload;
    };

    const fetchData = (fetchedData, hash, canAutoSwitchToSharedPortfolio = true) => {
      ApiClient.getPortfolios(getUuid(), 1, hash, cancelToken)
        .then(apiData => {
          if (!apiData === true) {
            return;
          }

          const sharedPortfolioUsers = sharedPortfolioUsersSelector(getState());
          const lastUsedPortfolioUserId = getLastUsedPortfolioUserId();

          if (
            canAutoSwitchToSharedPortfolio === true &&
            sharedPortfolioUsers.length > 0 &&
            !lastUsedPortfolioUserId === true &&
            (apiData.payload.portfolio.length === 0 ||
              (isAppInWhiteLabelMode() === false &&
                apiData.payload.portfolio.length === 1 &&
                apiData.payload.portfolio[0].details.custodian.length === 0)) &&
            !hash === true
          ) {
            setPortfolioSessionUserId(sharedPortfolioUsers[0].id);
            fetchData(null, null, false);
            return;
          }

          const currentData = mergePayload(fetchedData, apiData.payload);
          if (!apiData.payload.hash === true) {
            resolve(currentData);
            return;
          }
          fetchData(currentData, apiData.payload.hash);
        })
        .catch(apiError => {
          captureError(getState(), apiError);
          reject(apiError);
        });
    };

    const sharedPortfolioUsers = sharedPortfolioUsersSelector(getState());
    const lastUsedPortfolioUserId = getLastUsedPortfolioUserId();
    const isSubscriptionActive = accountSubscriptionIsActiveSelector(getState());
    if (sharedPortfolioUsers.length > 0 && !lastUsedPortfolioUserId === true && isSubscriptionActive === false) {
      setPortfolioSessionUserId(sharedPortfolioUsers[0].id);
    }

    fetchData(null, null);
  });
};

const getHoldings = (portfolioCustodianMap = null) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const mergePayload = (currentPayload, newPayload) => {
        if (!currentPayload === true) {
          const holdingsMap = {};
          for (const [portfolioId, holdingsData] of Object.entries(newPayload.portfolio)) {
            holdingsMap[portfolioId] = holdingsData.holding;
          }
          return holdingsMap;
        }
        for (const [portfolioId, holdingsData] of Object.entries(newPayload.portfolio)) {
          if (!currentPayload[portfolioId]) {
            currentPayload[portfolioId] = [];
          }
          currentPayload[portfolioId].push(...holdingsData.holding);
        }
        return currentPayload;
      };

      const fetchData = (fetchedData, hash) => {
        const apiCall =
          portfolioCustodianMap && !hash
            ? ApiClient.getHoldingsForCustodians(getUuid(), portfolioCustodianMap)
            : ApiClient.getHoldings(getUuid(), hash);
        apiCall
          .then(apiData => {
            const currentData = mergePayload(fetchedData, apiData.payload);
            if (!apiData.payload.hash === true) {
              const allHoldings = Object.values(currentData).flat();
              let tickerIdsToTest = new Set();
              for (const holding of allHoldings) {
                if (!holding.costTickerId === false) {
                  tickerIdsToTest.add(holding.costTickerId);
                }
                if (!holding.valueTickerId === false) {
                  tickerIdsToTest.add(holding.valueTickerId);
                }
                if (holding.valueTickerId === PVST_VALUE_TICKER_ID) {
                  const valueStr = getValueOfPropFromStringifiedJSON(holding.rate, "t");
                  if (valueStr) {
                    const covertedToInt = parseInt(valueStr, 10);
                    tickerIdsToTest.add(covertedToInt);
                  }
                }
              }

              let unknownTickerIds = new Set();
              for (const tickerId of Array.from(tickerIdsToTest)) {
                if (
                  tickerId !== UNKNOWN_TICKER_ID &&
                  getTickerUsingId(getState(), tickerId).shortName === UNKNOWN_TICKER_SHORT_NAME
                ) {
                  unknownTickerIds.add(tickerId);
                }
              }

              if (unknownTickerIds.size > 0) {
                dispatch(
                  getTickersForIds(
                    Array.from(unknownTickerIds),
                    () => {
                      resolve(currentData);
                    },
                    apiError => {
                      captureError(getState(), apiError);
                      reject(apiError);
                    }
                  )
                );
              } else {
                resolve(currentData);
              }
              return;
            }
            fetchData(currentData, apiData.payload.hash);
          })
          .catch(apiError => {
            captureError(getState(), apiError);
            reject(apiError);
          });
      };
      fetchData(null, null);
    });
  };
};

let isFirstFetchPortfoliosCall = true;
let fetchPortfoliosRetrycount = 0;
export const fetchPortfolios = (isBackgroundRefresh = false, shouldForceRefresh = false) => {
  const tempIsFirstFetchPortfoliosCall = isFirstFetchPortfoliosCall;
  isFirstFetchPortfoliosCall = false;
  return async (dispatch, getState) => {
    // K-4688: wait for state.auth.user to be loaded. After the 2-day persist purge,
    // Dashboard can mount and dispatch fetchPortfolios before fetchUser resolves. Without
    // user state, isSharedPortfolioUserSelector returns false in the axios interceptor, the
    // request goes out without `x-family-user-id`, and the backend returns the family
    // member's own portfolio instead of the owner's. deferUntilUserIsSet resolves when fetchUser succeeds
    if (!isAppInProofViewMode() && !isAppInViewMode() && !userSelector(getState()) === true) {
      await deferUntilUserIsSet;
    }

    const isInitialLoad = portfoliosSelector(getState()) === null;

    if (isBackgroundRefresh === true) {
      const currentPortfolio = currentPortfolioSelector(getState());
      if (!currentPortfolio === true) {
        return;
      }
    }

    dispatch(fetchPortfoliosPendingAction(isBackgroundRefresh));
    dispatch(updateFetchHoldingsStatusAction(true));
    dispatch(calculateDashboardDataForPortfolioPendingAction());

    const markInitialFetchDone = () => {
      if (!initialFetchSuccessSelector(getState())) {
        dispatch(initialFetchPortfoliosSuccessAction());
      }
    };

    const handleError = apiError => {
      console.log("Error fetching portfolios", apiError);
      markInitialFetchDone();
      dispatch(fetchPortfoliosErrorAction(apiError));

      if (isInitialLoad === false && apiError.errorCode !== apiErrorCodes.FETCH_PORTFOLIOS) {
        let message = isMobileDevice ? i18n.t("refreshFailedMessage.mobile") : i18n.t("refreshFailedMessage");
        if (navigator.onLine === false) {
          message = i18n.t("noInternetConnection");
        } else if (apiError.errorCode === apiErrorCodes.NO_RESPONSE && fetchPortfoliosRetrycount <= 1) {
          message = i18n.t("networkUnstable");
        }
        const toast = new Toast(toastType.GENERIC_ERROR, message, undefined, null, null);
        dispatch(showToastAction(toast));
      }
    };

    // Don't load change data before loading portfolio data when WLK admin is viewing
    // as the portfolio data could belong to the previous user that the admin was viewing
    const wlClientContext = wlClientContextSelector(getState());
    if (isInitialLoad === false && !wlClientContext === true) {
      const currentPortfolio = currentPortfolioSelector(getState());
      if (!currentPortfolio === false) {
        dispatch(fetchPortfolioChangeData(currentPortfolio.id, false));
        dispatch(fetchChartAndCagrData(currentPortfolio.id));
      }
    }

    const promises = RacePromiseWrapper(
      [
        ApiClient.getUserPreferences.bind(this, getUuid()),
        dispatch(getPortfolios),
        ApiClient.getBeneficiaries.bind(this, getUuid()),
        ApiClient.getTickerData.bind(this, getUuid())
      ],
      shouldForceRefresh ? 10000 : 20000
    )
      .then(([userPreferencesApiData, portfoliosApiData, beneficiariesApiData, tickerApiData]) => {
        fetchPortfoliosRetrycount = 0;
        dispatch(updateTickerDataAction(tickerApiData.payload));
        dispatch(updateUserTimezoneAndOffset());

        if (!userPreferencesApiData === false && !userPreferencesApiData.payload === false) {
          dispatch(setUserPreferencesAction(userPreferencesApiData.payload));
        }
        if (!beneficiariesApiData === false && !beneficiariesApiData.payload === false) {
          dispatch(updateBeneficiaryAction(beneficiariesApiData.payload));
        }
        return Promise.all([portfoliosApiData.portfolio]);
      })
      .then(([portfolios]) => {
        const initialFetchSuccess = initialFetchSuccessSelector(getState());
        for (const portfolio of portfolios) {
          const custodiansWithParentIds = new Set();
          const allCustodiansMap = new Map();

          const handleCustodianWithParentIds = custodian => {
            if (!custodian.parentId) {
              return;
            }

            if (allCustodiansMap.get(custodian.parentId)) {
              copyParentInfoToChild(allCustodiansMap.get(custodian.parentId), custodian);
              return;
            }

            custodiansWithParentIds.add(custodian.id);
          };

          // Cleanup manually deleted custodians
          const manuallyRemovedCustodians = [];

          // First we push all section ids and remove if corresponding custodians found
          const sectionsWithoutRows = new Set();
          portfolio.details.section.forEach(section => {
            sectionsWithoutRows.add(section.id);
          });

          // Remove empty custodians
          portfolio.details.custodian = portfolio.details.custodian.forEach(custodian => {
            if (
              !custodian.name === true &&
              custodian.value === null &&
              custodian.cost === null &&
              isCustodianAddedTodaySelector(getState(), custodian.id, custodian) === false
            ) {
              manuallyRemovedCustodians.push(custodian);
            }
            const filterCondition =
              !custodian.name === false ||
              !custodian.cost === false ||
              !custodian.value === false ||
              !custodian.accountNumber === false ||
              !custodian.description === false;
            if (filterCondition) {
              if (custodian.hidden !== 1) {
                sectionsWithoutRows.delete(custodian.sectionId);
              }
              allCustodiansMap.set(custodian.id, custodian);
              handleCustodianWithParentIds(custodian);
            }
          });

          if (manuallyRemovedCustodians.length > 0) {
            dispatch(cleanupManuallyDeletedCustodians(portfolio, manuallyRemovedCustodians));
          }

          // Preserve custodians which are being linked
          let localCustodianIdsToRemove = [];
          const localPortfolio = portfolioSelector(getState(), portfolio.id);
          if (!localPortfolio === false) {
            localPortfolio.details.custodian.forEach(custodian => {
              const filterCondition =
                (!custodian.name === false ||
                  !custodian.cost === false ||
                  !custodian.value === false ||
                  !custodian.accountNumber === false ||
                  !custodian.description === false) &&
                custodian.isLocallyEdited;
              if (
                custodian.isLinking === true ||
                !custodian.linkingAccountsData === false ||
                custodian.autoAddedForEmptySections ||
                (initialFetchSuccess && custodian.isAddedForConnect) ||
                (filterCondition && !allCustodiansMap.get(custodian.id)) ||
                (!custodian.parentId === false &&
                  custodian.hidden === 1 &&
                  !custodianSelector(getState(), custodian.parentId) === false)
              ) {
                sectionsWithoutRows.delete(custodian.sectionId);

                if (!allCustodiansMap.get(custodian.id)) {
                  if (
                    (!isCustodianEmpty(custodian) &&
                      !custodian.isLinking === true &&
                      !custodian.tsModified === false &&
                      !custodian.parentId === true) ||
                    (custodian.hidden === 0 &&
                      !custodian.autoAddedForEmptySections &&
                      tempIsFirstFetchPortfoliosCall === true)
                  ) {
                    console.log("Clearing locally created custodian", custodian);
                    localCustodianIdsToRemove.push(custodian.id);
                    return;
                  }
                }
                // If some custodians are stuck in linking state
                // unmark them as linking when page reloads to avoid the user
                // getting stuck with a linking loader
                if (
                  isBackgroundRefresh === false &&
                  tempIsFirstFetchPortfoliosCall === true &&
                  custodian.isLinking === true &&
                  !custodian.linkingAccountsData === true
                ) {
                  dispatch(unmarkCustodianAsLinking(portfolio.id, custodian));
                }

                const newCustodian = { ...custodian, ...(allCustodiansMap.get(custodian.id) || {}) };
                allCustodiansMap.set(custodian.id, newCustodian);
                handleCustodianWithParentIds(newCustodian);
              }
            });
          }

          if (localCustodianIdsToRemove.length > 0) {
            console.log("Deleting locally created custodian from cache", localCustodianIdsToRemove);
            dispatch(deleteCustodianBulkAction(portfolio.id, localCustodianIdsToRemove));
          }

          portfolio.details.custodian = [...allCustodiansMap.values()];

          sectionsWithoutRows.forEach(emptySectionId => {
            const defaultCustodians = [
              { id: getUuid(), sectionId: emptySectionId, sortKey: "1", autoAddedForEmptySections: true },
              { id: getUuid(), sectionId: emptySectionId, sortKey: "2", autoAddedForEmptySections: true },
              { id: getUuid(), sectionId: emptySectionId, sortKey: "3", autoAddedForEmptySections: true }
            ];

            portfolio.details.custodian.push(...defaultCustodians);
          });

          // Loop through remaining custodians with parentIds for which `copyParentInfoToChild` is not done
          custodiansWithParentIds.forEach(custodianId => {
            const custodian = allCustodiansMap.get(custodianId);
            copyParentInfoToChild(allCustodiansMap.get(custodian.parentId), custodian);
          });

          // Save net worth data
          if (!localPortfolio === false) {
            portfolio.details.networth = localPortfolio.details.networth;
            portfolio.details.changeData = localPortfolio.details.changeData;
            portfolio.details.chartAndCagrData = localPortfolio.details.chartAndCagrData;
          }

          // Sort shared links
          portfolio.details.share.sort((a, b) => b.tsCreated - a.tsCreated);

          portfolio.diyChart =
            portfolio.diyChart && portfolio.diyChart.data
              ? { ...portfolio.diyChart, data: JSON.parse(portfolio.diyChart.data) }
              : portfolio.diyChart;

          const planningData = portfolio.planning;
          planningData.rule =
            planningData &&
            planningData.rule &&
            planningData.rule.map(rule => {
              return { ...rule, data: JSON.parse(rule.data) };
            });
          portfolio.planning = planningData;

          portfolio.networthChartScenario = portfolio.networthChartScenario
            ? JSON.parse(portfolio.networthChartScenario)
            : portfolio.networthChartScenario;

          portfolio.reportPreference =
            portfolio.reportPreference &&
            portfolio.reportPreference.map(item => {
              return { ...item, data: JSON.parse(item.data) };
            });

          sortPortfolio(portfolio);
        }

        return portfolios;
      })
      .then(portfolios => {
        // Since editing is not allowed on Mobile show the first non-empty
        // portfolio as the current portfolio else the user will not be
        // able to navigate to the non-empty portfolio if its not the first one
        dispatch(setPortfoliosAction(portfolios, isMobileDevice));
        setLastUsedPortfolioUserId(getPortfolioSessionUserId());

        const currentPortfolio = currentPortfolioSelector(getState());
        if (!currentPortfolio === false && isInitialLoad === true) {
          dispatch(fetchPortfolioChangeDataPendingAction(currentPortfolio.id));
        }

        return new Promise((resolve, reject) => {
          var tickerIdsToTest = new Set();
          for (const portfolio of portfolios) {
            if (portfolio.details) {
              tickerIdsToTest.add(portfolio.tickerId);
              for (const custodian of portfolio.details.custodian) {
                if (!custodian.costTickerId === false) {
                  tickerIdsToTest.add(custodian.costTickerId);
                }
                if (!custodian.valueTickerId === false) {
                  tickerIdsToTest.add(custodian.valueTickerId);
                }
                if (custodian.valueTickerId === PVST_VALUE_TICKER_ID) {
                  const valueStr = getValueOfPropFromStringifiedJSON(custodian.rate, "t");
                  if (valueStr) {
                    const covertedToInt = parseInt(valueStr, 10);
                    tickerIdsToTest.add(covertedToInt);
                  }
                }
              }
            }
          }

          var unknownTickerIds = new Set();
          for (const tickerId of Array.from(tickerIdsToTest)) {
            if (
              tickerId !== UNKNOWN_TICKER_ID &&
              getTickerUsingId(getState(), tickerId).shortName === UNKNOWN_TICKER_SHORT_NAME
            ) {
              unknownTickerIds.add(tickerId);
            }
          }

          if (unknownTickerIds.size === 0) {
            dispatch(fetchPortfoliosSuccessAction(portfolios));

            // The timeout is set so that the loader stops showing as all portfolio
            // data is there and then async things like networth can happen
            setTimeout(() => {
              resolve(portfolios);
            }, 10);
          } else {
            dispatch(
              getTickersForIds(
                Array.from(unknownTickerIds),
                () => {
                  dispatch(fetchPortfoliosSuccessAction(portfolios));

                  setTimeout(() => {
                    resolve(portfolios);
                    dispatch(updateDashboardAction(null));
                  }, 10);
                },
                apiError => {
                  reject(apiError);
                }
              )
            ).catch(apiError => {
              captureError(getState(), apiError);
            });
          }
        });
      })
      .then(portfolios => {
        const currentPortfolio = currentPortfolioSelector(getState());
        if (!currentPortfolio === false) {
          if (isInitialLoad === true || !wlClientContext === false) {
            dispatch(fetchPortfolioChangeData(currentPortfolio.id, false));
            dispatch(fetchChartAndCagrData(currentPortfolio.id));
          }
          dispatch(fetchNetWorthDataForPortfolio(currentPortfolio.id, true, false));
        }

        if (!wlClientContext === false) {
          dispatch(fetchWlDashboard(undefined, false));
        }

        // Force entire dashboard to refresh when portfolio details are refreshed
        if (isInitialLoad === false) {
          dispatch(updateDashboardAction(null));
        }

        if (!currentPortfolio === false) {
          dispatch(getUpdatedIrr(currentPortfolio.id));
        }
        if (isAppInViewMode() === false) {
          dispatch(getMultiuserList());
        }
      })
      .then(() => {
        markInitialFetchDone();
        // check if subscription banner preference should be set to null
        const shouldSetSubscriptionBannerPreferenceToNull = checkIfBannerPreferenceShouldBeSetNull(getState());
        if (shouldSetSubscriptionBannerPreferenceToNull) {
          dispatch(
            updateUserPreferences({
              bannerPreference: null
            })
          );
        }
      })
      .then(() => {
        const currentPortfolio = currentPortfolioSelector(getState());
        if (!currentPortfolio === false) {
          dispatch(getConnectivityCenterData());
          dispatch(getPortfolioFundScheduleData());
        }
        return dispatch(getHoldings());
      })
      .then(portfolioHoldingsMap => {
        const currentPortfolio = currentPortfolioSelector(getState());
        dispatch(updateCustodianInBulkAction(undefined, undefined, true, portfolioHoldingsMap));
        dispatch(updateFetchHoldingsStatusAction(false));
        dispatch(refreshAllConnectedCustodiansForPortfolio(currentPortfolio.id, shouldForceRefresh ? 0 : undefined));
      })
      .then(() => {
        dispatch(calculateDashboardDataForPortfolio());
      })
      .catch(apiError => {
        const lastUsedPortfolioUserId = getLastUsedPortfolioUserId();
        if (
          apiError.errorCode === apiErrorCodes.FETCH_PORTFOLIOS &&
          !lastUsedPortfolioUserId === true &&
          isInitialLoad
        ) {
          const sharedPortfolioUsers = sharedPortfolioUsersSelector(getState());
          if (sharedPortfolioUsers.length > 0) {
            switchToSharedPortfolioUserAccount(sharedPortfolioUsers[0], true);
          }
        }
        captureError(getState(), apiError);
        handleError(apiError);
      });

    fetchPortfoliosRetrycount += 1;
    return promises;
  };
};

const cleanupManuallyDeletedCustodians = (portfolio, custodians) => {
  return (dispatch, getState) => {
    console.log("cleanupManuallyDeletedCustodians", portfolio.name, custodians);

    for (const custodian of custodians) {
      custodian.value = 0;

      var name = "Untitled";
      const custodianSection = sectionSelector(getState(), custodian.sectionId);
      if (custodianSection) {
        const custodianSheet = sheetSelector(getState(), custodianSection.sheetId);
        if (custodianSheet) {
          name += " " + custodianSheet.category;
        }
      }
      custodian.name = name;
    }

    const updateRequest = idempotentId => ApiClient.updateCustodianBulk(idempotentId, custodians);
    const updateSyncItem = new SyncItem(
      SyncItemType.BULK_UPDATE,
      portfolio.id + "bulk_update_cleanup",
      updateRequest,
      0,
      true,
      apiData => {}
    );
    dispatch(enqueueItem(updateSyncItem));

    for (const custodian of custodians) {
      const custodianIdToArchive = custodian.parentId || custodian.id;
      const archiveRequest = idempotentId =>
        ApiClient.archiveCustodian(idempotentId, custodian.sectionId, [custodianIdToArchive]);
      const archiveSyncItem = new SyncItem(
        SyncItemType.DELETE,
        "bulk_archive_cleanup" + custodian.id,
        archiveRequest,
        0,
        true,
        apiData => {}
      );
      dispatch(enqueueItem(archiveSyncItem));
    }
  };
};

export const refreshAllConnectedCustodiansForPortfolio = (portfolioId, maxLastRefreshAge = 60 * 60 * 1000) => {
  return (dispatch, getState) => {
    if (!portfolioId === true || isAppInViewMode() || isAppInExtensionMode()) {
      return;
    }
    const portfolio = portfolioSelector(getState(), portfolioId);
    if (!portfolio === true || portfolio.write === 0) {
      return;
    }

    const lastRefreshTs = portfolioLastForceUpdateTsSelector(getState(), portfolioId);
    const timeElapsedSinceLastRefresh = new Date().getTime() - lastRefreshTs;

    if (!portfolio === true) {
      return;
    }
    const isMaxRefreshAgeWaitOver = timeElapsedSinceLastRefresh >= maxLastRefreshAge;
    if (isMaxRefreshAgeWaitOver) {
      dispatch(setPortfolioLastForceRefreshTsAction(portfolioId, new Date().getTime()));
    }
    const connectedCustodians = portfolio.details.custodian.filter(custodian =>
      isMaxRefreshAgeWaitOver
        ? !custodian.linkType === false
        : custodian.linkType === accountLinkingService.KUBERA_PORTFOLIO
    );
    if (connectedCustodians.length === 0) {
      return;
    }
    // fetch change data if last refresh was more than 10 minutes ago
    const lastRefreshTsForPortfolioChangeData = portfolioChangeDataLastForceUpdateTsSelector(getState(), portfolioId);
    const timeElapsedSinceLastRefreshForPortfolioChangeData =
      new Date().getTime() - lastRefreshTsForPortfolioChangeData;
    const maxLastRefreshAgeForPortfolioChangeData = maxLastRefreshAge === 0 ? 0 : 10 * 60 * 1000;
    const isMaxRefreshAgeWaitOverForPortfolioChangeData =
      timeElapsedSinceLastRefreshForPortfolioChangeData >= maxLastRefreshAgeForPortfolioChangeData;
    const onCompletion = () => {
      dispatch(updateAllConnectedCustodiansRefreshStatusAction(false));
      dispatch(refreshCustodianDone());
      if (isMaxRefreshAgeWaitOverForPortfolioChangeData) {
        dispatch(setPortfolioChangeDataLastForceRefreshTsAction(portfolioId, new Date().getTime()));
        dispatch(fetchPortfolioChangeData(portfolioId, false));
        dispatch(fetchChartAndCagrData(portfolioId));
      }
    };
    dispatch(updateAllConnectedCustodiansRefreshStatusAction(true));
    dispatch(refreshCustodianBatch({ [portfolioId]: connectedCustodians }, 0, 0, onCompletion, onCompletion));
    dispatch(refreshConnectedPortfoliosLinkedCustodians(portfolioId, true));
  };
};

export const refreshPredecessorLinkedPortfolioCustodians = (
  portfolioId,
  onSuccess = () => null,
  onError = () => null
) => {
  return (dispatch, getState) => {
    const predecessors = portfolioPredecessorsSelector(getState());
    let portfolioCustodiansMap = {};
    for (const predecessor of predecessors) {
      const portfolio = portfolioSelector(getState(), predecessor.id);
      const linkedCustodians = portfolio.details.custodian.filter(
        custodian =>
          custodian.linkType === accountLinkingService.KUBERA_PORTFOLIO &&
          custodian.linkProviderAccountId === predecessor.childId
      );
      if (linkedCustodians.length > 0) {
        portfolioCustodiansMap[predecessor.id] = linkedCustodians;
      }
    }

    if (Object.keys(portfolioCustodiansMap).length > 0) {
      dispatch(refreshCustodianBatch(portfolioCustodiansMap, true, 0, onSuccess, onError));
    } else {
      onSuccess();
    }
  };
};

export const refreshConnectedPortfoliosLinkedCustodians = (
  portfolioId,
  custodiansWithHoldingsOnly = false,
  onSuccess = () => null,
  onError = () => null
) => {
  return (dispatch, getState) => {
    const portfolios = connectedPortfoliosSelector(getState());
    let portfolioCustodiansMap = {};
    for (const portfolio of portfolios) {
      const linkedCustodians = portfolio.details.custodian.filter(
        custodian =>
          !custodian.linkType === false && (custodiansWithHoldingsOnly === false || custodian.holdingsCount > 0)
      );
      if (linkedCustodians.length > 0) {
        portfolioCustodiansMap[portfolio.id] = linkedCustodians;
      }
    }

    if (Object.keys(portfolioCustodiansMap).length > 0) {
      dispatch(refreshCustodianBatch(portfolioCustodiansMap, true, 0, onSuccess, onError));
    } else {
      onSuccess();
    }
  };
};

export const refreshCustodianBatch = (
  portfolioCustodiansMap,
  forceUpdate,
  liveUpdate = 0,
  onSuccess,
  onError,
  isUserInitiated = false
) => {
  return (dispatch, getState) => {
    for (const portfolioId in portfolioCustodiansMap) {
      portfolioCustodiansMap[portfolioId] = portfolioCustodiansMap[portfolioId]
        .filter(item => item.hidden !== 1 || !item.parentId === true)
        .sort((currentCustodian, nextCustodian) => nextCustodian.holdingsCount - currentCustodian.holdingsCount);
    }

    let custodians = Object.values(portfolioCustodiansMap).reduce((a, b) => a.concat(b), []);
    for (const custodian of custodians) {
      custodian.isRefreshing = true;
    }
    for (const portfolioId in portfolioCustodiansMap) {
      dispatch(updateCustodianInBulkAction(portfolioId, portfolioCustodiansMap[portfolioId]));
    }

    const custodianIds = custodians.map(custodian => custodian.id);
    dispatch(updateDashboardAction(custodianIds));
    dispatch(refreshCustodianTimeout(portfolioCustodiansMap, false, isUserInitiated));

    const connectedParentCustodians = custodians.filter(custodian => !custodian.parentId === true);
    const connectedParentCustodianIds = connectedParentCustodians.map(custodian => custodian.id);

    const batchRefresh = () => {
      return new Promise((resolve, reject) => {
        const refreshApiCall = hash => {
          ApiClient.refreshCustodianBatch(getUuid(), connectedParentCustodianIds, hash, forceUpdate, liveUpdate)
            .then(apiData => {
              if (!apiData) return;
              const custodians = apiData.payload.custodian;
              const responseHash = apiData.payload.hash;

              var custodiansWithUpdatedIrr = [];
              for (const custodian of custodians) {
                const currentCustodian = connectedParentCustodians.find(item => item.id === custodian.info.id);
                if (getIrrValue(currentCustodian.irr) !== getIrrValue(custodian.info.irr)) {
                  custodiansWithUpdatedIrr.push(custodian.info);
                }
              }
              if (custodiansWithUpdatedIrr.length > 0) {
                dispatch(updateSectionIrr(custodiansWithUpdatedIrr.map(item => item.sectionId)));
              }

              batch(() => {
                var unknownTickerIds = new Set();
                function handleUnknownTickers(custodian) {
                  if (
                    !custodian.costTickerId === false &&
                    getTickerUsingId(getState(), custodian.costTickerId).shortName === UNKNOWN_TICKER_SHORT_NAME
                  ) {
                    unknownTickerIds.add(custodian.costTickerId);
                  }
                  if (
                    !custodian.valueTickerId === false &&
                    getTickerUsingId(getState(), custodian.valueTickerId).shortName === UNKNOWN_TICKER_SHORT_NAME
                  ) {
                    unknownTickerIds.add(custodian.valueTickerId);
                  }
                }
                for (const custodian of custodians) {
                  handleUnknownTickers(custodian.info);
                }
                if (unknownTickerIds.size > 0) {
                  dispatch(
                    getTickersForIds(
                      Array.from(unknownTickerIds),
                      () => {
                        dispatch(updateDashboardAction(null));
                      },
                      apiError => {}
                    )
                  );
                }
                dispatch(handleCustodianRefreshResponse(custodians));
              });

              if (!responseHash === true) {
                resolve(apiData.payload);
              } else {
                setTimeout(() => {
                  refreshApiCall(responseHash);
                }, 5000);
              }
            })
            .catch(apiError => {
              captureError(getState(), apiError);
              reject(apiError);
            });
        };

        refreshApiCall(null);
      });
    };

    batchRefresh()
      .then(data => {
        onSuccess(data);
      })
      .catch(() => {
        onError();
      });
  };
};

export const liveRefreshConnectedCustodian = (
  custodianId,
  onSuccess = () => null,
  onError = () => null,
  isUserInitiated = false
) => {
  return (dispatch, getState) => {
    const portfolio = custodianPortfolioSelector(getState(), custodianId);
    const custodian = custodianSelector(getState(), custodianId, portfolio.id);
    const targetCustodians = custodian.parentId
      ? [
          custodianSelector(getState(), custodian.parentId),
          ...custodiansWithSameParentIdSelector(getState(), portfolio.id, custodian.parentId)
        ]
      : [custodian];
    const portfolioCustodiansMap = {
      [portfolio.id]: targetCustodians
    };
    dispatch(refreshCustodianBatch(portfolioCustodiansMap, 0, 1, onSuccess, onError, isUserInitiated));
  };
};

export const refreshAllConnectedCustodians = (
  custodianId,
  onSuccess = () => null,
  onError = () => null,
  flags,
  isUserInitiated = false
) => {
  return (dispatch, getState) => {
    let { custodians, portfolioCustodiansMap } = custodiansLinkedToSameAccountSelector(getState(), custodianId);

    if (custodians.length === 0) {
      if (onSuccess) {
        onSuccess();
      }
      return;
    }

    for (const portfolioId in portfolioCustodiansMap) {
      for (const item of portfolioCustodiansMap[portfolioId]) {
        const children = custodiansWithSameParentIdSelector(getState(), portfolioId, item.id);
        portfolioCustodiansMap[portfolioId].push(...children);
      }
    }

    dispatch(refreshCustodianBatch(portfolioCustodiansMap, flags.force, 0, onSuccess, onError, isUserInitiated));
  };
};

const refreshSpinnerTimeout = 6 * 1000;
const userInitiatedRefreshSpinnerTimeout = 5 * 60 * 1000;

const refreshCustodianTimeout = (portfolioCustodiansMap, isInitialRefresh = false, isUserInitiated = false) => {
  return (dispatch, getState) => {
    if (isMobileDevice) {
      return;
    }
    const timeout = isUserInitiated === true ? userInitiatedRefreshSpinnerTimeout : refreshSpinnerTimeout;
    setTimeout(() => {
      for (const portfolioId in portfolioCustodiansMap) {
        var custodiansToUpdate = [];

        for (const custodian of portfolioCustodiansMap[portfolioId]) {
          const latestCustodian = custodianSelector(getState(), custodian.id);
          if (!latestCustodian === true || latestCustodian.isRefreshing === false) {
            continue;
          }

          latestCustodian.isInitialRefresh = isInitialRefresh;
          latestCustodian.isRefreshing = false;
          latestCustodian.isRefreshingInBackground = true;
          custodiansToUpdate.push(latestCustodian);
        }
        dispatch(updateCustodianInBulkAction(portfolioId, custodiansToUpdate));

        console.log("debug refreshCustodianTimeout", custodiansToUpdate.length);

        if (custodiansToUpdate.length > 0) {
          const custodianIdsToUpdate = custodiansToUpdate.map(custodian => custodian.id);
          dispatch(updateDashboardAction(custodianIdsToUpdate));
        }
      }
    }, timeout);
  };
};

export const refreshCustodian = (
  custodianId,
  onSuccess = () => null,
  onError = () => null,
  updateDashboard = true,
  isInitialRefresh = false,
  flags = { force: 0, totalMisMatch: 0, includeNotFoundCustodians: true },
  portfolioId = null
) => {
  return (dispatch, getState) => {
    var custodian = custodianSelector(getState(), custodianId);
    const currentPortfolio =
      !portfolioId === false
        ? portfolioSelector(getState(), portfolioId)
        : custodianPortfolioSelector(getState(), custodianId);

    if (!currentPortfolio === true || !custodian === true || !custodian.parentId === false) {
      if (onError) {
        onError();
      }
      return;
    }

    custodian.isInitialRefresh = isInitialRefresh;
    custodian.isRefreshing = true;
    dispatch(updateCustodianInBulkAction(currentPortfolio.id, [custodian]));
    if (updateDashboard === true) {
      dispatch(updateDashboardAction([custodianId]));
    }

    const children = custodiansWithSameParentIdSelector(getState(), currentPortfolio.id, custodianId);
    if (children.length > 0) {
      for (const child of children) {
        child.isInitialRefresh = isInitialRefresh;
        child.isRefreshing = true;
      }
      dispatch(updateCustodianInBulkAction(currentPortfolio.id, children));
      dispatch(updateDashboardAction(children.map(item => item.id)));
    }

    if (updateDashboard === true) {
      dispatch(refreshCustodianTimeout({ [currentPortfolio.id]: [custodian] }, isInitialRefresh));
    }

    const refreshApiCall = () => {
      return new Promise((resolve, reject) => {
        ApiClient.refreshCustodian(getUuid(), custodianId, flags.force, flags.totalMisMatch, flags.reauth)
          .then(apiData => {
            if (getIrrValue(custodian.irr, true) !== getIrrValue(apiData.payload.info.irr, true)) {
              dispatch(updateSectionIrr([custodian.sectionId]));
            }

            dispatch(handleCustodianRefreshResponse([apiData.payload], undefined, flags));
            resolve(apiData.payload);
            onSuccess(custodian);
          })
          .catch(apiError => {
            if (apiError.errorCode === apiErrorCodes.PRODUCT_NOT_READY) {
              setTimeout(() => {
                refreshApiCall();
              }, 5000);
            } else {
              reject(apiError);
              onError(apiError);

              const custodian = custodianSelector(getState(), custodianId);
              if (!custodian === true) {
                return;
              }

              custodian.isInitialRefresh = false;
              custodian.isRefreshing = false;
              custodian.isRefreshingInBackground = false;

              const custodiansToUpdate = [custodian];

              const children = custodiansWithSameParentIdSelector(getState(), currentPortfolio.id, custodianId);
              if (children.length > 0) {
                for (const child of children) {
                  child.isInitialRefresh = false;
                  child.isRefreshing = false;
                  child.isLinking = false;
                  child.isRefreshingInBackground = false;
                }
                custodiansToUpdate.push(...children);
              }

              dispatch(
                updateCustodianInBulkAction(currentPortfolio.id, custodiansToUpdate, flags.includeNotFoundCustodians)
              );
              dispatch(updateDashboardAction(custodiansToUpdate.map(item => item.id)));
            }
          });
      }).catch(err => {
        captureError(getState(), err);
        console.log(err);
      });
    };

    return refreshApiCall();
  };
};

const handleCustodianRefreshResponse = (refreshResponse, portfolio = null, flags = {}) => {
  return (dispatch, getState) => {
    let updatedCustodiansWithHoldings = [];
    let portfolioCustodiansMap = {};
    let custodianIdsToUpdate = new Set();
    for (const custodianItem of refreshResponse) {
      const custodian = custodianItem.info;
      const syncCompleted = custodianItem.syncCompleted === undefined || custodianItem.syncCompleted === 1;
      const currentPortfolio = portfolio || custodianPortfolioSelector(getState(), custodian.id);
      const custodiansToUpdate = [];

      if (!currentPortfolio?.id) return;

      const currentCustodian = custodianSelector(getState(), custodian.id);
      if (custodian.holdingsCount > 0 && custodian.tsLastUpdateCheck > currentCustodian.tsLastUpdateCheck) {
        updatedCustodiansWithHoldings.push(custodian);
      }

      // If custodian does not have children clean up its children if any in local cahe
      // This is needed for the case when all the children for a custodian
      // are removed. E.g. a user selling all holdings for a linked account
      if (!custodian.parentId === true && custodian.hidden === 0 && !custodian.holdingsCount === true) {
        const children = custodiansWithSameParentIdSelector(getState(), currentPortfolio.id, custodian.id);
        if (children.length > 0) {
          dispatch(deleteCustodianBulkAction(currentPortfolio.id, children.map(item => item.id)));
        }
      }

      if (syncCompleted) {
        custodian.isInitialRefresh = false;
        custodian.isRefreshing = false;
        custodian.isLinking = false;
        custodian.isRefreshingInBackground = false;
      }
      custodiansToUpdate.push(custodian);

      if (portfolioCustodiansMap[currentPortfolio.id] === undefined) {
        portfolioCustodiansMap[currentPortfolio.id] = [];
      }
      for (const custodianToUpdate of custodiansToUpdate) {
        if (!custodianIdsToUpdate.has(custodianToUpdate.id)) {
          portfolioCustodiansMap[currentPortfolio.id].push(custodianToUpdate);
          custodianIdsToUpdate.add(custodianToUpdate.id);
        }
      }

      const autoAdded = custodianItem.autoAdded;
      if (autoAdded && autoAdded.length > 0) {
        dispatch(handleCustodianRefreshResponse(autoAdded, currentPortfolio));
        dispatch(updateSectionIrr(autoAdded.map(item => item.info.sectionId)));
      }
    }
    dispatch(
      updateCustodianInBulkAction(undefined, undefined, flags.includeNotFoundCustodians, portfolioCustodiansMap)
    );
    dispatch(updateDashboardAction(Array.from(custodianIdsToUpdate)));

    if (updatedCustodiansWithHoldings.length > 0) {
      const portfolioCustodianMap = updatedCustodiansWithHoldings.reduce((acc, custodian) => {
        const custodianPortfolio = custodianPortfolioSelector(getState(), custodian.id);
        acc[custodianPortfolio.id]
          ? acc[custodianPortfolio.id].push(custodian.id)
          : (acc[custodianPortfolio.id] = [custodian.id]);
        return acc;
      }, {});

      dispatch(getHoldings(portfolioCustodianMap))
        .then(portfolioHoldingsMap => {
          let holdingsCustodians = [];
          const custodianIdToPortfolioIdMap = {};
          for (const [portfolioId, custodians] of Object.entries(portfolioHoldingsMap)) {
            for (const custodian of custodians) {
              holdingsCustodians.push(custodian);
              custodianIdToPortfolioIdMap[custodian.id] = portfolioId;
            }
          }

          holdingsCustodians = holdingsCustodians.map(item => {
            item.isInitialRefresh = false;
            item.isRefreshing = false;
            item.isLinking = false;
            item.isRefreshingInBackground = false;
            return item;
          });
          dispatch(
            updateCustodianInBulkAction(undefined, undefined, flags.includeNotFoundCustodians, portfolioHoldingsMap)
          );

          for (const holding of holdingsCustodians) {
            if (!sectionSelector(getState(), holding.sectionId) === true) {
              dispatch(moveCustodian(holding.sectionId, holding.sectionId, holding.id, holding.sortKey));
            }

            const childrenIds = custodiansWithSameParentIdSelector(
              getState(),
              custodianIdToPortfolioIdMap[holding.id],
              holding.parentId
            ).map(item => item.id);
            const holdingIds = holdingsCustodians.map(item => item.id);
            for (const childId of childrenIds) {
              if (holdingIds.includes(childId) === false) {
                dispatch(deleteCustodianAction(custodianIdToPortfolioIdMap[holding.id], childId));
              }
            }
          }
          dispatch(updateDashboardAction([...holdingsCustodians.map(item => item.id)]));
          dispatch(calculateDashboardDataForPortfolio());
        })
        .catch(apiError => {
          captureError(getState(), apiError);
        });
    } else {
      dispatch(calculateDashboardDataForPortfolio());
    }
  };
};

export const reauthCustodian = (custodianId, onSuccess, onError) => {
  return (dispatch, getState) => {
    const { custodians, portfolioCustodiansMap } = custodiansLinkedToSameAccountSelector(getState(), custodianId);

    if (custodians.length === 0) {
      onSuccess();
      return;
    }
    for (const portfolioId in portfolioCustodiansMap) {
      dispatch(removeConnectionErrorsAction(portfolioId, portfolioCustodiansMap[portfolioId].map(item => item.id)));
    }

    // On reauth update lastFetchedTs locally so that the error is
    // no longer shown against the custodian
    for (const custodian of custodians) {
      custodian.tsLastUpdateCheck = new Date().getTime() / 1000;
    }
    for (const portfolioId in portfolioCustodiansMap) {
      dispatch(updateCustodianInBulkAction(portfolioId, portfolioCustodiansMap[portfolioId]));
    }
    dispatch(updateDashboardAction(custodians.map(item => item.id)));
    const reauthCustodianPromises = custodians.map(custodian => {
      return new Promise((resolve, reject) => {
        dispatch(
          refreshCustodian(
            custodian.id,
            () => {
              resolve(true);
              return onSuccess;
            },
            () => {
              resolve(true);
              return onError;
            },
            true,
            false,
            { force: 1, reauth: 1 },
            portfolioCustodiansMap[custodian.id]
          )
        );
      });
    });
    Promise.all(reauthCustodianPromises)
      .then(() => {
        dispatch(getConnectivityCenterData());
      })
      .catch(error => {
        captureError(getState(), error);
        console.log("error", error);
      });
    dispatch(updateDashboardAction([custodianId]));
  };
};

export const custodianLinkingProviderChange = (
  portfolioId,
  custodian,
  providerName,
  previousProviderName,
  category
) => {
  return dispatch => {
    if (!providerName === true) {
      return;
    }

    if (
      !custodian.name === true ||
      custodian.name === getLinkCustodianPlaceholderName(providerName, category) ||
      custodian.name === previousProviderName
    ) {
      custodian.isLinking = true;
      custodian.hidden = 0;
      custodian.name = providerName;
      dispatch(updateCustodianInBulkAction(portfolioId, [custodian]));
      dispatch(updateDashboardAction([custodian.id]));
    }
  };
};

export const custodianLinkingHintWaitTime = 90000;
export const markCustodianAsLinking = (
  portfolioId,
  targetCustodian,
  preventSameAccountLinking = false,
  providerName,
  category,
  isUnlinkFlow = false,
  shouldUpdateLatestCustodian = true
) => {
  return (dispatch, getState) => {
    const markCustodian = (portfolioId, custodian, providerName, category) => {
      if (!custodian.isLinking === true) {
        custodian.linkingTs = new Date().getTime();
        if (shouldUpdateLatestCustodian) {
          setTimeout(() => {
            const latestCustodian = custodianSelector(getState(), custodian.id);
            if (latestCustodian && !latestCustodian.isLinking === false) {
              dispatch(updateCustodianInBulkAction(portfolioId, [latestCustodian]));
              dispatch(updateDashboardAction([custodian.id]));
            }
          }, custodianLinkingHintWaitTime);
        }
      }
      custodian.isLinking = true;
      custodian.isUnlinkFlow = isUnlinkFlow;
      custodian.hidden = 0;
      custodian.linkingAccountsData = null;
      custodian.linkingFailed = false;
      custodian.name = custodian.name || getLinkCustodianPlaceholderName(providerName, category);
      custodian.linkingScreenClosed = false;
    };

    if (!targetCustodian === true) {
      return;
    }

    const {
      custodians: linkedCustodians,
      portfolioCustodiansMap,
      custodianPortfolioMap
    } = custodiansLinkedToSameAccountSelector(getState(), targetCustodian.id, false, true);
    if (linkedCustodians.length === 0 || preventSameAccountLinking) {
      markCustodian(portfolioId, targetCustodian, providerName, category);
      dispatch(updateCustodianInBulkAction(portfolioId, [targetCustodian]));
      dispatch(updateDashboardAction([targetCustodian.id]));
    } else {
      for (const custodian of linkedCustodians) {
        markCustodian(custodianPortfolioMap[custodian.id], custodian, providerName, category);
      }

      dispatch(updateCustodianInBulkAction(undefined, undefined, undefined, portfolioCustodiansMap));
      dispatch(updateDashboardAction(linkedCustodians.map(item => item.id)));
    }
  };
};

export const unmarkCustodianAsLinking = (
  portfolioId,
  targetCustodian,
  providerName,
  category,
  ignoreLinkedAccounts = false,
  isReconnectedFromConnectivityCenter = true
) => {
  return (dispatch, getState) => {
    const unmarkCustodian = (custodians, custodianPortfolioMap) => {
      let portfolioCustodiansMap = {};
      let custodianIdsToUpdate = [];
      for (const custodian of custodians) {
        if (!custodian === false) {
          const latestCustodian = custodianSelector(getState(), custodian.id);
          if (!latestCustodian === false) {
            latestCustodian.isLinking = false;
            latestCustodian.linkingAccountsData = null;
            latestCustodian.linkingFailed = false;
            latestCustodian.linkingScreenClosed = false;

            if (
              !latestCustodian.tsModified === true &&
              (latestCustodian.linkType === null || latestCustodian.linkType === undefined)
            ) {
              latestCustodian.name = "";
            }
          }

          const portfolioId =
            custodianPortfolioMap[custodian.id] || findPortfolioUsingCustodianIdSelector(getState(), custodian.id)?.id;
          if (portfolioCustodiansMap[portfolioId] === undefined) {
            portfolioCustodiansMap[portfolioId] = [];
          }
          portfolioCustodiansMap[portfolioId].push(latestCustodian);
          custodianIdsToUpdate.push(latestCustodian.id);
        }
      }

      if (custodianIdsToUpdate.length > 0) {
        dispatch(updateCustodianInBulkAction(undefined, undefined, undefined, portfolioCustodiansMap));
        dispatch(updateDashboardAction(custodianIdsToUpdate));
      }
    };

    const { custodians: linkedCustodians, custodianPortfolioMap } = custodiansLinkedToSameAccountSelector(
      getState(),
      targetCustodian.id,
      false,
      true
    );
    if (linkedCustodians.length === 0 || ignoreLinkedAccounts === true) {
      const latestCustodian = custodianSelector(getState(), targetCustodian.id);
      if (latestCustodian) {
        unmarkCustodian([latestCustodian], { [latestCustodian.id]: portfolioId });
      }
    } else {
      unmarkCustodian([targetCustodian, ...linkedCustodians], custodianPortfolioMap);
    }
  };
};

export const custodianLinkingScreenClosed = (portfolioId, custodian) => {
  return (dispatch, getState) => {
    const latestCustodian = custodianSelector(getState(), custodian.id);
    if (!latestCustodian === true) {
      return;
    }

    latestCustodian.linkingScreenClosed = true;

    dispatch(updateCustodianInBulkAction(portfolioId, [latestCustodian]));
    dispatch(updateDashboardAction([custodian.id]));
  };
};

export const custodianLinkingFailed = (portfolioId, custodian) => {
  return dispatch => {
    custodian.linkingFailed = true;

    dispatch(updateCustodianInBulkAction(portfolioId, [custodian]));
    dispatch(updateDashboardAction([custodian.id]));
  };
};

export const custodianLinkingAccountsDataFetched = (
  portfolioId,
  custodian,
  linkingAccountsData,
  providerName,
  category
) => {
  return (dispatch, getState) => {
    const latestCustodian = custodianSelector(getState(), custodian.id);
    if (!latestCustodian === true) {
      return;
    }

    latestCustodian.linkingAccountsData = linkingAccountsData;
    if (latestCustodian.name) {
      latestCustodian.name.replace(getLinkCustodianPlaceholderName(providerName, category), "");
    }
    latestCustodian.name =
      latestCustodian.name && latestCustodian.name !== getLinkCustodianPlaceholderName(providerName, category)
        ? latestCustodian.name
        : linkingAccountsData.providerName;

    dispatch(updateCustodianInBulkAction(portfolioId, [latestCustodian]));
    dispatch(updateDashboardAction([latestCustodian.id]));
  };
};

export const updateCustodianOnDashboard = custodianId => {
  return (dispatch, getState) => {
    const currentPortfolio = custodianPortfolioSelector(getState(), custodianId);

    if (!currentPortfolio) {
      return;
    }

    const currentPortfolioCustodians = currentPortfolio.details.custodian;
    const custodian = currentPortfolioCustodians.find(custodian => custodian.id === custodianId);

    if (custodian) {
      dispatch(updateCustodianInBulkAction(currentPortfolio.id, [custodian]));
      dispatch(updateDashboardAction([custodianId]));
    }
  };
};

export const fetchCustodianTickerDetails = (custodianId, isCost, parseTickerText = true) => {
  return (dispatch, getState) => {
    const currentPortfolio = custodianPortfolioSelector(getState(), custodianId);
    const currentPortfolioCustodians = currentPortfolio ? currentPortfolio.details.custodian : [];
    const custodian = currentPortfolioCustodians.find(custodian => custodian.id === custodianId);
    const portfolioTicker = getTickerUsingShortName(getState(), currentPortfolio.currency);

    if (isCost === true) {
      dispatch(updateCustodian(false, custodianId, { fetchingCostTickerInfo: true, costTickerInfo: null }));
      dispatch(
        getTickersForText(
          custodian.costInvalidInputText,
          tickers => {
            var propertiesToUpdate = {
              fetchingCostTickerInfo: false,
              costTickerInfo: tickers
            };

            for (const tickerInfo of tickers) {
              dispatch(addTickerInfoAction(tickerInfo.info, tickerInfo.rate));
            }

            if (tickers.length === 1) {
              const tickerInfo = tickers[0].info;
              const rate = getExchangeRate(getState(), tickerInfo.shortName, currentPortfolio.currency);
              const costExchangeRate = getExchangeRateDetails(portfolioTicker.id, rate);

              propertiesToUpdate = {
                costTickerId: tickerInfo.id,
                costExchangeRate: costExchangeRate,
                costInvalidInputText: null,
                fetchingCostTickerInfo: false,
                costTickerInfo: null,
                cost: custodian.cost
              };
            }

            dispatch(updateCustodian(!custodian.tsModified === true, custodianId, propertiesToUpdate));
            dispatch(updateDashboardAction([custodianId]));
          },
          apiError => {
            dispatch(
              updateCustodian(!custodian.tsModified === true, custodianId, {
                fetchingCostTickerInfo: false,
                costTickerInfo: apiError.errorCode === apiErrorCodes.INVALID_INPUT ? [] : null
              })
            );
            dispatch(updateDashboardAction([custodianId]));
          }
        )
      );
    } else {
      dispatch(updateCustodian(false, custodianId, { fetchingValueTickerInfo: true, valueTickerInfo: null }));
      dispatch(
        getTickersForText(
          custodian.valueInvalidInputText,
          tickers => {
            var propertiesToUpdate = {
              fetchingValueTickerInfo: false,
              valueTickerInfo: tickers
            };

            for (const tickerInfo of tickers) {
              dispatch(addTickerInfoAction(tickerInfo.info, tickerInfo.rate));
            }

            if (tickers.length === 1) {
              const tickerInfo = tickers[0].info;
              const rate = getExchangeRate(getState(), tickerInfo.shortName, currentPortfolio.currency);
              const valueExchangeRate = getExchangeRateDetails(portfolioTicker.id, rate);

              propertiesToUpdate = {
                valueTickerId: tickerInfo.id,
                valueExchangeRate: valueExchangeRate,
                valueInvalidInputText: null,
                fetchingValueTickerInfo: false,
                valueTickerInfo: null,
                value: custodian.value
              };
            }

            dispatch(updateCustodian(!custodian.tsModified === true, custodianId, propertiesToUpdate));
            dispatch(updateDashboardAction([custodianId]));
          },
          apiError => {
            dispatch(
              updateCustodian(!custodian.tsModified === true, custodianId, {
                fetchingValueTickerInfo: false,
                valueTickerInfo: apiError.errorCode === apiErrorCodes.INVALID_INPUT ? [] : null
              })
            );
            dispatch(updateDashboardAction([custodianId]));
          },
          parseTickerText
        )
      );
    }
    dispatch(updateDashboardAction([custodianId]));
  };
};

const flashCustodianItemsInQueue = [];

const flashCustodiansInQueue = count => {
  const hasNotFountCustodian = flashCustodianItemsInQueue.findIndex(
    custodianId => !document.getElementById(custodianId)
  );

  if (hasNotFountCustodian === -1 || count === 5) {
    flashCustodianItemsInQueue.forEach(custodianId => {
      requestAnimationFrame(() => {
        document.flashElement(custodianId);
      });
    });
    flashCustodianItemsInQueue.length = 0;
  } else {
    requestIdleCallback(() => {
      flashCustodiansInQueue(parseInt(count, 10) + 1);
    });
  }
};

const flashCustodian = custodianId => {
  requestIdleCallback(() => {
    flashCustodiansInQueue(0);
  });

  flashCustodianItemsInQueue.push(custodianId);
};

const lastUpdateCustodianTs = {};
export const updateCustodian = (
  isFirstEdit,
  custodianId,
  propertiesToUpdate,
  updateDashboard = false,
  onCompletion = null,
  overridePreviousUpdate = true,
  isLinkingFlow = false
) => {
  return (dispatch, getState) => {
    const currentPortfolio = custodianPortfolioSelector(getState(), custodianId);
    const currentPortfolioCustodians = currentPortfolio ? currentPortfolio.details.custodian : [];
    var custodian = currentPortfolioCustodians.find(custodian => custodian.id === custodianId);
    const lastUpdate = (lastUpdateCustodianTs[(custodian || {}).id] = Date.now());

    if (custodian) {
      // Consider a row local only if its the first edit for the row with non-empty values
      // and it doesn't have a tsmodified which is set only by the server and also the
      // isLocallyEdited flag is flase or missing indicating that the row was never modified.
      // Local rows result in create custodian calls whereas non-local ones result in update calls.
      const isLocalRow = isFirstEdit === true && !custodian.tsModified === true && !custodian.isLocallyEdited === true;

      // While updating a custodian if value field is non-null ensure that the valueTickerId
      // and valueExchangeRate fields are also set correctly
      if (propertiesToUpdate.value != null) {
        if (propertiesToUpdate.valueTickerId == null) {
          propertiesToUpdate.valueTickerId = custodian.valueTickerId;
        }
        if (propertiesToUpdate.valueExchangeRate == null) {
          const rate = getExchangeRate(
            getState(),
            getTickerUsingId(getState(), propertiesToUpdate.valueTickerId).shortName,
            currentPortfolio.currency
          );
          propertiesToUpdate.valueExchangeRate = getExchangeRateDetails(
            getTickerUsingShortName(getState(), currentPortfolio.currency).id,
            rate
          );
        }
      }

      dispatch(setSectionUpdated(custodian.sectionId));
      custodian = { ...custodian, ...propertiesToUpdate };

      const apiKeys = CUSTODIAN_SYNC_API_KEYS;
      const apiCustodian = (isLocalRow === true ? Object.keys(custodian) : Object.keys(propertiesToUpdate))
        .filter(key => apiKeys.includes(key))
        .reduce((obj, key) => {
          obj[key] = custodian[key];
          return obj;
        }, {});

      if (apiCustodian.valueTickerId === PVST_VALUE_TICKER_ID && custodian.rate) {
        const rateParsed = JSON.parse(custodian.rate);
        apiCustodian.valueExchangeRate = dispatch(convertPVSTRateToValueExchangeRate(rateParsed, undefined, false));
      }

      if (!("past" in propertiesToUpdate) && Number.isInteger(apiCustodian.linkType)) {
        apiCustodian.past = 0;
        custodian.past = 0;
      }

      // Temporary hack as server does not seem to allow null value for name field
      if (apiCustodian["name"] === null) {
        apiCustodian["name"] = "";
      }

      // For read only clients everything is managed
      if (isReadOnlyWlClient(getState())) {
        apiCustodian.aum = 1;
      }

      // Only update modified TS if its already set
      // This is needed to differentiate items that are local only
      // from those that are present on the server as well.
      if (!custodian.tsModified === false) {
        custodian.tsModified = Math.floor(new Date().getTime() / 1000);
      }
      dispatch(updateCustodianInBulkAction(currentPortfolio.id, [custodian]));

      // When a parent custodian's type changes, propagate to its children (holdings)
      // so that dashboard data reflects the change immediately without a hard refresh.
      if (propertiesToUpdate.type != null && !custodian.parentId && custodian.holdingsCount > 0) {
        const children = currentPortfolioCustodians.filter(c => c.parentId === custodianId);
        if (children.length > 0) {
          const updatedChildren = children.map(child => ({ ...child, type: propertiesToUpdate.type }));
          dispatch(updateCustodianInBulkAction(currentPortfolio.id, updatedChildren));
        }
      }

      if (updateDashboard === true) {
        dispatch(updateDashboardAction([custodianId]));
      }

      // If custodian has invalid text entries for cost/value don't call API
      if (!custodian.costInvalidInputText === false || !custodian.valueInvalidInputText === false) {
        return;
      } else {
        custodian.isLocallyEdited = true;
        dispatch(updateCustodianInBulkAction(currentPortfolio.id, [custodian]));
      }

      const sectionId = custodian.sectionId;

      if (isLinkingFlow) {
        flashCustodian(custodian.id);
      }

      if (isLocalRow === true) {
        const request = idempotentId => ApiClient.createCustodian(idempotentId, sectionId, apiCustodian);
        const syncItem = new SyncItem(SyncItemType.CREATE, custodian.id, request, 0, false, apiData => {
          // Re-resolve the latest custodian from the current redux state instead of relying on
          // the closure-captured custodian array — reducer cleanups (e.g. blank-section
          // placeholder sweeps) may replace details.custodian, which would strand this callback
          // on a frozen snapshot and clobber the user's in-progress typing on API response.
          const latestCustodian = custodianSelector(getState(), custodianId);
          const newCustodian = latestCustodian ? { ...apiData.payload, ...latestCustodian } : { ...apiData.payload };

          if (
            latestCustodian &&
            (!latestCustodian.tsModified === true || latestCustodian.tsModified < apiData.payload.tsModified)
          ) {
            dispatch(updateCustodianInBulkAction(currentPortfolio.id, [newCustodian]));
            dispatch(updateDashboardAction([custodianId]));

            var tickerIdsToTest = new Set();
            if (!apiData.payload.valueTickerId === false) {
              tickerIdsToTest.add(apiData.payload.valueTickerId);
            }
            if (!apiData.payload.costTickerId === false) {
              tickerIdsToTest.add(apiData.payload.costTickerId);
            }
            for (const tickerId of Array.from(tickerIdsToTest)) {
              if (getTickerUsingId(getState(), tickerId).shortName === UNKNOWN_TICKER_SHORT_NAME) {
                dispatch(
                  refreshTickerData(() => {
                    dispatch(updateDashboardAction([custodianId]));
                  })
                );
                break;
              }
            }

            if (!apiData.payload.irr === false) {
              dispatch(updateSectionIrr([apiData.payload.sectionId]));
            }
          }
          if (onCompletion) {
            onCompletion(newCustodian, apiData.payload);
          }
        });
        dispatch(enqueueItem(syncItem));
      } else {
        const previousUpdate = getPreviousSyncUpdateSelector(getState(), custodian.id);
        var previousUpdatePayload = {};

        if (!previousUpdate === false && !previousUpdate.requestPayload === false) {
          previousUpdatePayload = previousUpdate.requestPayload;
        }

        const custodianPayload = { ...previousUpdatePayload, ...apiCustodian };
        const hasOtherPendingFields = Object.keys(previousUpdatePayload).some(k => k !== "type");
        const isHoldingsTypeUpdate =
          propertiesToUpdate.type != null && !custodian.parentId && custodian.holdingsCount > 0;

        let request;
        if (isHoldingsTypeUpdate && hasOtherPendingFields) {
          request = async idempotentId => {
            await ApiClient.updateHoldingsInvestableType(
              idempotentId,
              sectionId,
              custodian.id,
              propertiesToUpdate.type
            );
            const { type: _ignored, ...otherPayload } = custodianPayload;
            return ApiClient.updateCustodian(idempotentId, sectionId, custodian.id, otherPayload);
          };
        } else if (isHoldingsTypeUpdate) {
          request = idempotentId =>
            ApiClient.updateHoldingsInvestableType(idempotentId, sectionId, custodian.id, propertiesToUpdate.type);
        } else {
          request = idempotentId => ApiClient.updateCustodian(idempotentId, sectionId, custodian.id, custodianPayload);
        }
        const syncItem = new SyncItem(
          SyncItemType.UPDATE,
          custodian.id,
          request,
          3000,
          overridePreviousUpdate === true,
          apiData => {
            // Re-resolve the latest custodian from current redux state so reducer cleanups that
            // replace details.custodian don't leave us reading a stale snapshot.
            const latestCustodian = custodianSelector(getState(), custodianId);
            if (latestCustodian && latestCustodian.tsModified < apiData.payload.tsModified) {
              // If the custodian was moved after the API call was made ensure
              // it doesn't move  back
              if (apiData.payload.sectionId !== latestCustodian.sectionId) {
                apiData.payload.sectionId = latestCustodian.sectionId;
              }
              if (custodian.irrChangeTs !== latestCustodian.irrChangeTs) {
                apiData.payload.irrChangeTs = latestCustodian.irrChangeTs;
              }
              if (!latestCustodian.valueTickerInfo === false) {
                apiData.payload.valueTickerInfo = latestCustodian.valueTickerInfo;
                apiData.payload.valueInvalidInputText = latestCustodian.valueInvalidInputText;
                apiData.payload.value = latestCustodian.value;
              }
              if (!latestCustodian.costTickerInfo === false) {
                apiData.payload.costTickerInfo = latestCustodian.costTickerInfo;
                apiData.payload.costInvalidInputText = latestCustodian.costInvalidInputText;
                apiData.payload.cost = latestCustodian.cost;
              }
              if (custodian.isIrrUpdating) {
                apiData.payload.isIrrUpdating = false;
              }
              if (lastUpdate === lastUpdateCustodianTs[custodian.id]) {
                dispatch(updateCustodianInBulkAction(currentPortfolio.id, [apiData.payload]));
                dispatch(updateDashboardAction([custodianId]));
                delete lastUpdateCustodianTs[custodian.id];
              }
              if (getIrrValue(apiData.payload.irr, true) !== getIrrValue(latestCustodian.irr, true)) {
                dispatch(updateSectionIrr([apiData.payload.sectionId]));
              }
            }

            if (onCompletion) {
              onCompletion(apiData.payload);
            }
          }
        );
        syncItem.requestPayload = custodianPayload;
        dispatch(enqueueItem(syncItem));
      }
    }
  };
};

const BULK_ASYNC_ENTITY_TYPES = ["custodian", "fundSchedule", "cashFlow"];
const BULK_ASYNC_POLL_INTERVAL_MS = 2000;
const BULK_ASYNC_MAX_POLL_ERRORS = 3;
const BULK_ASYNC_STUCK_TIMEOUT_MS = 5 * 60 * 1000;

const bulkAsyncSleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const bulkAsyncRemainingTotal = remaining =>
  BULK_ASYNC_ENTITY_TYPES.reduce((sum, type) => sum + (remaining?.[type]?.length || 0), 0);

const bulkAsyncNormalizeRemaining = remaining => ({
  custodian: remaining?.custodian || [],
  fundSchedule: remaining?.fundSchedule || [],
  cashFlow: remaining?.cashFlow || []
});

// Drives the submit -> poll loop for the bulk custodian async endpoint. Resolves with
// { custodian, fundSchedule, cashFlow } where each is { completed, failed }. The whole
// loop is the sync-queue request promise, so a rejection triggers the queue's retry.
const bulkCustodianSubmitAndPoll = async (idempotentId, submitBody, onProgress) => {
  const aggregate = {
    custodian: { completed: new Map(), failed: new Map() },
    fundSchedule: { completed: new Set(), failed: new Map() },
    cashFlow: { completed: new Set(), failed: new Map() }
  };

  let lastReportedDone = -1;
  const reportProgress = total => {
    let completed = 0;
    let failed = 0;
    for (const type of BULK_ASYNC_ENTITY_TYPES) {
      completed += aggregate[type].completed.size;
      failed += aggregate[type].failed.size;
    }
    const done = completed + failed;
    if (done !== lastReportedDone) {
      lastReportedDone = done;
      onProgress?.({ completed, failed, total });
    }
  };

  const submit = await ApiClient.bulkCustodianAsyncSubmit(idempotentId, submitBody);
  let key = submit.payload.key;
  let remaining = bulkAsyncNormalizeRemaining(submit.payload.remaining);
  const total = bulkAsyncRemainingTotal(remaining);

  let resubmitted = false;
  let pollErrorCount = 0;
  let lastChangeTs = Date.now();

  while (bulkAsyncRemainingTotal(remaining) > 0) {
    await bulkAsyncSleep(BULK_ASYNC_POLL_INTERVAL_MS);

    let poll;
    try {
      poll = await ApiClient.bulkCustodianAsyncPoll(idempotentId, key, remaining);
      pollErrorCount = 0;
    } catch (error) {
      // 400 => task expired/lost (30 min TTL). Resubmit once for a fresh key.
      if (!resubmitted && error?.httpStatus === 400) {
        resubmitted = true;
        const fresh = await ApiClient.bulkCustodianAsyncSubmit(idempotentId, submitBody);
        key = fresh.payload.key;
        remaining = bulkAsyncNormalizeRemaining(fresh.payload.remaining);
        for (const type of BULK_ASYNC_ENTITY_TYPES) {
          aggregate[type].completed.clear();
          aggregate[type].failed.clear();
        }
        lastChangeTs = Date.now();
        continue;
      }
      // Transient poll failure: retry a few times before surfacing to the queue.
      pollErrorCount += 1;
      if (pollErrorCount >= BULK_ASYNC_MAX_POLL_ERRORS) {
        throw error;
      }
      continue;
    }

    for (const type of BULK_ASYNC_ENTITY_TYPES) {
      const group = poll.payload?.[type];
      if (!group) continue;
      for (const item of group.completed || []) {
        if (type === "custodian") {
          aggregate.custodian.completed.set(item.id, item);
        } else {
          aggregate[type].completed.add(item);
        }
      }
      for (const failure of group.failed || []) {
        aggregate[type].failed.set(failure.id, failure);
      }
      remaining[type] = group.remaining || [];
    }

    const prevDone = lastReportedDone;
    reportProgress(total);
    if (lastReportedDone !== prevDone) {
      lastChangeTs = Date.now();
    } else if (Date.now() - lastChangeTs > BULK_ASYNC_STUCK_TIMEOUT_MS) {
      throw new Error("Bulk custodian async sync appears stuck");
    }
  }

  return {
    custodian: {
      completed: [...aggregate.custodian.completed.values()],
      failed: [...aggregate.custodian.failed.values()]
    },
    fundSchedule: {
      completed: [...aggregate.fundSchedule.completed],
      failed: [...aggregate.fundSchedule.failed.values()]
    },
    cashFlow: {
      completed: [...aggregate.cashFlow.completed],
      failed: [...aggregate.cashFlow.failed.values()]
    }
  };
};

const buildBulkCustodianItem = custodian => {
  const item = {};
  for (const key of CUSTODIAN_SYNC_API_KEYS) {
    if (key in custodian && custodian[key] !== undefined) {
      item[key] = custodian[key];
    }
  }
  if (item.name === null) {
    item.name = "";
  }
  item.sectionId = custodian.sectionId;
  return item;
};

const syncExchangeRateDate = (rate, date) => {
  if (!rate || !date) return rate;
  try {
    const rateDetails = JSON.parse(rate);
    if (rateDetails.date !== date) {
      rateDetails.date = date;
      return JSON.stringify(rateDetails);
    }
  } catch (e) {}
  return rate;
};

const buildBulkCashflowItem = cashflow => {
  const date = converUIDateFormatToCustodianHistoryApiDateFormat(cashflow.date);
  if (!date) return null;
  const source = {
    ...cashflow,
    date,
    cashInExchangeRate: syncExchangeRateDate(cashflow.cashInExchangeRate, date),
    cashOutExchangeRate: syncExchangeRateDate(cashflow.cashOutExchangeRate, date)
  };
  const item = {};
  for (const key of CUSTODIAN_CASHFLOW_API_KEYS) {
    if (key in source && source[key] !== undefined) {
      item[key] = source[key];
    }
  }
  item.custodianId = cashflow.custodianId;
  return item;
};

const buildBulkFundScheduleItem = fundSchedule => {
  const date = converUIDateFormatToCustodianHistoryApiDateFormat(fundSchedule.date);
  if (!date) return null;
  const source = { ...fundSchedule, date };
  const item = {};
  for (const key of FUND_SCHEDULE_API_KEYS) {
    if (key in source && source[key] !== undefined && source[key] !== null) {
      item[key] = source[key];
    }
  }
  item.custodianId = fundSchedule.custodianId;
  return item;
};

const emptyBulkResult = () => ({
  custodian: { completed: [], failed: [] },
  fundSchedule: { completed: [], failed: [] },
  cashFlow: { completed: [], failed: [] }
});

// Generic bulk sync for custodians, cash flows and fund schedules through the async
// endpoint, routed via the sync queue. Each entity group is { create: [], update: [] }
// of fully-formed objects (custodians carry sectionId; cash flows / fund schedules carry
// custodianId). Callers own optimistic local writes; this action owns apiKey filtering,
// the submit/poll loop, and the post-completion side effects the per-entity actions run
// (merge returned custodians, fetch unknown tickers, refresh cashflow-touched custodians,
// update section IRRs, refresh the dashboard).
export const bulkSyncCustodianEntities = ({
  portfolioId,
  custodian = { create: [], update: [] },
  cashFlow = { create: [], update: [] },
  fundSchedule = { create: [], update: [] },
  custodiansToRefresh = [],
  onProgress,
  onComplete,
  onError
}) => {
  return (dispatch, getState) => {
    if (isAppInViewMode() === true) {
      onComplete?.(emptyBulkResult());
      return;
    }

    // Mirror updateCustodian: ensure value-bearing custodians carry a valueTickerId and
    // valueExchangeRate, and convert PVST rates to a value exchange rate, before submitting.
    const enrichCustodianItemValue = item => {
      if (item.value != null) {
        if (item.valueTickerId == null) {
          item.valueTickerId = custodianSelector(getState(), item.id)?.valueTickerId;
        }
        if (item.valueExchangeRate == null && item.valueTickerId != null) {
          const itemPortfolio =
            custodianPortfolioSelector(getState(), item.id) || portfolioSelector(getState(), portfolioId);
          const portfolioCurrency = itemPortfolio?.currency;
          if (portfolioCurrency) {
            const rate = getExchangeRate(
              getState(),
              getTickerUsingId(getState(), item.valueTickerId).shortName,
              portfolioCurrency
            );
            item.valueExchangeRate = getExchangeRateDetails(
              getTickerUsingShortName(getState(), portfolioCurrency).id,
              rate
            );
          }
        }
      }
      if (item.valueTickerId === PVST_VALUE_TICKER_ID && item.rate) {
        item.valueExchangeRate = dispatch(convertPVSTRateToValueExchangeRate(item.rate, undefined, false));
      }
      return item;
    };

    // Collapse repeated entries for the same custodian (e.g. a lastLink-only touch plus a
    // value update) into one item, mirroring the sync queue's override-previous behaviour.
    const mergeById = items => {
      const map = new Map();
      for (const item of items) {
        map.set(item.id, { ...(map.get(item.id) || {}), ...item });
      }
      return [...map.values()];
    };

    const submitBody = {};
    const custodianCreate = mergeById(
      (custodian.create || []).map(buildBulkCustodianItem).map(enrichCustodianItemValue)
    );
    const custodianUpdate = mergeById(
      (custodian.update || []).map(buildBulkCustodianItem).map(enrichCustodianItemValue)
    );
    const archiveHolding = [
      ...new Set((custodian.archiveHolding || []).filter(id => typeof id === "string" && id.length > 0))
    ];
    if (custodianCreate.length > 0 || custodianUpdate.length > 0 || archiveHolding.length > 0) {
      submitBody.custodian = { create: custodianCreate, update: custodianUpdate };
      if (archiveHolding.length > 0) {
        submitBody.custodian.archiveHolding = archiveHolding;
      }
    }
    const cashFlowCreate = (cashFlow.create || []).map(buildBulkCashflowItem).filter(Boolean);
    const cashFlowUpdate = (cashFlow.update || []).map(buildBulkCashflowItem).filter(Boolean);
    if (cashFlowCreate.length > 0 || cashFlowUpdate.length > 0) {
      submitBody.cashFlow = { create: cashFlowCreate, update: cashFlowUpdate };
    }
    const fundScheduleCreate = (fundSchedule.create || []).map(buildBulkFundScheduleItem).filter(Boolean);
    const fundScheduleUpdate = (fundSchedule.update || []).map(buildBulkFundScheduleItem).filter(Boolean);
    if (fundScheduleCreate.length > 0 || fundScheduleUpdate.length > 0) {
      submitBody.fundSchedule = { create: fundScheduleCreate, update: fundScheduleUpdate };
    }

    if (Object.keys(submitBody).length === 0) {
      onComplete?.(emptyBulkResult());
      return;
    }

    const cashflowTouchedCustodianIds = new Set();
    for (const item of [
      ...(cashFlow.create || []),
      ...(cashFlow.update || []),
      ...(fundSchedule.create || []),
      ...(fundSchedule.update || [])
    ]) {
      if (item.custodianId) {
        cashflowTouchedCustodianIds.add(item.custodianId);
      }
    }

    const request = idempotentId => bulkCustodianSubmitAndPoll(idempotentId, submitBody, onProgress);

    const createdCustodianIds = new Set(custodianCreate.map(item => item.id));

    // Route each custodian to its own portfolio (resolved from local state, falling back to the
    // passed portfolioId). For single-portfolio callers everything resolves to portfolioId, so the
    // grouped dispatch is identical to before; reconnectAccounts can span portfolios when the user
    // has moved accounts around, and each custodian still lands in the right portfolio.
    const groupByPortfolio = custodians => {
      const map = {};
      for (const item of custodians) {
        const resolvedPortfolioId = custodianPortfolioSelector(getState(), item.id)?.id || portfolioId;
        if (!map[resolvedPortfolioId]) {
          map[resolvedPortfolioId] = [];
        }
        map[resolvedPortfolioId].push(item);
      }
      return map;
    };

    const onSuccess = result => {
      const completedCustodians = result.custodian.completed || [];
      if (completedCustodians.length > 0) {
        // Match the per-entity updateCustodian merge order: for newly created custodians prefer
        // the locally-held fields over the server response (so an edit made to the row during the
        // async window isn't clobbered), while updates take the server payload. Server-only fields
        // (irr, tsModified, etc.) are preserved either way.
        const custodiansToApply = completedCustodians.map(serverCustodian => {
          if (!createdCustodianIds.has(serverCustodian.id)) {
            return serverCustodian;
          }
          const localCustodian = custodianSelector(getState(), serverCustodian.id);
          return localCustodian ? { ...serverCustodian, ...localCustodian } : serverCustodian;
        });
        dispatch(updateCustodianInBulkAction(null, null, true, groupByPortfolio(custodiansToApply)));

        const unknownTickerIds = new Set();
        for (const completed of completedCustodians) {
          for (const tickerId of [completed.valueTickerId, completed.costTickerId]) {
            if (tickerId && getTickerUsingId(getState(), tickerId)?.shortName === UNKNOWN_TICKER_SHORT_NAME) {
              unknownTickerIds.add(tickerId);
            }
          }
        }
        if (unknownTickerIds.size > 0) {
          dispatch(refreshTickerData(() => dispatch(updateDashboardAction(null))));
        }

        const custodianSectionIds = completedCustodians.map(item => item.sectionId).filter(Boolean);
        if (custodianSectionIds.length > 0) {
          dispatch(updateSectionIrr(custodianSectionIds));
        }
        dispatch(updateDashboardAction(completedCustodians.map(item => item.id)));
      }

      // Build one de-duped refresh set: caller-supplied account custodians plus the
      // custodians whose cash flows / fund schedules changed (server recomputes their XIRR;
      // resolve children to their parent so the batch refresh cascades to the holding).
      const refreshById = new Map();
      const addRefresh = custodian => {
        const fresh = custodian && custodian.id ? custodianSelector(getState(), custodian.id) || custodian : custodian;
        if (fresh && fresh.id) {
          refreshById.set(fresh.id, fresh);
        }
      };
      for (const custodian of custodiansToRefresh) {
        addRefresh(custodian);
      }
      const cashflowSectionIds = [];
      for (const custodianId of cashflowTouchedCustodianIds) {
        const touched = custodianSelector(getState(), custodianId);
        if (!touched) continue;
        if (touched.sectionId) {
          cashflowSectionIds.push(touched.sectionId);
        }
        addRefresh(touched.parentId ? custodianSelector(getState(), touched.parentId) : touched);
      }
      if (cashflowSectionIds.length > 0) {
        dispatch(updateSectionIrr(cashflowSectionIds));
      }

      const allFailed = [...result.custodian.failed, ...result.cashFlow.failed, ...result.fundSchedule.failed];
      if (allFailed.length > 0) {
        console.error("bulkSyncCustodianEntities partial failures", allFailed);
      }

      const refreshList = [...refreshById.values()];
      if (refreshList.length > 0) {
        dispatch(
          refreshCustodianBatch(
            groupByPortfolio(refreshList),
            0,
            0,
            () => onComplete?.(result),
            () => onComplete?.(result)
          )
        );
      } else {
        onComplete?.(result);
      }
    };

    const syncItem = new SyncItem(
      SyncItemType.BULK_UPDATE,
      `bulkSync-${getUuid()}`,
      request,
      0,
      false,
      onSuccess,
      onError
    );
    dispatch(enqueueItem(syncItem));
  };
};

const SECTION_IRR_UPDATE_DEBOUNCE_MS = 1000;
const pendingSectionIrrIds = new Set();
let sectionIrrUpdateTimer = null;

export const updateSectionIrr = (sectionIds, promise) => {
  return dispatch => {
    if (promise) {
      dispatch(flushSectionIrrUpdate(sectionIds, promise));
      return;
    }

    for (const id of sectionIds) {
      pendingSectionIrrIds.add(id);
    }

    clearTimeout(sectionIrrUpdateTimer);
    sectionIrrUpdateTimer = setTimeout(() => {
      sectionIrrUpdateTimer = null;
      const idsToUpdate = [...pendingSectionIrrIds];
      pendingSectionIrrIds.clear();
      dispatch(flushSectionIrrUpdate(idsToUpdate));
    }, SECTION_IRR_UPDATE_DEBOUNCE_MS);
  };
};

const flushSectionIrrUpdate = (sectionIds, promise) => {
  return async (dispatch, getState) => {
    if (isAppInViewMode() === true) return;
    sectionIds = [...new Set(sectionIds)];

    var sectionItemsToUpdate = [];
    for (const id of sectionIds) {
      const portfolio = sectionPortfolioSelector(getState(), id);
      if (!portfolio === true || portfolio.write === 0) {
        continue;
      }
      const section = sectionSelector(getState(), id);
      const sectionCustodians = sectionCustodiansSelector(getState(), portfolio.id, id, false, true);
      const anyCustodianHasIrr = sectionCustodians.find(
        item => isCustodianEmpty(item) === false && !!item.irr === true
      );
      if (anyCustodianHasIrr) {
        sectionItemsToUpdate.push({ section, portfolioId: portfolio.id, portfolioTickerId: portfolio.tickerId });
      }
    }

    if (sectionItemsToUpdate.length === 0) {
      return;
    }

    const sectionPortfolioTickerMap = new Map();
    for (const item of sectionItemsToUpdate) {
      if (sectionPortfolioTickerMap.get(item.portfolioTickerId)) {
        sectionPortfolioTickerMap.get(item.portfolioTickerId).push(item.section.id);
      } else {
        sectionPortfolioTickerMap.set(item.portfolioTickerId, [item.section.id]);
      }
    }

    if (promise) {
      await promise;
    }

    for (let [key, value] of sectionPortfolioTickerMap) {
      ApiClient.updateSectionIrr(getUuid(), key, value)
        .then(apiData => {
          const udpatedSections = apiData.payload;
          if (udpatedSections.length > 0) {
            const portfolioSectionsMap = {};
            for (const section of udpatedSections) {
              const portfolio = sectionPortfolioSelector(getState(), section.id);
              if (!portfolio === false) {
                if (!portfolioSectionsMap[portfolio.id]) {
                  portfolioSectionsMap[portfolio.id] = [];
                }
                portfolioSectionsMap[portfolio.id].push(section);
              }
            }
            dispatch(updateSectionAction(portfolioSectionsMap, ["irr"]));
            dispatch(updateDashboardAction([udpatedSections.map(item => item.id)]));
          }
        })
        .catch(_ => {
          // captureError(getState(), apiError);
        });
    }

    dispatch(updateSheetIrr(sectionItemsToUpdate.map(item => item.section.sheetId), true));
  };
};

const updateSheetIrr = (sheetIds, bypassFilters = false) => {
  return (dispatch, getState) => {
    if (isAppInViewMode() === true) return;
    sheetIds = [...new Set(sheetIds)];

    var sheetItemsToUpdate = [];
    for (const id of sheetIds) {
      const portfolio = sheetPortfolioSelector(getState(), id);
      if (!portfolio === true || portfolio.write === 0) {
        continue;
      }
      let isSkip = false;
      if (!bypassFilters) {
        const sheetSections = sheetSectionsSelector(getState(), portfolio.id, id);
        const anySectionHasIrr = sheetSections.filter(item => {
          const anyCustodianHasIrr = sectionCustodiansSelector(getState(), portfolio.id, item.id, false, true).filter(
            item => isCustodianEmpty(item) === false && !!item.irr === true
          );
          return anyCustodianHasIrr.length > 0 ? true : false;
        });

        if (anySectionHasIrr.length === 0) {
          isSkip = true;
        }
      }

      if (isSkip) {
        continue;
      }

      sheetItemsToUpdate.push({ id, portfolioId: portfolio.id, portfolioTickerId: portfolio.tickerId });
    }

    if (sheetItemsToUpdate.length === 0) {
      return;
    }

    const sheetPortfolioTickerMap = new Map();
    for (const item of sheetItemsToUpdate) {
      if (sheetPortfolioTickerMap.get(item.portfolioTickerId)) {
        sheetPortfolioTickerMap.get(item.portfolioTickerId).push(item.id);
      } else {
        sheetPortfolioTickerMap.set(item.portfolioTickerId, [item.id]);
      }
    }

    for (let [key, value] of sheetPortfolioTickerMap) {
      ApiClient.updateSheetIrr(getUuid(), key, value)
        .then(apiData => {
          const updatedSheets = apiData.payload;
          if (updatedSheets.length > 0) {
            const portfolioSheetsMap = {};
            for (const sheet of updatedSheets) {
              const portfolio = sheetPortfolioSelector(getState(), sheet.id);
              if (!portfolio === false) {
                if (!portfolioSheetsMap[portfolio.id]) {
                  portfolioSheetsMap[portfolio.id] = [];
                }
                portfolioSheetsMap[portfolio.id].push(sheet);
              }
            }
            dispatch(updateSheetAction(portfolioSheetsMap, ["irr"]));
            dispatch(updateDashboardAction([updatedSheets.map(item => item.id)]));
          }
        })
        .catch(_ => {
          // captureError(getState(), apiError);
        });
    }
  };
};

export const getIrrValue = (irrString, dontSanitize = false) => {
  try {
    const details = JSON.parse(irrString);
    if (!details.all.error === false) {
      return null;
    }
    return !dontSanitize ? sanitizeIrr(details.all.value) : details.all.value;
  } catch (e) {
    return null;
  }
};

export const moveCustodian = (
  fromSectionId,
  toSectionId,
  custodianId,
  newSortKey,
  showUndoToast = false,
  isNewSheet = false,
  isNewSection = false
) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioCustodians = currentPortfolio.details.custodian;
    const custodian = currentPortfolioCustodians.find(custodian => custodian.id === custodianId);
    const targetPortfolio = sectionPortfolioSelector(getState(), toSectionId);

    if (!targetPortfolio === true) {
      return;
    }

    if (custodian) {
      const isMovingAcrossPortfolios = targetPortfolio.id !== currentPortfolio.id;
      const originalSortKey = custodian.sortKey;
      const originalSectionId = custodian.sectionId;

      let custodiansToMove = [];
      if (isMovingAcrossPortfolios) {
        if (!custodian.parentId === false) {
          custodiansToMove = [
            custodianSelector(getState(), custodian.parentId),
            ...custodiansWithSameParentIdSelector(getState(), currentPortfolio.id, custodian.parentId)
          ];
        } else {
          custodiansToMove = [
            custodian,
            ...custodiansWithSameParentIdSelector(getState(), currentPortfolio.id, custodian.id)
          ];
        }
      } else {
        custodian.sortKey = newSortKey;
        custodiansToMove = [custodian];
      }

      for (const custodian of custodiansToMove) {
        custodian.sectionId = toSectionId;
      }

      const custodianIdsToMove = custodiansToMove.map(item => item.id);
      if (isMovingAcrossPortfolios) {
        dispatch(deleteCustodianBulkAction(currentPortfolio.id, custodianIdsToMove));
      }
      dispatch(updateCustodianInBulkAction(targetPortfolio.id, custodiansToMove));
      dispatch(updateDashboardAction(custodianIdsToMove));

      // removeCreatedContainers is false once finishMove has run: the sheet/section
      // creates are already enqueued, so dropping them locally would strand them
      // on the server.
      const revertMove = (removeCreatedContainers = true) => {
        if (isMovingAcrossPortfolios) {
          dispatch(deleteCustodianBulkAction(targetPortfolio.id, custodianIdsToMove));
        } else {
          custodian.sortKey = originalSortKey;
        }
        for (const custodian of custodiansToMove) {
          custodian.sectionId = originalSectionId;
        }
        dispatch(updateCustodianInBulkAction(currentPortfolio.id, custodiansToMove));

        if (removeCreatedContainers === true) {
          if (isNewSheet === true) {
            const section = sectionSelector(getState(), toSectionId);
            const sheet = sheetSelector(getState(), section.sheetId);
            dispatch(deleteSheetAction(targetPortfolio.id, sheet.id));
          } else if (isNewSection === true) {
            dispatch(deleteSectionAction(targetPortfolio.id, toSectionId));
          }
        }
        dispatch(updateDashboardAction(custodianIdsToMove));
      };

      const finishMove = () => {
        if (isNewSheet === true) {
          const section = sectionSelector(getState(), toSectionId);
          const sheet = sheetSelector(getState(), section.sheetId);

          const createSheetRequest = idempotentId => ApiClient.createSheet(idempotentId, targetPortfolio.id, sheet);
          const createSheetSyncItem = new SyncItem(
            SyncItemType.CREATE,
            sheet.id,
            createSheetRequest,
            0,
            false,
            apiData => {}
          );
          dispatch(enqueueItem(createSheetSyncItem));

          const createSectionRequest = idempotentId =>
            ApiClient.createSection(idempotentId, targetPortfolio.id, sheet.id, section);
          const createSectionSyncItem = new SyncItem(
            SyncItemType.CREATE,
            section.id,
            createSectionRequest,
            0,
            false,
            apiData => {}
          );
          dispatch(enqueueItem(createSectionSyncItem));
        } else if (isNewSection === true) {
          const section = sectionSelector(getState(), toSectionId);
          const sheet = sheetSelector(getState(), section.sheetId);

          const createSectionRequest = idempotentId =>
            ApiClient.createSection(idempotentId, targetPortfolio.id, sheet.id, section);
          const createSectionSyncItem = new SyncItem(
            SyncItemType.CREATE,
            section.id,
            createSectionRequest,
            0,
            false,
            apiData => {}
          );
          dispatch(enqueueItem(createSectionSyncItem));
        }

        // If all child custodians for a parent are moved out to another section
        // move the parent to the new section as well so that if the user deletes
        // the section the parent doesn't get deleted with it
        if (isMovingAcrossPortfolios === false && !custodian.parentId === false) {
          const parentCustodian = custodianSelector(getState(), custodian.parentId);
          const childCustodians = custodiansWithSameParentIdSelector(
            getState(),
            currentPortfolio.id,
            custodian.parentId
          );
          const childCustodiansInFromSection = childCustodians.filter(item => item.sectionId === fromSectionId);
          if (parentCustodian && parentCustodian.hidden === 1 && childCustodiansInFromSection.length === 0) {
            const request = idempotentId =>
              ApiClient.updateCustodian(idempotentId, fromSectionId, parentCustodian.id, {
                sectionId: toSectionId
              });

            const syncItem = new SyncItem(SyncItemType.UPDATE, parentCustodian.id, request, 0, false, apiData => {});
            dispatch(enqueueItem(syncItem));
          }
        }

        if (custodian.name || custodian.cost || custodian.value) {
          const request = idempotentId =>
            ApiClient.moveCustodian(idempotentId, custodian.id, fromSectionId, toSectionId, custodian.sortKey);
          const syncItem = new SyncItem(SyncItemType.UPDATE, custodian.id, request, 0, false, apiData => {
            if (fromSectionId !== toSectionId) {
              dispatch(updateSectionIrr([fromSectionId, toSectionId]));
            }

            // the server switches a linked account's container when it moves between
            // Assets and Debts (loan in Debts, bank in Assets), which flips its debt sign
            const movedCustodian = apiData?.payload;
            if (
              movedCustodian?.id === custodian.id &&
              !movedCustodian.linkContainer === false &&
              movedCustodian.linkContainer !== custodianSelector(getState(), custodian.id)?.linkContainer
            ) {
              dispatch(
                updateCustodianInBulkAction(
                  targetPortfolio.id,
                  [{ id: custodian.id, linkContainer: movedCustodian.linkContainer }],
                  false
                )
              );
            }
          });
          syncItem.onRevertLocalChange = () => {
            dispatch(
              showToastAction(
                new Toast(toastType.GENERIC_ERROR, i18n.t("moveDestinationUnavailable"), undefined, null, null)
              )
            );
            if (custodianSelector(getState(), custodian.id)?.sectionId !== toSectionId) {
              return;
            }
            revertMove(false);
          };
          dispatch(enqueueItem(syncItem));
        }
      };

      if (showUndoToast === true) {
        const toastMessage = "Moved";
        const toast = new Toast(
          toastType.UNDO,
          toastMessage,
          3000,
          () => {
            revertMove();
          },
          () => {
            finishMove();
          }
        );
        dispatch(showToastAction(toast));
      } else {
        finishMove();
      }
    }
  };
};

export const insertCustodianAtEndOfSection = (
  portfolioId,
  sectionId,
  custodianId,
  hidden = 0,
  additionalProps = {},
  ignoreEmptyCustodians = false,
  removeEmptyCustodians = false
) => {
  return (dispatch, getState) => {
    const currentPortfolio = portfolioSelector(getState(), portfolioId);
    sortPortfolio(currentPortfolio);
    if (ignoreEmptyCustodians === true) {
      custodians = custodians.filter(custodian => !isCustodianEmpty(custodian));
    }
    let custodians = currentPortfolio.details.custodian.filter(temp => temp.sectionId === sectionId);
    if (removeEmptyCustodians === true) {
      const emptyCustodians = custodians.filter(custodian => isCustodianEmpty(custodian));
      if (emptyCustodians.length > 0) {
        dispatch(deleteCustodianBulkAction(portfolioId, emptyCustodians.map(custodian => custodian.id)));
      }
    }
    const foundCustodian = custodians.find(custodian => custodian.id === custodianId) || {};
    const sortKeyBefore = custodians.length > 0 ? custodians[custodians.length - 1].sortKey : null;
    const sortKeyAfter = null;
    const sortKey = foundCustodian.sortKey || getSortKeyBetween(sortKeyBefore, sortKeyAfter);

    dispatch(insertCustodian(portfolioId, sectionId, custodianId, sortKey, hidden, additionalProps));
    return sortKey;
  };
};

export const insertCustodian = (portfolioId, sectionId, custodianId, sortKey, hidden = 0, additionalProps = {}) => {
  return dispatch => {
    const custodian = {
      id: custodianId,
      sectionId: sectionId,
      sortKey: sortKey,
      hidden: hidden,
      ...additionalProps
    };
    dispatch(insertCustodianInBulkAction(portfolioId, [custodian]));
    // Bump lastDashboardUpdateTs so connected grids recompute gridData immediately and the
    // blank-section overlay clears the moment a real row lands instead of waiting on the API.
    dispatch(updateDashboardAction([custodianId]));
  };
};

export const getCurrentCustodianFromCustodianIdSelector = (state, custodianId) => {
  const currentPortfolio = currentPortfolioSelector(state);
  const currentPortfolioCustodians = currentPortfolio.details.custodian;
  return currentPortfolioCustodians.find(custodian => custodian.id === custodianId);
};

export const isCustodianAddedTodaySelector = (state, custodianId, custodianObject = null) => {
  const custodian = custodianObject || custodianSelector(state, custodianId);
  if (custodian) {
    let tsStart = custodian.tsStart;
    if (!custodian.parentId === false) {
      tsStart = custodianSelector(state, custodian.parentId)?.tsStart;
    }

    const tsStartDateStr = getCustodianHistoryFormattedDateString(
      toUserTimezoneDate(tsStart ? tsStart * 1000 : Date.now())
    );
    const todayDateStr = getCustodianHistoryFormattedDateString(toUserTimezoneDate());

    return tsStartDateStr === todayDateStr;
  }

  return false;
};

export const insertTickerCustodianInSection = (
  portfolioId,
  sectionId,
  custodianId,
  custodianDetails,
  newSortKey,
  onSuccess = () => null,
  tickerShortName = null
) => {
  return (dispatch, getState) => {
    const { isLinking, ...details } = custodianDetails;
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioCustodians = currentPortfolio.details.custodian;
    const currentCustodian = getCurrentCustodianFromCustodianIdSelector(getState(), custodianId);
    const custodians = currentPortfolioCustodians.filter(custodian => custodian.sectionId === sectionId);
    const currentIndex = custodians.findIndex(custodian => currentCustodian && custodian.id === currentCustodian.id);
    const sortKeyBefore = custodians.length > 0 ? custodians[custodians.length - 1].sortKey : null;
    const sortKeyAfter = custodians[currentIndex + 1] ? custodians[currentIndex + 1].sortKey : null;
    const sortKey = newSortKey || (currentCustodian || {}).sortKey || getSortKeyBetween(sortKeyBefore, sortKeyAfter);
    const shouldFetchTicker =
      !tickerShortName === false &&
      getTickerUsingId(getState(), details.valueTickerId).shortName === UNKNOWN_TICKER_SHORT_NAME;

    var custodian = {
      ...currentCustodian,
      ...details,
      id: custodianId,
      sectionId: sectionId,
      sortKey: sortKey,
      hidden: 0,
      fetchingValueTickerInfo: shouldFetchTicker === true,
      isLinking: isLinking
    };

    if (!details.valueTickerId === false) {
      const portfolioTicker = getTickerUsingShortName(getState(), currentPortfolio.currency);
      const valueTicker = getTickerUsingId(getState(), details.valueTickerId);
      const rate = getExchangeRate(getState(), valueTicker.shortName, portfolioTicker.shortName);
      details.valueExchangeRate = getExchangeRateDetails(portfolioTicker.id, rate);
    }

    dispatch(insertCustodianInBulkAction(portfolioId, [custodian]));
    dispatch(updateDashboardAction([custodian.id]));

    if (isLinking) {
      dispatch(markCustodianAsLinking(portfolioId, custodian, true));
    }

    requestIdleCallback(() => {
      if (shouldFetchTicker === true) {
        custodian.valueInvalidInputText = tickerShortName;
        dispatch(updateCustodianInBulkAction(portfolioId, [custodian]));
        dispatch(fetchCustodianTickerDetails(custodianId, false, false));
      } else {
        dispatch(
          updateCustodian(
            true,
            custodianId,
            {
              sortKey,
              ...details
            },
            true,
            onSuccess
          )
        );
      }
    });

    return {
      custodianId: getUuid(),
      sortKey: getSortKeyBetween(sortKey, sortKeyAfter)
    };
  };
};

export const insertSection = (portfolioId, sheetId, sectionData) => {
  return dispatch => {
    const section = {
      id: sectionData.id,
      name: sectionData.name,
      sheetId: sheetId,
      sortKey: sectionData.sortKey
    };

    dispatch(insertSectionAction(portfolioId, section));

    if (sectionData.rows) {
      const custodians = sectionData.rows.map(row => {
        return {
          id: row.id,
          sectionId: section.id,
          sortKey: row.sortKey,
          autoAddedForEmptySections: true
        };
      });
      dispatch(insertCustodianInBulkAction(portfolioId, custodians));
    }

    // Bump lastDashboardUpdateTs so connected grids (AssetsComponent etc.) recompute their
    // gridData immediately and the new section's blank-state UI renders without waiting for the
    // API roundtrip.
    dispatch(updateDashboardAction([section.id]));

    const request = idempotentId => ApiClient.createSection(idempotentId, portfolioId, sheetId, section);
    const syncItem = new SyncItem(SyncItemType.CREATE, section.id, request, 0, false, apiData => {
      document.getElementById(sectionData.id)?.scrollIntoView({ behavior: "smooth" });
    });
    dispatch(enqueueItem(syncItem));
  };
};

export const updateSection = (portfolioId, sheetId, sectionId, propertiesToUpdate) => {
  return (dispatch, getState) => {
    const currentPortfolio = portfolioSelector(getState(), portfolioId);
    const currentPortfolioSections = currentPortfolio.details.section;
    var section = currentPortfolioSections.find(section => section.id === sectionId);

    if (section) {
      const isIgnorable = section.expanded !== propertiesToUpdate.expanded;
      const isExpandedChanged =
        propertiesToUpdate.expanded !== undefined && propertiesToUpdate.expanded !== section.expanded;
      section = { ...section, ...propertiesToUpdate };
      if (isExpandedChanged) {
        markDirtyField(dirtyItemTypes.SECTION, sectionId, "expanded");
      }

      const apiKeys = ["name", "sheetId", "sortKey", "expanded", "columnSortKey", "columnSortOrder"];
      const apiSection = Object.keys(section)
        .filter(
          key => apiKeys.includes(key) && (section[key] !== null || ["columnSortKey", "columnSortOrder"].includes(key))
        )
        .reduce((obj, key) => {
          obj[key] = section[key];
          return obj;
        }, {});

      dispatch(updateSectionAction({ [portfolioId]: [section] }));
      dispatch(updateDashboardAction([sectionId]));

      const request = idempotentId =>
        ApiClient.updateSection(idempotentId, portfolioId, sheetId, sectionId, apiSection);
      const syncItem = new SyncItem(SyncItemType.UPDATE, sectionId, request, 0, true, apiData => {});
      syncItem.ignorable = isIgnorable;
      dispatch(enqueueItem(syncItem));
    }
  };
};

export const moveSection = (sheetId, sectionId, targetSheetId, isNewSheet = false) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioSections = currentPortfolio.details.section;
    const targetPortfolio = sheetPortfolioSelector(getState(), targetSheetId);
    var section = currentPortfolioSections.find(section => section.id === sectionId);
    const originalSheetId = section.sheetId;

    if (section) {
      var associatedCustodiansOutsideSection = [];
      if (targetPortfolio.id !== currentPortfolio.id) {
        associatedCustodiansOutsideSection = sectionCustodiansSelector(
          getState(),
          currentPortfolio.id,
          sectionId,
          true
        ).filter(item => item.sectionId !== sectionId);

        if (associatedCustodiansOutsideSection.length > 0) {
          const custodiansToMove = JSON.parse(JSON.stringify(associatedCustodiansOutsideSection));
          for (const item of custodiansToMove) {
            item.sectionId = sectionId;
          }
          dispatch(deleteCustodianBulkAction(currentPortfolio.id, custodiansToMove.map(item => item.id)));
          dispatch(updateCustodianInBulkAction(targetPortfolio.id, custodiansToMove));
        }
      }

      section = { ...section, sheetId: targetSheetId };

      dispatch(moveSectionAction(currentPortfolio.id, section, targetPortfolio.id));
      dispatch(updateDashboardAction([sectionId]));

      const toastMessage = "Moved";
      const toast = new Toast(
        toastType.UNDO,
        toastMessage,
        3000,
        () => {
          section = { ...section, sheetId: originalSheetId };

          if (associatedCustodiansOutsideSection.length > 0) {
            dispatch(
              deleteCustodianBulkAction(targetPortfolio.id, associatedCustodiansOutsideSection.map(item => item.id))
            );
            dispatch(updateCustodianInBulkAction(currentPortfolio.id, associatedCustodiansOutsideSection));
          }

          dispatch(moveSectionAction(targetPortfolio.id, section, currentPortfolio.id));

          if (isNewSheet === true) {
            dispatch(deleteSheetAction(targetPortfolio.id, targetSheetId));
          }
          dispatch(updateDashboardAction([sectionId]));
        },
        () => {
          if (isNewSheet === true) {
            const sheet = sheetSelector(getState(), targetSheetId);
            const createSheetRequest = idempotentId => ApiClient.createSheet(idempotentId, targetPortfolio.id, sheet);
            const createSheetSyncItem = new SyncItem(
              SyncItemType.CREATE,
              sheet.id,
              createSheetRequest,
              0,
              false,
              apiData => {}
            );
            dispatch(enqueueItem(createSheetSyncItem));
          }

          if (associatedCustodiansOutsideSection.length > 0) {
            const apiCustodians = associatedCustodiansOutsideSection.map(item => {
              return { id: item.id, sectionId: sectionId };
            });
            const updateRequest = idempotentId => ApiClient.updateCustodianBulk(idempotentId, apiCustodians);
            const updateSyncItem = new SyncItem(
              SyncItemType.BULK_UPDATE,
              currentPortfolio.id + "bulk_move",
              updateRequest,
              0,
              false,
              apiData => {}
            );
            dispatch(enqueueItem(updateSyncItem));
          }

          const apiKeys = ["name", "sheetId", "sortKey", "expanded", "columnSortKey", "columnSortOrder"];
          const apiSection = Object.keys(section)
            .filter(
              key =>
                apiKeys.includes(key) && (section[key] !== null || ["columnSortKey", "columnSortOrder"].includes(key))
            )
            .reduce((obj, key) => {
              obj[key] = section[key];
              return obj;
            }, {});

          const request = idempotentId =>
            ApiClient.updateSection(idempotentId, currentPortfolio.id, sheetId, sectionId, apiSection);
          const syncItem = new SyncItem(SyncItemType.UPDATE, sectionId, request, 0, true, apiData => {
            if (associatedCustodiansOutsideSection.length > 0) {
              dispatch(
                updateSectionIrr([sectionId, ...associatedCustodiansOutsideSection.map(item => item.sectionId)])
              );
            } else {
              dispatch(updateSheetIrr([sheetId, targetSheetId]));
            }
          });
          dispatch(enqueueItem(syncItem));
        }
      );
      dispatch(showToastAction(toast));
    }
  };
};

export const updateSheet = (portfolioId, sheetId, propertiesToUpdate) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioSheets = currentPortfolio.details.sheet;
    var sheet = currentPortfolioSheets.find(sheet => sheet.id === sheetId);

    if (sheet) {
      sheet = { ...sheet, ...propertiesToUpdate };

      const apiKeys = ["name", "sortKey", "updateFrequency"];
      const apiSheet = Object.keys(sheet)
        .filter(key => apiKeys.includes(key) && sheet[key] !== null)
        .reduce((obj, key) => {
          obj[key] = sheet[key];
          return obj;
        }, {});

      dispatch(updateSheetAction({ [portfolioId]: [sheet] }));
      sortPortfolio(currentPortfolio);
      dispatch(updateDashboardAction([sheetId]));

      const request = idempotentId => ApiClient.updateSheet(idempotentId, portfolioId, sheetId, apiSheet);
      const syncItem = new SyncItem(SyncItemType.UPDATE, sheetId, request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    }
  };
};

export const moveSheet = (sheetId, targetPortfolioId) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioSheets = currentPortfolio.details.sheet;
    var sheet = currentPortfolioSheets.find(sheet => sheet.id === sheetId);

    if (sheet) {
      const sheetSectionIds = sheetSectionsSelector(getState(), currentPortfolio.id, sheetId).map(item => item.id);

      const custodiansOutsideSheet = sheetCustodiansSelector(getState(), currentPortfolio.id, sheetId, true).filter(
        item => sheetSectionIds.includes(item.sectionId) === false
      );
      var custodiansOutsideSheetToMove = [];

      if (custodiansOutsideSheet.length > 0) {
        custodiansOutsideSheetToMove = JSON.parse(JSON.stringify(custodiansOutsideSheet));
        for (const item of custodiansOutsideSheetToMove) {
          item.sectionId = sheetSectionIds[0];
        }
        dispatch(deleteCustodianBulkAction(currentPortfolio.id, custodiansOutsideSheetToMove.map(item => item.id)));
        dispatch(updateCustodianInBulkAction(targetPortfolioId, custodiansOutsideSheetToMove));
      }

      dispatch(moveSheetAction(currentPortfolio.id, sheet, targetPortfolioId));
      dispatch(updateDashboardAction([sheetId]));

      const toastMessage = "Moved";
      const toast = new Toast(
        toastType.UNDO,
        toastMessage,
        3000,
        () => {
          dispatch(deleteCustodianBulkAction(targetPortfolioId, custodiansOutsideSheet.map(item => item.id)));
          dispatch(updateCustodianInBulkAction(currentPortfolio.id, custodiansOutsideSheet));

          dispatch(moveSheetAction(targetPortfolioId, sheet, currentPortfolio.id));
          dispatch(updateDashboardAction([sheetId]));
        },
        () => {
          if (custodiansOutsideSheetToMove.length > 0) {
            const apiCustodians = custodiansOutsideSheetToMove.map(item => {
              return { id: item.id, sectionId: item.sectionId };
            });
            const updateRequest = idempotentId => ApiClient.updateCustodianBulk(idempotentId, apiCustodians);
            const updateSyncItem = new SyncItem(
              SyncItemType.BULK_UPDATE,
              currentPortfolio.id + "bulk_move",
              updateRequest,
              0,
              false,
              apiData => {
                dispatch(updateSectionIrr(custodiansOutsideSheetToMove.map(item => item.sectionId)));
              }
            );
            dispatch(enqueueItem(updateSyncItem));
          }

          const request = idempotentId =>
            ApiClient.updateSheet(idempotentId, currentPortfolio.id, sheetId, { portfolioId: targetPortfolioId });
          const syncItem = new SyncItem(SyncItemType.UPDATE, sheetId, request, 0, true, apiData => {});
          dispatch(enqueueItem(syncItem));
        }
      );
      dispatch(showToastAction(toast));
    }
  };
};

export const archiveCustodian = (sectionId, custodianId, isEmptyCustodian, options = {}, showUndoToast = true) => {
  return (dispatch, getState) => {
    if (!custodianId) {
      return;
    }

    const { isArchiveOnDelete = false } = options;
    const currentPortfolio = custodianPortfolioSelector(getState(), custodianId);
    const custodian = currentPortfolio.details.custodian.find(custodian => custodian.id === custodianId);
    const parentCustodian = currentPortfolio.details.custodian.find(item => item.id === custodian.parentId);
    const isCustodianAddedTodayFlag = isCustodianAddedTodaySelector(
      getState(),
      parentCustodian ? parentCustodian.id : custodian.id
    );

    const associatedCustodians =
      custodian.linkType === accountLinkingService.KUBERA_PORTFOLIO
        ? custodiansLinkedToSameAccountSelector(getState(), custodianId).portfolioCustodiansMap[
            currentPortfolio.id
          ].filter(item => item.id !== custodianId)
        : [];

    dispatch(deleteCustodianAction(currentPortfolio.id, custodianId));

    const siblings = custodiansWithSameParentIdSelector(getState(), currentPortfolio.id, custodian.parentId);
    for (const item of siblings) {
      dispatch(deleteCustodianAction(currentPortfolio.id, item.id));
    }
    if (!custodian.parentId === false) {
      dispatch(deleteCustodianAction(currentPortfolio.id, custodian.parentId));
    }
    for (const item of associatedCustodians) {
      dispatch(deleteCustodianAction(currentPortfolio.id, item.id));
    }
    dispatch(
      updateDashboardAction([
        custodianId,
        ...siblings.map(item => item.id),
        ...associatedCustodians.map(item => item.id)
      ])
    );

    // If custodian is empty and has never been modified don't archive as its a local entry
    if (isEmptyCustodian === true && !custodian.tsModified === true) {
      return;
    }

    const makeArchiveApiCall = () => {
      const custodianIdToArchive = !custodian.parentId === true ? custodianId : custodian.parentId;
      const request = idempotentId => ApiClient.archiveCustodian(idempotentId, sectionId, [custodianIdToArchive]);
      const syncItem = new SyncItem(SyncItemType.DELETE, custodianId, request, 0, true, apiData => {
        dispatch(updateSectionIrr([sectionId, ...siblings.map(item => item.sectionId)]));
      });
      dispatch(enqueueItem(syncItem));

      for (const item of associatedCustodians) {
        const request = idempotentId => ApiClient.archiveCustodian(idempotentId, item.sectionId, [item.id]);
        const syncItem = new SyncItem(SyncItemType.DELETE, item.id, request, 0, true, apiData => {
          dispatch(updateSectionIrr([item.sectionId]));
        });
        dispatch(enqueueItem(syncItem));
      }
    };

    if (isEmptyCustodian === true) {
      makeArchiveApiCall();
    } else if (showUndoToast === true) {
      const toastMessage = !isCustodianAddedTodayFlag
        ? isArchiveOnDelete
          ? "Archived to preserve history"
          : "Archived"
        : "Deleted";
      const toast = new Toast(
        toastType.UNDO,
        toastMessage,
        undefined,
        () => {
          if (!parentCustodian === false) {
            dispatch(insertCustodianInBulkAction(currentPortfolio.id, [parentCustodian]));
          }
          dispatch(insertCustodianInBulkAction(currentPortfolio.id, [custodian]));
          dispatch(insertCustodianInBulkAction(currentPortfolio.id, siblings));
          dispatch(insertCustodianInBulkAction(currentPortfolio.id, associatedCustodians));
          dispatch(
            updateDashboardAction([
              custodianId,
              ...siblings.map(item => item.id),
              ...associatedCustodians.map(item => item.id)
            ])
          );
        },
        () => {
          makeArchiveApiCall();
        }
      );
      dispatch(showToastAction(toast));
    } else {
      makeArchiveApiCall();
    }
  };
};

// Archives a single holding custodian, unlike archiveCustodian which treats a
// holding as a proxy for its parent and archives the whole account.
export const archiveCustodianHolding = (
  sectionId,
  custodianId,
  onUndo = () => null,
  showUndoToast = true,
  onSuccess = () => null
) => {
  return (dispatch, getState) => {
    if (!custodianId === true) {
      return;
    }

    const currentPortfolio = custodianPortfolioSelector(getState(), custodianId);
    const custodian = currentPortfolio.details.custodian.find(item => item.id === custodianId);
    if (!custodian === true) {
      return;
    }
    const parentCustodian = custodianSelector(getState(), custodian.parentId);

    // Remove the holding from the portfolio and (via DELETE_HOLDING_BULK) from
    // the open parent's details.holdings list, then recompute the parent total
    // from the remaining holdings so the list and total update immediately; the
    // details reducer mirrors the new value onto details.info.
    const dashboardIds = [custodianId];
    if (!custodian.parentId === false && custodian.hidden === 1) {
      dispatch(deleteHoldingBulkAction([custodianId]));
    }
    dispatch(deleteCustodianAction(currentPortfolio.id, custodianId));
    dispatch(updateParentCustodianValueWithHoldingsTotal(custodian.parentId, true));
    dashboardIds.push(custodian.parentId);
    dashboardIds.push(custodian.sectionId);
    dispatch(updateDashboardAction(dashboardIds));

    const makeArchiveApiCall = () => {
      // Dedicated holding-archive endpoint: zeroes the holding's value on the
      // server (parent/siblings untouched) and returns an empty body. The local
      // store was already updated optimistically above.
      const request = idempotentId => ApiClient.archiveHolding(idempotentId, sectionId, [custodianId]);
      const syncItem = new SyncItem(SyncItemType.DELETE, custodianId, request, 0, true, () => {
        dispatch(updateSectionIrr([sectionId]));
        const remainingChildren = custodiansWithSameParentIdSelector(
          getState(),
          currentPortfolio.id,
          custodian.parentId
        );
        if (parentCustodian && remainingChildren.length === 0) {
          dispatch(
            archiveCustodian(parentCustodian.sectionId, custodian.parentId, false, { isArchiveOnDelete: true }, false)
          );
        }
        onSuccess();
      });
      dispatch(enqueueItem(syncItem));
    };

    if (showUndoToast === true) {
      const toast = new Toast(
        toastType.UNDO,
        "Archived to preserve history",
        undefined,
        () => {
          const undoDashboardIds = [custodianId];
          if (!custodian.parentId === false && custodian.hidden === 1) {
            dispatch(insertHoldingBulkAction([custodian]));
          }
          dispatch(insertCustodianInBulkAction(currentPortfolio.id, [custodian]));
          dispatch(updateParentCustodianValueWithHoldingsTotal(custodian.parentId, true));
          undoDashboardIds.push(custodian.parentId);
          undoDashboardIds.push(custodian.sectionId);
          dispatch(updateDashboardAction(undoDashboardIds));
          onUndo();
        },
        () => {
          makeArchiveApiCall();
        }
      );
      dispatch(showToastAction(toast));
    } else {
      makeArchiveApiCall();
    }
  };
};

export const deleteAllConnectedCustodians = (custodianId, excludeChildren) => {
  return (dispatch, getState) => {
    const { custodians, custodianPortfolioMap } = custodiansLinkedToSameAccountSelector(
      getState(),
      custodianId,
      excludeChildren
    );
    if (custodians.length === 0) {
      return;
    }

    for (const item of custodians) {
      dispatch(deleteCustodian(item.id, false, false, custodianPortfolioMap[item.id]));
    }

    const custodianIds = custodians.map(custodian => custodian.id);
    dispatch(updateDashboardAction(custodianIds));
  };
};

export const deleteCustodian = (custodianId, isEmptyCustodian, showToast = true, portfolioId = null) => {
  return (dispatch, getState) => {
    if (!custodianId === true) {
      return;
    }

    const currentPortfolio =
      !portfolioId === false ? portfolioSelector(getState(), portfolioId) : currentPortfolioSelector(getState());
    var custodian = currentPortfolio.details.custodian.find(custodian => custodian.id === custodianId);
    if (!custodian === true) {
      return;
    }

    const associatedCustodians =
      custodian.linkType === accountLinkingService.KUBERA_PORTFOLIO
        ? custodiansLinkedToSameAccountSelector(getState(), custodianId).portfolioCustodiansMap[
            currentPortfolio.id
          ].filter(item => item.id !== custodianId)
        : [];

    dispatch(deleteCustodianAction(currentPortfolio.id, custodianId));
    for (const item of associatedCustodians) {
      dispatch(deleteCustodianAction(currentPortfolio.id, item.id));
    }
    dispatch(updateDashboardAction([custodianId, ...associatedCustodians.map(item => item.id)]));

    // If custodian is empty and has never been modified don't archive as its a local entry
    if (isEmptyCustodian === true && !custodian.tsModified === true) {
      return;
    }

    if (isEmptyCustodian === true) {
      const request = idempotentId => ApiClient.deleteCustodian(idempotentId, custodian.sectionId, [custodianId]);
      const syncItem = new SyncItem(SyncItemType.DELETE, custodianId, request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    } else if (showToast === true) {
      const toast = new Toast(
        toastType.UNDO,
        "Deleted",
        undefined,
        () => {
          dispatch(insertCustodianInBulkAction(currentPortfolio.id, [custodian]));
          dispatch(insertCustodianInBulkAction(currentPortfolio.id, associatedCustodians));
          dispatch(updateDashboardAction([custodianId, ...associatedCustodians.map(item => item.id)]));
        },
        () => {
          // front end can not determine to call delete/archive, if custodian is added today with history
          // hance call is changed to archiveCustodian from deleteCustodian
          // api in backend will take care
          const request = idempotentId => ApiClient.archiveCustodian(idempotentId, custodian.sectionId, [custodianId]);
          const syncItem = new SyncItem(SyncItemType.DELETE, custodianId, request, 0, true, apiData => {
            dispatch(updateSectionIrr([custodian.sectionId]));
          });
          dispatch(enqueueItem(syncItem));

          for (const item of associatedCustodians) {
            const request = idempotentId => ApiClient.archiveCustodian(idempotentId, item.sectionId, [item.id]);
            const syncItem = new SyncItem(SyncItemType.DELETE, item.id, request, 0, true, apiData => {
              dispatch(updateSectionIrr([item.sectionId]));
            });
            dispatch(enqueueItem(syncItem));
          }
        }
      );
      dispatch(showToastAction(toast));
    } else {
      const request = idempotentId => ApiClient.deleteCustodian(idempotentId, custodian.sectionId, [custodianId]);
      const syncItem = new SyncItem(SyncItemType.DELETE, custodianId, request, 0, true, apiData => {
        dispatch(updateSectionIrr([custodian.sectionId]));
      });
      dispatch(enqueueItem(syncItem));
    }
  };
};

export const bulkActionSource = {
  SECTION: "SECTION",
  SHEET: "SHEET"
};

export const bulkChangeCustodianStarStatus = (custodianIdArray, sourceId, source) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());

    var starredItemsCount = 0;
    for (const custodian of currentPortfolio.details.custodian) {
      if (custodianIdArray.includes(custodian.id) && custodian.star === 1) {
        starredItemsCount++;
      }
    }

    const shouldStar = starredItemsCount === 0 || starredItemsCount !== custodianIdArray.length;
    dispatch(bulkChangeCustodianStarStatusAction(currentPortfolio.id, custodianIdArray, shouldStar));
    dispatch(updateDashboardAction(custodianIdArray));

    var request = null;
    const starValue = shouldStar === true ? 1 : 0;
    if (source === bulkActionSource.SECTION) {
      request = idempotentId => ApiClient.sectionStarAll(idempotentId, sourceId, starValue);
    } else if (source === bulkActionSource.SHEET) {
      request = idempotentId => ApiClient.sheetStarAll(idempotentId, currentPortfolio.id, sourceId, starValue);
    }
    const syncItem = new SyncItem(SyncItemType.BULK_UPDATE, sourceId, request, 0, true, apiData => {});
    dispatch(enqueueItem(syncItem));
  };
};

export const bulkChangeCustodianUpdatedStatus = (custodianIdArray, sourceId, source) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());

    var updatedItemsCount = 0;
    for (const custodian of currentPortfolio.details.custodian) {
      if (custodianIdArray.includes(custodian.id) && custodian.past === 0) {
        updatedItemsCount++;
      }
    }
    const isUpdated = updatedItemsCount !== custodianIdArray.length;

    dispatch(bulkChangeCustodianUpdatedStatusAction(currentPortfolio.id, custodianIdArray, isUpdated));
    dispatch(updateDashboardAction(custodianIdArray));

    const pastValue = isUpdated === true ? 0 : 1;
    var request = null;
    if (source === bulkActionSource.SECTION) {
      request = idempotentId => ApiClient.sectionSetAsPast(idempotentId, sourceId, pastValue);
    } else if (source === bulkActionSource.SHEET) {
      request = idempotentId => ApiClient.sheetSetAsPast(idempotentId, currentPortfolio.id, sourceId, pastValue);
    }
    const syncItem = new SyncItem(SyncItemType.BULK_UPDATE, sourceId, request, 0, true, apiData => {});
    dispatch(enqueueItem(syncItem));
  };
};

export const archiveSection = (sheetId, sectionId) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioSections = currentPortfolio.details.section;
    var section = currentPortfolioSections.find(section => section.id === sectionId);
    const sectionCustodians = sectionCustodiansSelector(getState(), currentPortfolio.id, sectionId);
    const visibleParentCustodianIdsInSection = sectionCustodians
      .filter(item => !item.parentId === true && !item.hidden === true)
      .map(item => item.id);
    const hiddenCustodiansWithoutParentInSection = sectionCustodians.filter(
      item => item.hidden === 1 && visibleParentCustodianIdsInSection.includes(item.parentId) === false
    );
    const hiddenParentCustodiansWithoutChildrenInSection = sectionCustodians.filter(
      item =>
        !item.parentId === true &&
        item.hidden === 1 &&
        custodiansWithSameParentIdSelector(getState(), currentPortfolio.id, item.id).filter(
          item => item.sectionId === sectionId
        ).length === 0
    );

    if (section) {
      // Don't show toast for empty sections getting archived
      const nonEmptyCustodians = getNonEmptyCustodiansInSection(getState(), currentPortfolio.id, sectionId);
      if (nonEmptyCustodians && nonEmptyCustodians.length === 0) {
        dispatch(deleteSectionAction(currentPortfolio.id, sectionId));

        const request = idempotentId => ApiClient.archiveSection(idempotentId, currentPortfolio.id, sheetId, sectionId);
        const syncItem = new SyncItem(SyncItemType.DELETE, sectionId, request, 0, false, apiData => {
          dispatch(updateDashboardAction([sectionId]));
        });
        dispatch(enqueueItem(syncItem));
        return;
      }

      let associatedSectionCustodians = sectionAssociatedCustodiansSelector(getState(), currentPortfolio.id, sectionId);

      const sectionCustodianIdsMap = {};
      const portfolioCustodiansMap = {};

      const storeCustodianToMaps = (custodian, portfolioId) => {
        if (!portfolioCustodiansMap[portfolioId]) {
          portfolioCustodiansMap[portfolioId] = new Map();
        }
        if (!custodian.parentId === true && !portfolioCustodiansMap[portfolioId].has(custodian.id)) {
          if (!sectionCustodianIdsMap[custodian.sectionId]) {
            sectionCustodianIdsMap[custodian.sectionId] = [];
          }
          sectionCustodianIdsMap[custodian.sectionId].push(custodian.id);
        }
        portfolioCustodiansMap[portfolioId].set(custodian.id, custodian);
      };

      associatedSectionCustodians.forEach(custodian => storeCustodianToMaps(custodian, currentPortfolio.id));

      const hiddenCustodianIdsWithoutParentInSection = hiddenCustodiansWithoutParentInSection.map(item => item.id);
      for (const item of sectionCustodians) {
        if (
          isCryptoLinkingService(item.linkType) &&
          hiddenCustodianIdsWithoutParentInSection.includes(item.id) === false
        ) {
          const {
            portfolioCustodiansMap: accountsLinkedPortfolioCustodiansMap
          } = custodiansLinkedToSameAccountSelector(getState(), item.id, false, false);
          for (const portfolioId in accountsLinkedPortfolioCustodiansMap) {
            const custodians = accountsLinkedPortfolioCustodiansMap[portfolioId];
            custodians.forEach(custodian => storeCustodianToMaps(custodian, portfolioId));
          }
        }
      }

      const [resolveAccessorObj, promise] = resolveWhenCounterReachesZero();

      dispatch(updateSectionIrr(Object.keys(sectionCustodianIdsMap), promise));

      for (const portfolioId in portfolioCustodiansMap) {
        const custodianIds = [...portfolioCustodiansMap[portfolioId].keys()];
        dispatch(deleteCustodianBulkAction(portfolioId, custodianIds));
      }

      dispatch(deleteSectionAction(currentPortfolio.id, sectionId));
      dispatch(updateDashboardAction([sectionId]));

      const toast = new Toast(
        toastType.UNDO,
        "Removed",
        undefined,
        () => {
          dispatch(insertSectionAction(currentPortfolio.id, section));
          dispatch(insertCustodianInBulkAction(currentPortfolio.id, sectionCustodians));
          for (const portfolioId in portfolioCustodiansMap) {
            const custodians = portfolioCustodiansMap[portfolioId].values();
            dispatch(insertCustodianInBulkAction(portfolioId, [...custodians]));
          }
          sortPortfolio(currentPortfolio);
          dispatch(updateDashboardAction([sectionId]));
        },
        () => {
          if (hiddenCustodiansWithoutParentInSection.length > 0) {
            const custodiansToRehome = [];
            for (const item of hiddenCustodiansWithoutParentInSection) {
              const custodian = custodianSelector(getState(), item.id);
              const parentCustodian = custodianSelector(getState(), item.parentId);
              if (!custodian === true || !parentCustodian === true) {
                continue;
              }
              custodian.sectionId = parentCustodian.sectionId;
              custodiansToRehome.push(custodian);
            }

            if (custodiansToRehome.length > 0) {
              dispatch(updateCustodianInBulkAction(currentPortfolio.id, custodiansToRehome));
              dispatch(updateDashboardAction(custodiansToRehome.map(item => item.id)));
            }
          }

          if (hiddenParentCustodiansWithoutChildrenInSection.length > 0) {
            for (const custodian of hiddenParentCustodiansWithoutChildrenInSection) {
              const destinationSectionId = custodiansWithSameParentIdSelector(
                getState(),
                currentPortfolio.id,
                custodian.id
              )[0]?.sectionId;
              dispatch(moveCustodian(custodian.sectionId, destinationSectionId, custodian.id, custodian.sortKey));
            }
          }

          const request = idempotentId =>
            ApiClient.archiveSection(idempotentId, currentPortfolio.id, sheetId, sectionId);
          const syncItem = new SyncItem(SyncItemType.DELETE, sectionId, request, 0, false, () => {});
          dispatch(enqueueItem(syncItem));

          for (const sectionId in sectionCustodianIdsMap) {
            resolveAccessorObj.counter++;
            const request = idempotentId =>
              ApiClient.archiveCustodian(idempotentId, sectionId, sectionCustodianIdsMap[sectionId]);
            const syncItem = new SyncItem(
              SyncItemType.DELETE,
              sectionId,
              request,
              0,
              true,
              () => {
                resolveAccessorObj.counter--;
              },
              () => {
                resolveAccessorObj.counter--;
              }
            );
            dispatch(enqueueItem(syncItem));
          }
        }
      );
      dispatch(showToastAction(toast));
    }
  };
};

export const deleteSheet = sheetId => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioSheets = currentPortfolio.details.sheet;
    var sheet = currentPortfolioSheets.find(sheet => sheet.id === sheetId);
    const associatedSheetCustodians = sheetAssociatedCustodiansSelector(getState(), currentPortfolio.id, sheetId);

    if (sheet) {
      for (const item of associatedSheetCustodians) {
        dispatch(deleteCustodianAction(currentPortfolio.id, item.id));
      }

      dispatch(deleteSheetAction(currentPortfolio.id, sheetId));
      dispatch(updateDashboardAction([sheetId]));
      const request = idempotentId => ApiClient.archiveSheet(idempotentId, currentPortfolio.id, sheetId);
      const syncItem = new SyncItem(SyncItemType.DELETE, sheetId, request, 0, false, apiData => {});
      dispatch(enqueueItem(syncItem));

      for (const custodian of associatedSheetCustodians) {
        if (!custodian.parentId === true) {
          const request = idempotentId => ApiClient.archiveCustodian(idempotentId, custodian.sectionId, [custodian.id]);
          const syncItem = new SyncItem(SyncItemType.DELETE, custodian.id, request, 0, true, apiData => {});
          dispatch(enqueueItem(syncItem));
        }
      }
    }
  };
};

export const archiveSheet = (sheetId, isLastSheet = false) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioSheets = currentPortfolio.details.sheet;
    var sheet = currentPortfolioSheets.find(sheet => sheet.id === sheetId);
    const sheetSections = sheetSectionsSelector(getState(), currentPortfolio.id, sheetId);
    const sheetCustodians = sheetCustodiansSelector(getState(), currentPortfolio.id, sheetId);
    const associatedSheetCustodians = sheetAssociatedCustodiansSelector(getState(), currentPortfolio.id, sheetId);
    const allSheetCustodians = [...sheetCustodians, ...associatedSheetCustodians];

    if (sheet) {
      for (const item of allSheetCustodians) {
        dispatch(deleteCustodianAction(currentPortfolio.id, item.id));
      }

      if (!isLastSheet) {
        dispatch(deleteSheetAction(currentPortfolio.id, sheetId));
      }
      dispatch(updateDashboardAction([sheetId]));
      const toast = new Toast(
        toastType.UNDO,
        "Removed",
        undefined,
        () => {
          if (!isLastSheet) {
            dispatch(insertSheetAction(currentPortfolio.id, sheet));
          }
          for (const section of sheetSections) {
            dispatch(insertSectionAction(currentPortfolio.id, section));
          }
          dispatch(insertCustodianInBulkAction(currentPortfolio.id, allSheetCustodians));
          sortPortfolio(currentPortfolio);
          dispatch(updateDashboardAction([sheetId]));
        },
        () => {
          if (!isLastSheet) {
            const request = idempotentId => ApiClient.archiveSheet(idempotentId, currentPortfolio.id, sheetId);
            const syncItem = new SyncItem(SyncItemType.DELETE, sheetId, request, 0, false, apiData => {});
            dispatch(enqueueItem(syncItem));
          }
          // sheet might have a linked custodian whose other associated accounts might have been moved to another sheet
          // and even those need to be archived when a sheet is archived
          for (const custodian of allSheetCustodians) {
            if (!custodian.parentId === true) {
              const request = idempotentId =>
                ApiClient.archiveCustodian(idempotentId, custodian.sectionId, [custodian.id]);
              const syncItem = new SyncItem(SyncItemType.DELETE, custodian.id, request, 0, true, apiData => {});
              dispatch(enqueueItem(syncItem));
            }
          }
        }
      );
      dispatch(showToastAction(toast));
    }
  };
};

export const insertSheet = (category, sheetData) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const sheet = {
        id: sheetData.id,
        name: sheetData.name,
        category: category,
        sortKey: sheetData.sortKey
      };

      const currentPortfolio = currentPortfolioSelector(getState());
      dispatch(insertSheetAction(currentPortfolio.id, sheet));

      const request = idempotentId => ApiClient.createSheet(idempotentId, currentPortfolio.id, sheet);
      const syncItem = new SyncItem(SyncItemType.CREATE, sheet.id, request, 0, false, apiData => {
        resolve(apiData);
      });
      dispatch(enqueueItem(syncItem));
      dispatch(insertSection(currentPortfolio.id, sheetData.id, sheetData.sections[0]));
    });
  };
};

export const createNewLocalSheet = (portfolioId, sheet, section) => {
  return dispatch => {
    section.sheetId = sheet.id;
    dispatch(insertSheetAction(portfolioId, sheet));
    dispatch(insertSectionAction(portfolioId, section));
  };
};

const accountBelongsToDifferentSheetSelector = (state, custodian, account) => {
  if (!custodian === true || !account === true) {
    return false;
  }
  // For debts we want to put items in the correct sheet irrespective of where
  // the user is adding them.
  if (account.category !== categoryType.DEBT) {
    return false;
  }

  var isAccountForDifferentSheet = false;
  const targetSheet = custodianSheetSelector(state, custodian.id);
  const isTargetLoanSheet =
    targetSheet &&
    (targetSheet.name.toLowerCase().includes("loan") || targetSheet.name.toLowerCase().includes("mortgage"));
  const isTargetCreditCardSheet = targetSheet && targetSheet.name.toLowerCase().includes("credit card");

  if (
    isTargetLoanSheet &&
    (account.accountName.toLowerCase().includes("credit card") === true ||
      account.container === "creditCard" ||
      account.container === "credit")
  ) {
    isAccountForDifferentSheet = true;
  } else if (
    (isTargetCreditCardSheet &&
      (account.accountName.toLowerCase().includes("loan") || account.accountName.toLowerCase().includes("mortgage")) ===
        true) ||
    account.container === "loan" ||
    account.container === "marginBalance"
  ) {
    isAccountForDifferentSheet = true;
  }
  return isAccountForDifferentSheet;
};

const getAppropriateTargetSheetForAccount = (portfolio, category, account) => {
  const currentPortfolio = portfolio;
  const isAccountForDifferentCategory = category !== account.category;
  const targetCategorySheets = currentPortfolio.details.sheet.filter(sheet => sheet.category === account.category);
  var targetSheet = targetCategorySheets[0];

  if (account.accountName && account.category === categoryType.DEBT) {
    if (
      account.accountName.toLowerCase().includes("credit card") ||
      account.container === "creditCard" ||
      account.container === "credit"
    ) {
      const creditCardSheet = targetCategorySheets.find(sheet => sheet.name.toLowerCase().includes("credit card"));
      if (creditCardSheet) {
        targetSheet = creditCardSheet;
      }
    } else if (
      account.accountName.toLowerCase().includes("loan") ||
      account.accountName.toLowerCase().includes("mortgage") ||
      account.container === "loan" ||
      account.container === "marginBalance"
    ) {
      const loanSheet = targetCategorySheets.find(sheet => sheet.name.toLowerCase().includes("loan"));
      if (loanSheet) {
        targetSheet = loanSheet;
      }
    } else if (isAccountForDifferentCategory) {
      const othersSheet = targetCategorySheets.find(sheet => sheet.name.toLowerCase().includes("others"));
      if (account.providerId === getAccountLinkingService(accountLinkingService.KUBERA_PORTFOLIO)) {
        const linkedPortfolioDebtsSheet = targetCategorySheets.find(
          sheet => sheet.name === defaultLinkedPortfolioDebtSheetName
        );
        targetSheet = linkedPortfolioDebtsSheet || othersSheet;
      } else if (othersSheet) {
        targetSheet = othersSheet;
      }
    }
  }
  return targetSheet;
};

const filterUniqueLinkedAccounts = (account, set) => {
  if (account?.linkedCustodianId && !set.has(account.linkedCustodianId)) {
    set.add(account.linkedCustodianId);
    return true;
  }
  return false;
};

const createLinkBulkContext = getState => ({
  custodian: { create: [], update: [] },
  sideEffectsById: new Map(),
  summaryToasts: [],
  verifiedConnectionsCountBefore: verifiedConnectionKeysSelector(getState()).size
});

// After the bulk submit resolves, run each linked custodian's post-update side effects
// (balance-mismatch refresh, investment cost / holdings polling, LEAN refresh) using the
// server-returned custodian. Custodians that landed in failed[] are skipped.
const runLinkSideEffects = (getState, result, sideEffectsById) => {
  const completedById = new Map((result.custodian.completed || []).map(item => [item.id, item]));
  for (const [custodianId, sideEffect] of sideEffectsById) {
    const apiResponseCustodian = completedById.get(custodianId);
    if (!apiResponseCustodian) continue;
    sideEffect.onCustodianUpdate(custodianSelector(getState(), custodianId), apiResponseCustodian);
  }
};

const emitVerifiedConnectionMilestones = (dispatch, getState, linkContext, result) => {
  const failedCustodianIds = new Set((result.custodian.failed || []).map(item => item.id));
  const countAfter = verifiedConnectionKeysSelector(getState(), failedCustodianIds).size;
  const firedMilestones = userPreferencesSelector(getState()).verifiedConnectionMilestonesFired || [];
  const crossedMilestones = VERIFIED_CONNECTION_MILESTONES.filter(
    milestone =>
      linkContext.verifiedConnectionsCountBefore < milestone &&
      countAfter >= milestone &&
      firedMilestones.includes(milestone) === false
  );

  if (crossedMilestones.length === 0) {
    return;
  }

  dispatch(updateUserPreferences({ verifiedConnectionMilestonesFired: [...firedMilestones, ...crossedMilestones] }));
  for (const milestone of crossedMilestones) {
    eventBus.dispatch("verifiedConnectionMilestone", [milestone]);
  }
};

// Submit a link session's accumulated custodians as one bulk call. For user-initiated flows a
// non-dismissable "Processing X of Y accounts" toast is shown on the main tab and updated in
// place from the API progress (scaled to the number of accounts); on completion it is dismissed,
// the post-update side effects run, and any deferred summary toasts are shown.
const MIN_PROGRESS_TOAST_MS = 3000;

const flushLinkBulkSync = (dispatch, getState, { portfolioId, linkContext, showProgressToast, onComplete }) => {
  const totalAccounts = linkContext.sideEffectsById.size;
  const showProgress = showProgressToast === true && totalAccounts > 0;
  let progressToastId = null;
  let lastUpdateTs = null;

  const tail = "This may take several minutes. Don't close the tab or refresh.";
  const accountLabel = count => `${count} ${count === 1 ? "account" : "accounts"}`;
  // Before the first poll returns we only know the total submitted; after that we report the
  // actual number completed so far.
  const initialMessage = `Processing ${accountLabel(totalAccounts)}. ${tail}`;
  const progressMessage = completedAccounts =>
    `Processed ${completedAccounts} of ${accountLabel(totalAccounts)}. ${tail}`;

  const renderProgressToast = message => {
    const currentToast = getState().toast?.toast;
    if (progressToastId !== null && currentToast && currentToast.id === progressToastId) {
      // Our toast is on screen — update the message in place.
      dispatch(updateToastAction(progressToastId, message));
      lastUpdateTs = Date.now();
    } else if (!currentToast) {
      // Nothing showing (first tick, or a higher-priority toast such as a SyncComponent error
      // has since cleared) — (re)show the progress toast.
      const toast = new Toast(toastType.TIP, message, -1, null, null);
      toast.dismissOnOutsideClick = false;
      toast.showDismissButton = false;
      progressToastId = toast.id;
      lastUpdateTs = Date.now();
      dispatch(showToastAction(toast));
    }
    // Otherwise another toast (e.g. a sync error) is showing — leave it; we resume on a later tick.
  };

  // Show the toast right away so the user sees it the moment they're back on the main tab.
  if (showProgress) {
    renderProgressToast(initialMessage);
  }

  dispatch(
    bulkSyncCustodianEntities({
      portfolioId,
      custodian: linkContext.custodian,
      custodiansToRefresh: [...linkContext.sideEffectsById.keys()].map(id => ({ id })),
      onProgress: !showProgress
        ? undefined
        : ({ completed, total }) => {
            if (!total) return;
            renderProgressToast(
              progressMessage(Math.min(totalAccounts, Math.round((totalAccounts * completed) / total)))
            );
          },
      onComplete: result => {
        runLinkSideEffects(getState, result, linkContext.sideEffectsById);
        emitVerifiedConnectionMilestones(dispatch, getState, linkContext, result);

        const finishToast = () => {
          // Only clear the toast if it's still ours (another toast may have superseded it).
          if (progressToastId !== null && getState().toast?.toast?.id === progressToastId) {
            dispatch(dismissToastAction());
          }
          for (const toast of linkContext.summaryToasts) {
            dispatch(showToastAction(toast));
          }
        };

        // Keep the toast visible for at least MIN_PROGRESS_TOAST_MS after its last update so a
        // fast sync doesn't flash it.
        const sinceLastUpdate = lastUpdateTs === null ? MIN_PROGRESS_TOAST_MS : Date.now() - lastUpdateTs;
        if (sinceLastUpdate < MIN_PROGRESS_TOAST_MS) {
          setTimeout(finishToast, MIN_PROGRESS_TOAST_MS - sinceLastUpdate);
        } else {
          finishToast();
        }

        onComplete?.(result);
      }
    })
  );
};

export const linkAccountsWithCustodian = (
  portfolioId,
  category,
  selectedAccounts,
  allAccounts = [],
  custodian,
  linkingService,
  selectedProviderId,
  selectedProviderName,
  isRelinkFlow,
  onAccountsLinked,
  useCustodianAsTargetCustodian,
  canUpdateCustodianName,
  indexCompareToLast = false,
  showToast = true,
  linkBulkOptions = {}
) => {
  return (dispatch, getState) => {
    const { bulkContext, showProgressToast = false } = linkBulkOptions;
    const linkContext = bulkContext || createLinkBulkContext(getState);
    const currentPortfolio = portfolioSelector(getState(), portfolioId);
    var latestReferenceCustodian = currentPortfolio.details.custodian.find(item => item.id === custodian.id);
    if (!latestReferenceCustodian === true) {
      custodian.tsModified = null;
      dispatch(insertCustodianInBulkAction(currentPortfolio.id, [custodian]));
      latestReferenceCustodian = currentPortfolio.details.custodian.find(item => item.id === custodian.id);
    }

    if (!latestReferenceCustodian.name === false) {
      custodian.name = latestReferenceCustodian.name;
    }
    if (isRelinkFlow === true) {
      const sectionId = custodian.sectionId;
      const newCustodianId = getUuid();

      dispatch(deleteAllConnectedCustodians(custodian.id));
      dispatch(insertCustodianAtEndOfSection(currentPortfolio.id, sectionId, newCustodianId));

      custodian = custodianSelector(getState(), newCustodianId);
      latestReferenceCustodian = custodian;
    }

    const otherCategory = category === categoryType.ASSET ? categoryType.DEBT : categoryType.ASSET;
    const accountIdToRelatedIdMap = {};

    const sameCategoryAccounts = selectedAccounts
      .filter(account => account.category === category)
      .sort((a, b) => b.balance - a.balance);
    const otherCategoryAccounts = selectedAccounts
      .filter(account => account.category !== category)
      .sort((a, b) => b.balance - a.balance);

    let startIndex = 0;
    if (category === categoryType.ASSET) {
      selectedAccounts = [...otherCategoryAccounts, ...sameCategoryAccounts];
      startIndex = otherCategoryAccounts.length;
    } else {
      selectedAccounts = [...sameCategoryAccounts, ...otherCategoryAccounts];
    }

    const hasSubAccounts = allAccounts && allAccounts.length > 1;
    const emptyCustodians = getEmptyCustodiansInSection(getState(), portfolioId, custodian.sectionId);

    let custodianIds = [];

    const allLinkedAccountsLinkedCustodianIdSet = new Set();
    const allLinkedAccounts = [
      ...allAccounts.filter(account => {
        if (filterUniqueLinkedAccounts(account, allLinkedAccountsLinkedCustodianIdSet)) {
          accountIdToRelatedIdMap[account.id] = account.linkedCustodianId;
          return true;
        }
        return false;
      }),
      ...selectedAccounts.filter(account => {
        if (filterUniqueLinkedAccounts(account, allLinkedAccountsLinkedCustodianIdSet)) {
          accountIdToRelatedIdMap[account.id] = account.linkedCustodianId;
          return true;
        }
        return false;
      })
    ];
    // Preserve which account's custodian the completion callback used to receive (the old per-
    // account flow fired onAccountsLinked for the first or, when indexCompareToLast, the last).
    const callbackIndex = !indexCompareToLast ? 0 : selectedAccounts.length - 1;
    let callbackCustodianId = latestReferenceCustodian.id;
    const relocatedSheets = [];
    for (const [index, account] of selectedAccounts.entries()) {
      if (account.relatedAccountId) {
        account.relatedId = accountIdToRelatedIdMap[account.relatedAccountId];
      }
      const { custodianId, relocatedSheet } = dispatch(
        linkAccountWithCustodian(
          currentPortfolio,
          account,
          index === startIndex && useCustodianAsTargetCustodian === true
            ? custodian
            : emptyCustodians[index - startIndex - 1],
          custodian,
          linkingService,
          selectedProviderId,
          selectedProviderName,
          category,
          null,
          useCustodianAsTargetCustodian,
          canUpdateCustodianName,
          linkContext
        )
      );
      if (index === callbackIndex) {
        callbackCustodianId = custodianId;
      }
      relocatedSheets[index] = relocatedSheet;
      accountIdToRelatedIdMap[account.id] = custodianId;
      custodianIds.push(custodianId);
    }
    dispatch(updateDashboardAction(custodianIds));
    allLinkedAccounts.forEach(account => {
      const relatedId = account.category === categoryType.ASSET && accountIdToRelatedIdMap[account.relatedAccountId];

      if (relatedId) {
        const relatedCustodian = custodianSelector(getState(), account.linkedCustodianId);
        if (relatedCustodian) {
          dispatch(updateCustodianInBulkAction(currentPortfolio.id, [{ ...relatedCustodian, relatedId }]));
          linkContext.custodian.update.push({
            id: account.linkedCustodianId,
            sectionId: relatedCustodian.sectionId,
            relatedId
          });
        }
      }
    });

    var toastMessage = null;
    var toastDuration = -1;
    if (showToast) {
      if (selectedAccounts.length === 1 && linkingService !== accountLinkingService.ZABO) {
        const account = selectedAccounts[0];
        const accountName = account.accountName;

        if (selectedAccounts[0].category !== category) {
          toastMessage = `${
            linkingService === accountLinkingService.ZERION ? accountName : otherCategoryAccounts[0].accountName
          } got added in ${otherCategory}s`;
        } else if (relocatedSheets[0]) {
          toastMessage = `${account.accountName} got added in the ${relocatedSheets[0].name} sheet`;
        } else if (hasSubAccounts === false && !latestReferenceCustodian.linkProviderId === true) {
          toastMessage = `${selectedProviderName} - Connected`;
          toastDuration = 5000;
        }
      } else if (otherCategoryAccounts.length === 1) {
        const account = otherCategoryAccounts[0];
        const accountName = account.accountName;

        if (account.providerId === getAccountLinkingService(accountLinkingService.KUBERA_PORTFOLIO)) {
          if (account.balance > 0) {
            toastMessage = `${
              linkingService === accountLinkingService.ZERION ? accountName : otherCategoryAccounts[0].accountName
            } got added in ${otherCategory}s`;
          }
        } else {
          toastMessage = `${
            linkingService === accountLinkingService.ZERION ? accountName : otherCategoryAccounts[0].accountName
          } got added in ${otherCategory}s`;
        }
      } else if (otherCategoryAccounts.length > 1) {
        toastMessage = `Some accounts got added in ${otherCategory}s`;
      }
    }

    if (linkingService !== accountLinkingService.LEAN) {
      dispatch(unmarkCustodianAsLinking(currentPortfolio.id, latestReferenceCustodian));
    }

    if (!toastMessage === false) {
      const toast = new Toast(toastType.TIP, toastMessage, toastDuration, null, null);
      toast.dismissOnOutsideClick = false;
      toast.dismissOnNavigationToCategory = otherCategory;
      // For progress-toast flows the summary is deferred so it shows after the progress toast;
      // otherwise it shows immediately as before.
      if (showProgressToast === true) {
        linkContext.summaryToasts.push(toast);
      } else {
        dispatch(showToastAction(toast));
      }
    }

    // When an external bulkContext is injected (e.g. reconnectAccounts) the injector owns the
    // single flush across all link calls; otherwise submit this session's custodians as one bulk.
    if (!bulkContext) {
      flushLinkBulkSync(dispatch, getState, {
        portfolioId: currentPortfolio.id,
        linkContext,
        showProgressToast,
        onComplete: () => {
          if (onAccountsLinked) {
            onAccountsLinked(custodianSelector(getState(), callbackCustodianId));
          }
        }
      });
    }
  };
};

export const sanitizeLinkAccountNumber = accountNumber => {
  if (!accountNumber === true) {
    return accountNumber;
  }
  return accountNumber.slice(-4);
};

const linkAccountWithCustodian = (
  portfolio,
  account,
  targetCustodian,
  referenceCustodian,
  linkType,
  selectedProviderId,
  selectedProviderName,
  category = null,
  onCompletion,
  useCustodianAsTargetCustodian,
  canUpdateCustodianName,
  bulkContext
) => {
  return (dispatch, getState) => {
    const currentPortfolio = portfolio;
    var isAccountForDifferentSheet = accountBelongsToDifferentSheetSelector(getState(), referenceCustodian, account);
    const isAccountForDifferentCategory = category !== account.category;

    if (targetCustodian && referenceCustodian && targetCustodian.id === referenceCustodian.id) {
      isAccountForDifferentSheet = false;
    }

    if (useCustodianAsTargetCustodian === false) {
      targetCustodian = null;
    } else if (isAccountForDifferentCategory) {
      targetCustodian = null;
    } else if (isAccountForDifferentSheet) {
      targetCustodian = null;
    } else {
      var latestReferenceCustodian = currentPortfolio.details.custodian.find(
        custodian => custodian.id === referenceCustodian.id
      );
      if (!latestReferenceCustodian === true) {
        referenceCustodian.tsModified = null;
        latestReferenceCustodian = referenceCustodian;
        dispatch(insertCustodianInBulkAction(currentPortfolio.id, [latestReferenceCustodian]));
      } else if (
        (!latestReferenceCustodian.tsModified === true && !latestReferenceCustodian.isLocallyEdited === true) ||
        latestReferenceCustodian.linkType === 0
      ) {
        targetCustodian = latestReferenceCustodian;
      }
    }

    const isNewCustodian = !targetCustodian === true;
    var custodianId = isNewCustodian === true ? getUuid() : targetCustodian.id;
    var relocatedSheet = null;

    sortPortfolio(currentPortfolio);

    if (isAccountForDifferentCategory || isAccountForDifferentSheet) {
      const targetSheet = getAppropriateTargetSheetForAccount(currentPortfolio, category, account);
      const targetSheetSections = currentPortfolio.details.section.filter(
        section => section.sheetId === targetSheet.id
      );
      if (targetSheetSections.length === 0) {
        return {};
      }
      const sourceSheet = custodianSheetSelector(getState(), referenceCustodian.id);
      if (targetSheet.id !== sourceSheet?.id) {
        relocatedSheet = targetSheet;
      }
      const targetSectionCustodians = currentPortfolio.details.custodian.filter(
        custodian => custodian.sectionId === targetSheetSections[0].id
      );
      const targetEmptyCustodians = targetSectionCustodians.filter(
        custodian => !custodian.tsModified === true && !custodian.isLocallyEdited === true
      );

      if (targetEmptyCustodians.length > 0) {
        custodianId = targetEmptyCustodians[0].id;
      } else {
        dispatch(insertCustodianAtEndOfSection(currentPortfolio.id, targetSheetSections[0].id, custodianId));
      }
    } else if (isNewCustodian) {
      const custodians = currentPortfolio.details.custodian.filter(
        temp => temp.sectionId === referenceCustodian.sectionId
      );
      const custodiansWithSameAccount = custodians.filter(
        item =>
          item.linkProviderAccountId === account.providerAccountId &&
          item.sortKey.localeCompare(referenceCustodian.sortKey) === 1
      );
      const insertAfterCustodian =
        custodiansWithSameAccount.length > 0
          ? custodiansWithSameAccount[custodiansWithSameAccount.length - 1]
          : referenceCustodian;
      const custodianIndex = custodians.findIndex(custodian => custodian.id === insertAfterCustodian.id);
      const sortKeyBefore = custodians[custodianIndex].sortKey;
      const sortKeyAfter =
        custodianIndex === custodians.length - 1 || custodians.length === 1
          ? null
          : custodians[custodianIndex + 1].sortKey;
      const sortKey = getSortKeyBetween(sortKeyBefore, sortKeyAfter);
      dispatch(insertCustodian(currentPortfolio.id, referenceCustodian.sectionId, custodianId, sortKey));
    }
    var custodianName;
    if (
      linkType === accountLinkingService.ZERION ||
      linkType === accountLinkingService.IN_HOUSE_CRYPTO_API ||
      linkType === accountLinkingService.IN_HOUSE_CRYPTO_OAUTH ||
      linkType === accountLinkingService.IN_HOUSE_OAUTH ||
      linkType === accountLinkingService.KUBERA_PORTFOLIO
    ) {
      custodianName = account.accountName;
    } else {
      custodianName = referenceCustodian.name;
      if (!custodianName === false && canUpdateCustodianName === true) {
        custodianName = custodianName.replace(getLinkCustodianPlaceholderName(selectedProviderName, category), "");
      }
      if (canUpdateCustodianName === true) {
        custodianName = selectedProviderName;

        if (!account.accountName === false) {
          custodianName = `${custodianName} - ${account.accountName}`;
        }
        if (!account.accountNumber === false) {
          custodianName = `${custodianName} - ${sanitizeLinkAccountNumber(account.accountNumber)}`;
        }
      }
    }

    const portfolioTicker = getTickerUsingShortName(getState(), currentPortfolio.currency);
    const accountTickerFromId = getTickerUsingId(getState(), account.tickerId);
    const accountCurrency =
      !account.currency === false
        ? account.currency
        : accountTickerFromId.shortName !== UNKNOWN_TICKER_SHORT_NAME
        ? accountTickerFromId.shortName
        : defaultCustodianTickerSelector(getState(), currentPortfolio.currency).shortName;
    const accountTicker = getTickerUsingShortName(getState(), accountCurrency);
    const accountTickerId = accountTicker.shortName === UNKNOWN_TICKER_SHORT_NAME ? account.tickerId : accountTicker.id;
    const exchangeRate = getExchangeRate(getState(), accountCurrency, currentPortfolio.currency);
    const valueExchangeRate = getExchangeRateDetails(portfolioTicker.id, exchangeRate);

    const onCustodianUpdate = (updatedCustodian, apiResponseCustodian) => {
      if (!onCompletion === false) {
        onCompletion(custodianSelector(getState(), custodianId));
      }

      // If post update the api returned a different custodian value refresh to get the right value
      // This was happening with some MX accounts
      if (account.balance !== apiResponseCustodian?.value) {
        dispatch(refreshCustodian(updatedCustodian.id));
      }

      // Fetch custodian info to get cost as that might not be
      // available immediately post linking for investments
      if (account.container === "investment") {
        setTimeout(() => {
          var retryCount = 0;
          const fetchCustodianCost = () => {
            const latestCustodian = currentPortfolio.details.custodian.find(custodian => custodian.id === custodianId);
            if (!latestCustodian === true || !latestCustodian.cost === false) {
              return;
            }

            ApiClient.getCustodian(getUuid(), custodianId)
              .then(apiData => {
                const fetchedCustodian = apiData.payload;
                retryCount++;

                if (!fetchedCustodian.cost === true) {
                  if (retryCount === 3) {
                    return;
                  }

                  setTimeout(() => {
                    fetchCustodianCost();
                  }, 30 * 1000);
                  return;
                }

                const currentPortfolio = currentPortfolioSelector(getState());
                const latestCustodian = currentPortfolio.details.custodian.find(
                  custodian => custodian.id === custodianId
                );

                if (!latestCustodian === false && !latestCustodian.cost === true) {
                  latestCustodian.cost = fetchedCustodian.cost;
                  dispatch(updateCustodianInBulkAction(currentPortfolio.id, [latestCustodian]));
                  dispatch(updateDashboardAction([latestCustodian.id]));

                  if (latestCustodian.holdingsCount > 0) {
                    dispatch(refreshCustodian(latestCustodian.id));
                  }
                }
              })
              .catch(apiError => {
                captureError(getState(), apiError);
              });
          };

          fetchCustodianCost();
        }, 15 * 1000);
      }

      // Fetch custodian holdings if applicable and not yet fetched
      if (
        linkType === accountLinkingService.ZABO ||
        (account.container === "investment" &&
          linkType !== accountLinkingService.SALTEDGE &&
          linkType !== accountLinkingService.SALTEDGE_EU)
      ) {
        setTimeout(() => {
          var retryCount = 0;
          const fetchCustodianHoldings = () => {
            const latestCustodian = currentPortfolio.details.custodian.find(custodian => custodian.id === custodianId);
            if (!latestCustodian === true || latestCustodian.holdingsCount > 0) {
              return;
            }

            ApiClient.getCustodian(getUuid(), custodianId)
              .then(apiData => {
                const fetchedCustodian = apiData.payload;
                retryCount++;

                if (fetchedCustodian.holdingsCount === 0) {
                  if (retryCount === 5) {
                    return;
                  }

                  setTimeout(() => {
                    fetchCustodianHoldings();
                  }, 30 * 1000);
                  return;
                }

                const currentPortfolio = currentPortfolioSelector(getState());
                const latestCustodian = currentPortfolio.details.custodian.find(
                  custodian => custodian.id === custodianId
                );

                if (latestCustodian?.holdingsCount === 0) {
                  latestCustodian.holdingsCount = fetchedCustodian.holdingsCount;
                  dispatch(updateCustodianInBulkAction(currentPortfolio.id, [latestCustodian]));
                  dispatch(updateDashboardAction([latestCustodian.id]));

                  dispatch(refreshCustodian(latestCustodian.id));
                }
              })
              .catch(apiError => {
                captureError(getState(), apiError);
              });
          };

          fetchCustodianHoldings();
        }, 15 * 1000);
      }

      if (linkType === accountLinkingService.LEAN) {
        dispatch(refreshCustodian(custodianId));
      }
    };
    const isFirstEdit = !targetCustodian === true || !targetCustodian.tsModified === true;
    const linkPayload = {
      name: custodianName,
      value: account.balance,
      valueTickerId: accountTickerId,
      sourceValue: account.assetSourceValue !== undefined ? account.assetSourceValue : account.balance,
      sourceValueTickerId:
        account.assetSourceValueTickerId !== undefined ? account.assetSourceValueTickerId : accountTickerId,
      valueExchangeRate: valueExchangeRate,
      tsLastUpdateCheck: Math.floor(new Date().getTime() / 1000),
      linkType: linkType,
      linkProviderAccountId: account.providerAccountId,
      linkAccountId: account.id,
      linkAccountContainer: account.container,
      linkAutoRefresh: account.autoRefresh,
      linkProviderId: account.providerId ? account.providerId : selectedProviderId,
      linkProviderName: account.providerName ? account.providerName : selectedProviderName,
      linkAccountMask:
        linkType === accountLinkingService.ZERION ||
        linkType === accountLinkingService.IN_HOUSE_CRYPTO_API ||
        linkType === accountLinkingService.IN_HOUSE_CRYPTO_OAUTH ||
        linkType === accountLinkingService.IN_HOUSE_OAUTH
          ? account.accountMask
          : account.accountNumber,
      linkAccountName: account.accountName,
      isLinking: linkType === accountLinkingService.LEAN,
      linkingAccountsData: null,
      type: account.type || 0,
      ...(account.relatedId ? { relatedId: account.relatedId } : {}),
      ...(account.subType ? { subType: account.subType } : {}),
      ...(account.assetDescription ? { description: account.assetDescription } : {})
    };

    // Replicate updateCustodian's optimistic, synchronous writes (the bulk action owns only the
    // API submit + post-completion side effects), then accumulate the custodian into the bulk
    // context instead of dispatching a per-account updateCustodian.
    const latestStateCustodian = custodianSelector(getState(), custodianId) || {};
    const isLocalRow =
      isFirstEdit === true &&
      !latestStateCustodian.tsModified === true &&
      !latestStateCustodian.isLocallyEdited === true;

    dispatch(setSectionUpdated(latestStateCustodian.sectionId));

    if (!("past" in linkPayload) && Number.isInteger(linkPayload.linkType)) {
      linkPayload.past = 0;
    }
    if (isReadOnlyWlClient(getState())) {
      linkPayload.aum = 1;
    }
    const mergedCustodian = {
      ...latestStateCustodian,
      ...linkPayload,
      linkContainer: linkPayload.linkAccountContainer
    };
    if (mergedCustodian.name === null) {
      mergedCustodian.name = "";
    }
    if (!mergedCustodian.tsModified === false) {
      mergedCustodian.tsModified = Math.floor(new Date().getTime() / 1000);
    }
    mergedCustodian.isLocallyEdited = true;
    dispatch(updateCustodianInBulkAction(currentPortfolio.id, [mergedCustodian]));

    if (linkPayload.type != null && !mergedCustodian.parentId && mergedCustodian.holdingsCount > 0) {
      const children = currentPortfolio.details.custodian
        .filter(item => item.parentId === custodianId)
        .map(item => ({ ...item, type: linkPayload.type }));
      if (children.length > 0) {
        dispatch(updateCustodianInBulkAction(currentPortfolio.id, children));
      }
    }

    flashCustodian(custodianId);

    if (bulkContext) {
      if (isLocalRow === true) {
        bulkContext.custodian.create.push(mergedCustodian);
      } else {
        bulkContext.custodian.update.push({ id: custodianId, sectionId: mergedCustodian.sectionId, ...linkPayload });
      }
      bulkContext.sideEffectsById.set(custodianId, { onCustodianUpdate });
    }
    return { custodianId, relocatedSheet };
  };
};

export const reconnectAccounts = (
  portfolioId,
  category,
  custodian,
  linkingService,
  selectedProviderId,
  selectedProviderName,
  existingCustodians,
  incomingAccounts,
  onCompletetion = dispatch => {
    dispatch(getConnectivityCenterData());
  },
  showProgressToast = false
) => {
  return (dispatch, getState) => {
    // All reconnected links accumulate into one shared bulk context and submit as a single
    // bulk call; unlinks (deletes) remain separate and we flush once they settle.
    const bulkContext = createLinkBulkContext(getState);
    let bulkPortfolioId = portfolioId;

    const accumulateLink = (linkPortfolioId, accounts, applicableCustodian, applicableCategory) => {
      bulkPortfolioId = linkPortfolioId;
      dispatch(
        linkAccountsWithCustodian(
          linkPortfolioId,
          applicableCategory,
          accounts,
          incomingAccounts,
          applicableCustodian || custodian,
          linkingService,
          selectedProviderId,
          selectedProviderName,
          false,
          null,
          !applicableCustodian === false,
          !applicableCustodian === true,
          true,
          true,
          { bulkContext, showProgressToast }
        )
      );

      if (!applicableCustodian === false) {
        dispatch(deleteChildrenForCustodian(applicableCustodian.id));
      }
    };
    const unlinkAccount = custodianId => {
      // The delay is needed to ensure that if multiple accounts are being unlinked in
      // quick succession like when the user selects multiple accounts the dashboard
      // gets time to update itself appropriately
      return new Promise(resolve => {
        setTimeout(() => {
          dispatch(
            unlinkAccountWithCustodian(custodianId, true, () => {
              resolve(true);
            })
          );
        }, 10);
      });
    };

    var extraAccounts = [];
    const unlinkPromises = [];
    incomingAccounts.forEach((account, index) => {
      if (index < existingCustodians.length) {
        if (account === null) {
          unlinkPromises.push(unlinkAccount(existingCustodians[index].id));
        } else {
          const custodianSheet = custodianSheetSelector(getState(), existingCustodians[index].id) || {
            category: null
          };
          const custodianPortfolio = custodianPortfolioSelector(getState(), existingCustodians[index].id);
          // If user has matched a custodian to an account, ignore the account's category as
          // it can be different from the custodian's category. E.g. when provider gives bad data and
          // account list gives a credit card account with asset category.
          account.category = custodianSheet.category;
          accumulateLink(custodianPortfolio.id, [account], existingCustodians[index], custodianSheet.category);
        }
      } else if (account !== null) {
        extraAccounts.push(account);
      }
    });
    if (extraAccounts.length > 0) {
      accumulateLink(portfolioId, extraAccounts, null, category);
    }

    const flush = () => {
      flushLinkBulkSync(dispatch, getState, {
        portfolioId: bulkPortfolioId,
        linkContext: bulkContext,
        showProgressToast,
        onComplete: () => onCompletetion(dispatch)
      });
    };

    if (unlinkPromises.length > 0) {
      Promise.all(unlinkPromises)
        .then(flush)
        .catch(err => {
          captureError(getState(), err);
          console.log("err", err);
          flush();
        });
    } else {
      flush();
    }
  };
};

export const editAccountsInExistingConnection = (
  portfolioId,
  category,
  custodian,
  linkingService,
  selectedProviderId,
  selectedProviderName,
  selectedAccounts,
  useCustodianAsTargetCustodian = false,
  showProgressToast = false
) => {
  return (dispatch, getState) => {
    const linkAccounts = accounts => {
      dispatch(
        linkAccountsWithCustodian(
          portfolioId,
          category,
          accounts,
          selectedAccounts,
          custodian,
          linkingService,
          selectedProviderId,
          selectedProviderName,
          false,
          null,
          useCustodianAsTargetCustodian,
          true,
          false,
          true,
          { showProgressToast }
        )
      );
    };
    const archiveCustodians = custodians => {
      for (const custodian of custodians) {
        setTimeout(() => {
          dispatch(archiveCustodian(custodian.sectionId, custodian.id, false, undefined, false));
        }, 10);
      }
    };

    const existingCustodians = getCustodiansLinkedWithProviderAccountId(
      getState(),
      custodian.linkProviderAccountId,
      false
    );
    const newAccounts = selectedAccounts.filter(item => item.linkedCustodianId === "");
    const custodiansToArchive = existingCustodians.filter(
      custodian => !selectedAccounts.find(account => account.linkedCustodianId === custodian.id) === true
    );

    if (newAccounts.length > 0) {
      linkAccounts(newAccounts);
    }
    // We don't want to archive accounts missing in selectedAccounts for Plaid as it
    // does not provide the list of currently selected accounts during reconnection
    if (linkingService !== accountLinkingService.PLAID && custodiansToArchive.length > 0) {
      archiveCustodians(custodiansToArchive);
    }

    if (newAccounts.length === 0 && custodiansToArchive.length === 0) {
      dispatch(unmarkCustodianAsLinking(portfolioId, custodian, selectedProviderName, category, false));
    } else {
      for (const existingCustodian of existingCustodians) {
        dispatch(unmarkCustodianAsLinking(portfolioId, existingCustodian, selectedProviderName, category, true));
      }
    }
  };
};

export const unlinkAllConnectedCustodians = (custodianId, options = {}) => {
  return (dispatch, getState) => {
    const { isRemoveFlow = false } = options;
    const currentPortfolio = currentPortfolioSelector(getState());
    const custodian = custodianSelector(getState(), custodianId);

    if (custodian?.linkType === accountLinkingService.KUBERA_PORTFOLIO) {
      dispatch(unlinkAndManuallyTrackAllConnectedCustodians(custodianId));
      return;
    }

    const { custodians, custodianPortfolioMap } = custodiansLinkedToSameAccountSelector(
      getState(),
      custodianId,
      false,
      false,
      isRemoveFlow
    );

    if (custodians.length === 0) {
      return;
    }
    const unlinkPromises = custodians.map(custodian => {
      return new Promise((resolve, _) => {
        dispatch(deleteCustodianAction(custodianPortfolioMap[custodian.id], custodian.id));
        dispatch(updateDashboardAction([custodian.id]));

        if (!custodian.parentId === true) {
          const request = idempotentId => ApiClient.archiveCustodian(idempotentId, custodian.sectionId, [custodian.id]);
          const syncItem = new SyncItem(SyncItemType.DELETE, custodian.id, request, 0, true, apiData => {
            resolve(true);
          });
          dispatch(enqueueItem(syncItem));
        } else {
          resolve(true);
        }
      });
    });
    Promise.all(unlinkPromises).then(() => {
      dispatch(getConnectivityCenterData());
      dispatch(fetchNetWorthDataForPortfolio(currentPortfolio.id, true));
    });
  };
};

export const unlinkAndManuallyTrackAConnectedCustodian = (custodianId, updateConnectivityCenterData = false) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const custodian = custodianSelector(getState(), custodianId);
    const isAHoldingAndShownOutside = custodian.parentId && !custodian.hidden;

    dispatch(
      markCustodianAsLinking(
        currentPortfolio.id,
        custodian,
        true,
        custodian.linkProviderName,
        categoryType.ASSET,
        true,
        false
      )
    );
    if (!custodian.tsModified === false) {
      custodian.tsModified = Math.floor(new Date().getTime() / 1000);
    }

    const unlinkAccountParams = {
      ...(custodian.linkType === accountLinkingService.ZILLOW
        ? { value: 0, valueTickerId: null, ownership: 100, cost: null, costExchangeRate: null, costTickerId: null }
        : {}),
      linkType: 0,
      linkProviderAccountId: null,
      linkAccountId: null,
      linkAccountContainer: null,
      past: 0
    };

    if (custodian.parentId) {
      const parentCustodian = custodianSelector(getState(), custodian.parentId);
      const updatedParentCustodian = {
        ...parentCustodian,
        linkType: 0
      };
      dispatch(updateCustodianInBulkAction(currentPortfolio.id, [updatedParentCustodian]));
    }

    ApiClient.trackManually(
      getUuid(),
      custodian.sectionId,
      isAHoldingAndShownOutside ? custodian.parentId : custodian.id,
      unlinkAccountParams
    ).then(apiData => {
      const { custodians } = apiData.payload;
      setTimeout(() => {
        dispatch(unmarkCustodianAsLinking(currentPortfolio.id, custodian, null, null, false, false));
        dispatch(updateCustodianInBulkAction(currentPortfolio.id, custodians));
        const entitiesToUpdate = custodians.map(custodian => custodian.id);
        dispatch(updateDashboardAction([custodianId, ...entitiesToUpdate]));
      }, 1000);
    });
  };
};

export const unlinkAndManuallyTrackAllConnectedCustodians = (custodianId, updateConnectivityCenterData = false) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const custodian = custodianSelector(getState(), custodianId);
    var { custodians, custodianPortfolioMap, portfolioCustodiansMap } = custodiansLinkedToSameAccountSelector(
      getState(),
      custodianId,
      false,
      true
    );
    if (custodian.linkType === accountLinkingService.KUBERA_PORTFOLIO) {
      custodians = portfolioCustodiansMap[currentPortfolio.id];
    }
    if (custodians.length === 0) {
      return;
    }
    dispatch(
      markCustodianAsLinking(
        currentPortfolio.id,
        custodian,
        false,
        custodian.linkProviderName,
        categoryType.ASSET,
        true,
        false
      )
    );
    const unlinkAccountParams = {
      ...(custodian.linkType === accountLinkingService.ZILLOW
        ? { value: 0, valueTickerId: null, ownership: 100, cost: null, costExchangeRate: null, costTickerId: null }
        : {}),
      linkType: 0,
      linkProviderAccountId: null,
      linkAccountId: null,
      linkAccountContainer: null,
      parentId: null
    };
    const custodianIdsCollapsed = new Set();
    const unlinkPromises = custodians.map(custodian => {
      if (custodian.parentId) {
        if (custodianIdsCollapsed.has(custodian.parentId)) return Promise.resolve();
        return new Promise((resolve, _) => {
          dispatch(
            collapseHoldingsForCustodian(custodian.id, () => {
              custodianIdsCollapsed.add(custodian.parentId);
              const parentCustodian = custodianSelector(getState(), custodian.parentId);
              dispatch(
                markCustodianAsLinking(
                  currentPortfolio.id,
                  parentCustodian,
                  false,
                  parentCustodian.linkProviderName,
                  categoryType.ASSET,
                  true
                )
              );
              dispatch(
                updateCustodian(false, custodian.parentId, unlinkAccountParams, true, () => {
                  resolve(true);
                  dispatch(
                    unmarkCustodianAsLinking(
                      custodianPortfolioMap[custodian.id],
                      parentCustodian,
                      null,
                      null,
                      false,
                      false
                    )
                  );
                })
              );
            })
          );
        });
      } else {
        return new Promise((resolve, _) => {
          dispatch(
            updateCustodian(false, custodian.id, unlinkAccountParams, true, () => {
              resolve(true);
              dispatch(
                unmarkCustodianAsLinking(custodianPortfolioMap[custodian.id], custodian, null, null, false, false)
              );
            })
          );
        });
      }
    });
    Promise.all(unlinkPromises).then(() => {
      if (updateConnectivityCenterData) {
        setTimeout(() => {
          dispatch(getConnectivityCenterData());
        }, 0);
      }
      // fetch latest custodians response and calculate recap data after unlinking a portfolio
      if (custodian.linkType === accountLinkingService.KUBERA_PORTFOLIO) {
        dispatch(fetchNetWorthDataForPortfolio(currentPortfolio.id, true));
      }
    });
  };
};

const getDisconnectedDescriptionStr = custodian => {
  if (!custodian) return "";

  if (/disconnected/i.test(custodian.description)) return custodian.description;

  return custodian.description ? `${custodian.description} (disconnected)` : "(disconnected)";
};

// DEV/TEST FIXTURE (K-4825) — plants a local "disconnected" duplicate custodian that is NOT synced
// to the server but IS written into the persisted portfolios localforage blob, so it survives a
// reload and behaves exactly like the reported phantom row. Use it to verify that the removal logic
// clears the row everywhere (Redux + localforage) under the right conditions.
//
// Trigger from the browser console:
//   window.addDummyDisconnectedCustodian()                 // clones the first real row in the current portfolio
//   window.addDummyDisconnectedCustodian("<custodianId>")  // clones a specific row (e.g. the connected SIPP IRA)
export const addDummyDisconnectedCustodian = (sourceCustodianId = null) => {
  return async (dispatch, getState) => {
    if (process.env.REACT_APP_ENV !== "local") {
      return null;
    }
    const currentPortfolio = currentPortfolioSelector(getState());
    if (!currentPortfolio) {
      console.warn("[K-4825 fixture] No current portfolio to attach the dummy to");
      return null;
    }

    // Clone a real custodian so the duplicate looks realistic; fall back to the first populated row.
    const source =
      (sourceCustodianId && custodianSelector(getState(), sourceCustodianId)) ||
      currentPortfolio.details.custodian.find(
        custodian => !custodian.autoAddedForEmptySections && !custodian.name === false && !custodian.value === false
      );

    if (!source) {
      console.warn("[K-4825 fixture] No populated source custodian found to clone");
      return null;
    }

    const dummy = {
      ...source,
      id: getUuid(),
      parentId: null,
      relatedId: null,
      // Disconnected => behaves like a manual row, mirroring a lapsed/reconnected account.
      linkType: 0,
      linkProviderAccountId: null,
      linkAccountId: null,
      linkAccountContainer: null,
      past: 1,
      // Visible in the grid (grid only hides hidden === 1), yet archived — the reported symptom.
      hidden: 0,
      // The stuck flag that makes the fetch-merge preserve this row against the server
      // (filterCondition at PortfolioActions.js ~1484). This is the K-4825 root cause.
      isLocallyEdited: true,
      isManual: true,
      // Archived timestamp set => the merge's clear block (~1504-1516) skips removal because of the
      // `!custodian.tsArchive === true` guard, so the row gets re-injected on every fetch. This is
      // exactly the escape that lets the "archived asset" keep coming back. Remove/relax this guard
      // (or clear isLocallyEdited) to test the correct removal conditions.
      tsArchive: Math.floor(Date.now() / 1000),
      description: getDisconnectedDescriptionStr(source),
      // Sort just below the source so the dup renders right under the real row.
      sortKey: `${source.sortKey || "1"}9999`
    };

    // Local-only insert: NO SyncItem is enqueued, so the dummy is never sent to the server.
    dispatch(insertCustodianInBulkAction(currentPortfolio.id, [dummy]));
    dispatch(updateDashboardAction([dummy.id]));

    // Persist into the same localforage blob the app rehydrates from on load, so the phantom
    // survives a reload exactly like the reported bug.
    try {
      const portfoliosArray = portfoliosSelector(getState());
      if (portfoliosArray) {
        await localforage.setItem(
          getPortfoliosDataStorageKey(getPortfoliosStorePortfolioSessionUserId()),
          portfoliosArray
        );
        console.log("[K-4825 fixture] Disconnected dummy persisted to localforage:", dummy.id, dummy.name);
      }
    } catch (e) {
      console.warn("[K-4825 fixture] localforage persist failed", e);
    }

    return dummy;
  };
};

export const unlinkAccountWithCustodian = (custodianId, updateDashboard = true, onSuccess = () => null) => {
  return (dispatch, getState) => {
    const custodian = custodianSelector(getState(), custodianId);
    const currentPortfolio = custodianPortfolioSelector(getState(), custodianId);
    if (custodian.isLinking === true) {
      dispatch(unmarkCustodianAsLinking(currentPortfolio.id, custodian));
    }

    if (!custodian.tsModified === true) {
      return;
    }

    var custodianToUpdate = custodian;
    var custodiansWithSameParentId = [];
    let unlinkConditionalParams = {};

    if (custodian.linkType === accountLinkingService.ZILLOW) {
      unlinkConditionalParams = {
        subType: tickerSubTypes.HOME
      };
    }

    if (custodian.linkType === accountLinkingService.CARS) {
      unlinkConditionalParams = {
        subType: tickerSubTypes.CARS
      };
    }

    if (custodian.linkType === accountLinkingService.DOMAINS) {
      unlinkConditionalParams = {
        subType: tickerSubTypes.DOMAINS
      };
    }

    if (
      ![
        accountLinkingService.ZILLOW,
        accountLinkingService.CARS,
        accountLinkingService.DOMAINS,
        accountLinkingService.AI_APPRAISER
      ].includes(custodian.linkType)
    ) {
      unlinkConditionalParams["description"] = getDisconnectedDescriptionStr(custodian);
      unlinkConditionalParams["past"] = 1;
    }

    const unlinkAccountParams = {
      ...unlinkConditionalParams,
      linkType: 0,
      linkProviderAccountId: null,
      linkAccountId: null,
      linkAccountContainer: null,
      parentId: null
    };

    if (!custodian.parentId === false) {
      custodianToUpdate = custodianSelector(getState(), custodian.parentId);
      custodiansWithSameParentId = custodiansWithSameParentIdSelector(
        getState(),
        currentPortfolio.id,
        custodian.parentId
      );
      const custodiansToBulkUpdate = [];

      for (const item of custodiansWithSameParentId) {
        const unlinkedItem = { ...item, ...unlinkAccountParams };
        custodiansToBulkUpdate.push(unlinkedItem);
      }
      dispatch(updateCustodianInBulkAction(currentPortfolio.id, custodiansToBulkUpdate));
    }

    if (!custodianToUpdate) {
      return;
    }

    dispatch(updateCustodian(false, custodianToUpdate.id, unlinkAccountParams, false, onSuccess));

    if (updateDashboard === true) {
      dispatch(updateDashboardAction([custodianToUpdate.id, ...custodiansWithSameParentId.map(item => item.id)]));
    }
  };
};

export const uploadDocument = (
  file,
  fileThumbnail,
  { custodianId = null, retryDocument = null, folderId = null, portfolioId = null } = {}
) => {
  return (dispatch, getState) => {
    const currentPortfolio = portfolioId
      ? portfolioSelector(getState(), portfolioId)
      : currentPortfolioSelector(getState());
    var fileDocument = null;

    if (retryDocument === null) {
      fileDocument = {
        id: getUuid(),
        portfolioId: currentPortfolio.id,
        name: file.name,
        size: file.size,
        fileType: file.type,
        tsModified: file.lastModified,
        isUploading: true,
        uploadProgress: 0,
        custodianId: custodianId,
        folderId: folderId,
        uploadError: null,
        file: file
      };

      dispatch(addPendingDocumentUploadAction(fileDocument));

      // Show thumbnail
      if (fileThumbnail) {
        var reader = new FileReader();
        reader.onload = function(e) {
          fileDocument.thumbnail = e.target.result;
          dispatch(updatePendingDocumentUploadAction(fileDocument));
        };
        reader.readAsDataURL(fileThumbnail);
      }
    } else {
      fileDocument = retryDocument;
      fileDocument.uploadError = null;
      fileDocument.isUploading = true;
      fileDocument.uploadProgress = 0;

      dispatch(updatePendingDocumentUploadAction(fileDocument));
    }

    // Make API call
    const onProgress = progressEvent => {
      const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);

      if (progress === 100 && fileDocument.isUploading !== true) {
        fileDocument.uploadProgress = 100;
        dispatch(updatePendingDocumentUploadAction(fileDocument));
      }
    };

    setTimeout(() => {
      fileDocument.uploadProgress = 60;
      dispatch(updatePendingDocumentUploadAction(fileDocument));

      setTimeout(() => {
        if (fileDocument.isUploading === true && fileDocument.uploadError === null) {
          fileDocument.uploadProgress = 80;
          dispatch(updatePendingDocumentUploadAction(fileDocument));
        }
      }, 1000);
    }, 100);

    var request = null;
    if (file.type === "folder") {
      request = ApiClient.uploadPortfolioFolder(getUuid(), currentPortfolio.id, file, null, null, onProgress);
    } else if (folderId) {
      request = ApiClient.uploadPortfolioDocumentToFolder(
        getUuid(),
        currentPortfolio.id,
        folderId,
        file,
        fileThumbnail,
        fileDocument.id,
        onProgress
      );
    } else if (custodianId === null) {
      request = ApiClient.uploadPortfolioDocument(
        getUuid(),
        currentPortfolio.id,
        file,
        fileThumbnail,
        fileDocument.id,
        onProgress
      );
    } else {
      request = ApiClient.uploadCustodianDocument(
        getUuid(),
        currentPortfolio.id,
        file,
        fileThumbnail,
        fileDocument.id,
        onProgress,
        custodianId
      );
    }

    request
      .then(apiData => {
        dispatch(removePendingDocumentUploadAction(fileDocument));
        // If local thumbnail is available use it to prevent jerkiness in the UI where
        // thumbnail disappears and appears again
        var doc = apiData.payload;
        if (fileDocument.thumbnail) {
          doc.thumbnail = fileDocument.thumbnail;
        }
        doc.tsModified = fileDocument.tsModified;
        fileDocument.isUploading = false;
        dispatch(insertDocumentAction(currentPortfolio.id, apiData.payload));
      })
      .catch(apiError => {
        captureError(getState(), apiError);
        fileDocument.isUploading = false;
        fileDocument.uploadProgress = 0;
        fileDocument.uploadError = apiError;
        dispatch(updatePendingDocumentUploadAction(fileDocument));
      });
  };
};

export const uploadCSVDocument = (file, isImportFlow = false) => {
  return dispatch => {
    const fileDocument = {
      id: getUuid(),
      name: file.name,
      size: file.size,
      fileType: file.type,
      tsModified: file.lastModified,
      isUploading: true,
      uploadProgress: 0,
      uploadError: null,
      file: file
    };

    return new Promise((resolve, reject) => {
      ApiClient.uploadWLKCSVDocument(getUuid(), file, fileDocument.id, isImportFlow)
        .then(apiData => {
          resolve(apiData.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const uploadHistoryDocument = (file, isImportFlow = false) => {
  return dispatch => {
    const fileDocument = {
      id: getUuid(),
      name: file.name,
      size: file.size,
      fileType: file.type,
      tsModified: file.lastModified,
      isUploading: true,
      uploadProgress: 0,
      uploadError: null,
      file: file
    };

    return new Promise((resolve, reject) => {
      ApiClient.uploadWLKHistoryDocument(getUuid(), file, fileDocument.id, isImportFlow)
        .then(apiData => {
          resolve(apiData.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const uploadAssetsDebtsDocument = (file, isImportFlow = false) => {
  return dispatch => {
    const fileDocument = {
      id: getUuid(),
      name: file.name,
      size: file.size,
      fileType: file.type,
      tsModified: file.lastModified,
      isUploading: true,
      uploadProgress: 0,
      uploadError: null,
      file: file
    };

    return new Promise((resolve, reject) => {
      ApiClient.uploadWLKAssetsDebtsDocument(getUuid(), file, fileDocument.id, isImportFlow)
        .then(apiData => {
          resolve(apiData.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const uploadCurrentValueDocument = (file, isImportFlow = false) => {
  return dispatch => {
    const fileDocument = {
      id: getUuid(),
      name: file.name,
      size: file.size,
      fileType: file.type,
      tsModified: file.lastModified,
      isUploading: true,
      uploadProgress: 0,
      uploadError: null,
      file: file
    };

    return new Promise((resolve, reject) => {
      ApiClient.uploadWLKCurrentValueDocument(getUuid(), file, fileDocument.id, isImportFlow)
        .then(apiData => {
          resolve(apiData.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const uploadAiImportDocument = (
  portfolioId,
  file,
  context,
  custodianId = undefined,
  sheetId = undefined,
  url = undefined,
  supplementalText = undefined
) => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.uploadAiImportDocument(
        getUuid(),
        portfolioId,
        file,
        getUuid(),
        context,
        custodianId,
        sheetId,
        url,
        supplementalText
      )
        .then(apiData => {
          resolve(apiData.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const rescanAiImportDocument = documentId => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.rescanAiImportDocument(getUuid(), documentId)
        .then(apiData => {
          resolve(apiData.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

const setMatchedCustodianIdBasedOnTargetCustodianId = (
  custodianNameCashflowMap,
  targetCustodianId,
  cashflowHasMultipleAssets
) => {
  const custodianNameCount = Object.keys(custodianNameCashflowMap).length;
  const targetCustodianAssignedKey = targetCustodianId
    ? Object.keys(custodianNameCashflowMap).find(
        key => custodianNameCashflowMap[key][0].matchedCustodianId === targetCustodianId
      )
    : undefined;
  if (targetCustodianId) {
    if (custodianNameCount === 1 && cashflowHasMultipleAssets === false) {
      for (const item of custodianNameCashflowMap[Object.keys(custodianNameCashflowMap)[0]]) {
        item.matchedCustodianId = targetCustodianId;
      }
    } else if (targetCustodianAssignedKey) {
      const cashflows = [...custodianNameCashflowMap[targetCustodianAssignedKey]];
      delete custodianNameCashflowMap[targetCustodianAssignedKey];
      custodianNameCashflowMap = Object.assign({ [targetCustodianAssignedKey]: cashflows }, custodianNameCashflowMap);

      Object.keys(custodianNameCashflowMap).forEach(key => {
        if (key !== targetCustodianAssignedKey) {
          custodianNameCashflowMap[key].forEach(cashflow => {
            cashflow.matchedCustodianId = undefined;
          });
        }
      });
    } else {
      for (const [key, cashflows] of Object.entries(custodianNameCashflowMap)) {
        cashflows.forEach(cashflow => {
          cashflow.matchedCustodianId = undefined;
        });
      }
    }
  }
  return custodianNameCashflowMap;
};

const getCustodianNameCashflowMap = (items, targetCustodianId, cashflowHasMultipleAssets) => {
  let custodianNameCashflowMap = {};
  for (const item of items) {
    if (!custodianNameCashflowMap[item.name] === true) {
      custodianNameCashflowMap[item.name] = [];
    }
    custodianNameCashflowMap[item.name].push(item);
  }
  custodianNameCashflowMap = setMatchedCustodianIdBasedOnTargetCustodianId(
    custodianNameCashflowMap,
    targetCustodianId,
    cashflowHasMultipleAssets
  );

  for (const key of Object.keys(custodianNameCashflowMap).reverse()) {
    const cashflows = custodianNameCashflowMap[key];
    if (cashflows.length > 0 && !cashflows[0].matchedCustodianId === false) {
      delete custodianNameCashflowMap[key];
      custodianNameCashflowMap = Object.assign({ [key]: cashflows }, custodianNameCashflowMap);
    }
  }
  return custodianNameCashflowMap;
};

export const getAiImportExtractedData = (documentId, targetCustodianId = undefined) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const maxRetryCount = 50;
      var retryCount = 0;

      const fetchExtractedData = () => {
        ApiClient.getAiImportExtractedData(getUuid(), documentId)
          .then(async apiData => {
            const response = apiData.payload;
            retryCount++;

            if (response.status === "error" || retryCount >= maxRetryCount) {
              if (response.statusInfo === "MAX_TOKENS") {
                reject({ errorCode: apiErrorCodes.MAX_TOKENS });
              } else {
                reject(apiData);
              }
              return;
            } else if (response.status === "in-progress") {
              setTimeout(() => {
                fetchExtractedData();
              }, 7000);
            } else if (response.status === "success") {
              const dataExtracted = JSON.parse(response.dataExtracted);
              response.dataExtracted = dataExtracted;

              const collapseMatchingCustodianIds = items => {
                for (const item of items || []) {
                  if (item.matchingCustodianIds === undefined) continue;
                  if (item.matchingCustodianIds.length === 0) {
                    item.matchedCustodianId = undefined;
                  } else {
                    item.matchedCustodianId = item.matchingCustodianIds[0];
                  }
                }
              };
              collapseMatchingCustodianIds(dataExtracted.account?.asset);
              collapseMatchingCustodianIds(dataExtracted.account?.debt);
              collapseMatchingCustodianIds(dataExtracted.cashFlow?.cashIn);
              collapseMatchingCustodianIds(dataExtracted.cashFlow?.cashOut);

              const cashflowHasMultipleAssets =
                [
                  ...new Set(
                    [...dataExtracted.cashFlow.cashIn, ...dataExtracted.cashFlow.cashOut].map(item => item.name)
                  )
                ].length > 1;
              const todayMidnightTime = getTodayMidnight().getTime();
              const cashIn = dataExtracted.cashFlow.cashIn.filter(
                item => parseKuberaDateString(item.date).getTime() <= todayMidnightTime
              );
              const cashOut = dataExtracted.cashFlow.cashOut.filter(
                item => parseKuberaDateString(item.date).getTime() <= todayMidnightTime
              );
              const capitalCall = dataExtracted.cashFlow.cashIn.filter(
                item => parseKuberaDateString(item.date).getTime() > todayMidnightTime
              );
              const distributionSchedule = dataExtracted.cashFlow.cashOut.filter(
                item => parseKuberaDateString(item.date).getTime() > todayMidnightTime
              );

              dataExtracted.cashFlow.cashIn = cashIn;
              dataExtracted.cashFlow.cashOut = cashOut;
              dataExtracted.capitalCall = { items: capitalCall };
              dataExtracted.distributionSchedule = { items: distributionSchedule };

              if (
                dataExtracted.account.asset.length === 0 &&
                dataExtracted.account.debt.length === 0 &&
                dataExtracted.cashFlow.cashIn.length === 0 &&
                dataExtracted.cashFlow.cashOut.length === 0 &&
                dataExtracted.capitalCall.items.length === 0 &&
                dataExtracted.distributionSchedule.items.length === 0
              ) {
                reject({ errorCode: apiErrorCodes.EMPTY_RESPONSE });
                return;
              }
              dataExtracted.cashFlow.custodianNameCashflowMap = getCustodianNameCashflowMap(
                [...dataExtracted.cashFlow.cashIn, ...dataExtracted.cashFlow.cashOut],
                targetCustodianId,
                cashflowHasMultipleAssets
              );
              dataExtracted.capitalCall.custodianNameCashflowMap = getCustodianNameCashflowMap(
                dataExtracted.capitalCall.items,
                targetCustodianId,
                cashflowHasMultipleAssets
              );
              dataExtracted.distributionSchedule.custodianNameCashflowMap = getCustodianNameCashflowMap(
                dataExtracted.distributionSchedule.items,
                targetCustodianId,
                cashflowHasMultipleAssets
              );

              const cashflowCustodianIds = [
                ...new Set(
                  [...dataExtracted.cashFlow.cashIn, ...dataExtracted.cashFlow.cashOut]
                    .filter(item => !item.matchedCustodianId === false)
                    .map(item => item.matchedCustodianId)
                )
              ];
              let custodianIdCashflowMap = {};
              await Promise.all(cashflowCustodianIds.map(custodianId => dispatch(getCustodianCashflow(custodianId))))
                .then(results => {
                  for (const result of results) {
                    if (result.length > 0) {
                      custodianIdCashflowMap[result[0].custodianId] = result;
                    }
                  }
                })
                .catch(err => {});
              dataExtracted.cashFlow.custodianIdCashflowMap = custodianIdCashflowMap;

              const isRedundantCashflowUpdate = (item, isCashIn) => {
                if (!item.matchedCustodianId) return false;
                const existing = (custodianIdCashflowMap[item.matchedCustodianId] || []).find(
                  entry => entry.date === item.date
                );
                if (!existing) return false;
                let itemValue, itemTickerId;
                if (item.quantity !== undefined && item.tickerId) {
                  itemValue = item.quantity;
                  itemTickerId = item.tickerId;
                } else if (item.value !== undefined && item.valueTickerId) {
                  itemValue = item.value;
                  itemTickerId = item.valueTickerId;
                } else {
                  return false;
                }
                return isCashIn
                  ? Number(existing.cashIn) === Number(itemValue) && existing.cashInTickerId === itemTickerId
                  : Number(existing.cashOut) === Number(itemValue) && existing.cashOutTickerId === itemTickerId;
              };
              const redundantItemIds = new Set();
              dataExtracted.cashFlow.cashIn = dataExtracted.cashFlow.cashIn.filter(item => {
                const redundant = isRedundantCashflowUpdate(item, true);
                if (redundant) redundantItemIds.add(item.id);
                return !redundant;
              });
              dataExtracted.cashFlow.cashOut = dataExtracted.cashFlow.cashOut.filter(item => {
                const redundant = isRedundantCashflowUpdate(item, false);
                if (redundant) redundantItemIds.add(item.id);
                return !redundant;
              });
              const filteredNameCashflowMap = dataExtracted.cashFlow.custodianNameCashflowMap;
              for (const name of Object.keys(filteredNameCashflowMap)) {
                filteredNameCashflowMap[name] = filteredNameCashflowMap[name].filter(
                  item => !redundantItemIds.has(item.id)
                );
                if (filteredNameCashflowMap[name].length === 0) {
                  delete filteredNameCashflowMap[name];
                }
              }

              const fundScheduleCustodianIds = [
                ...new Set(
                  [...dataExtracted.capitalCall.items, ...dataExtracted.distributionSchedule.items]
                    .filter(item => !item.matchedCustodianId === false)
                    .map(item => item.matchedCustodianId)
                )
              ];
              let custodianIdFundScheduleMap = {};
              await Promise.all(
                fundScheduleCustodianIds.map(custodianId => dispatch(getCustodianFundSchedule(custodianId)))
              )
                .then(results => {
                  for (const result of results) {
                    if (result.length > 0) {
                      custodianIdFundScheduleMap[result[0].custodianId] = result;
                    }
                  }
                })
                .catch(err => {});
              dataExtracted.capitalCall.custodianIdFundScheduleMap = custodianIdFundScheduleMap;
              dataExtracted.distributionSchedule.custodianIdFundScheduleMap = custodianIdFundScheduleMap;

              var unknownTickerIds = new Set();
              const tickerIdsToTest = [
                ...dataExtracted.account.asset.map(item => item.tickerId),
                ...dataExtracted.account.asset.map(item => item.valueTickerId),
                ...dataExtracted.account.asset.map(item => item.priceTickerId),
                ...dataExtracted.account.asset.map(item => item.costTickerId),
                ...dataExtracted.account.debt.map(item => item.tickerId),
                ...dataExtracted.account.debt.map(item => item.valueTickerId),
                ...dataExtracted.cashFlow.cashIn.map(item => item.tickerId),
                ...dataExtracted.cashFlow.cashIn.map(item => item.valueTickerId),
                ...dataExtracted.cashFlow.cashOut.map(item => item.tickerId),
                ...dataExtracted.cashFlow.cashOut.map(item => item.valueTickerId),
                ...dataExtracted.capitalCall.items.map(item => item.tickerId),
                ...dataExtracted.capitalCall.items.map(item => item.valueTickerId),
                ...dataExtracted.distributionSchedule.items.map(item => item.tickerId),
                ...dataExtracted.distributionSchedule.items.map(item => item.valueTickerId)
              ].filter(item => !item === false);
              for (const tickerId of tickerIdsToTest) {
                if (getTickerUsingId(getState(), tickerId).shortName === UNKNOWN_TICKER_SHORT_NAME) {
                  unknownTickerIds.add(tickerId);
                }
              }

              if (unknownTickerIds.size === 0) {
                resolve(response);
              } else {
                dispatch(
                  getTickersForIds(
                    Array.from(unknownTickerIds),
                    () => {
                      resolve(response);
                    },
                    apiError => {
                      resolve(response);
                    }
                  )
                );
              }
            }
          })
          .catch(apiError => {
            reject(apiError);
          });
      };
      fetchExtractedData();
    });
  };
};

export const getCustodianCashflow = custodianId => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.getCustodianCashflow(getUuid(), custodianId)
        .then(apiData => {
          resolve(apiData.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const getCustodianFundSchedule = custodianId => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.getCustodianFundSchedule(getUuid(), custodianId)
        .then(apiData => {
          resolve(apiData.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const updateAiImportDocumentUsageStatus = (portfolioId, documentId, ignoredHashes, usedHashes, stat) => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.updateAiImportDocumentUsageStatus(getUuid(), portfolioId, documentId, ignoredHashes, usedHashes, stat)
        .then(apiData => {
          resolve(apiData.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const renameDocument = (updatedDocument, portfolioId = null) => {
  return (dispatch, getState) => {
    const portfolio = portfolioId ? portfolioSelector(getState(), portfolioId) : currentPortfolioSelector(getState());
    dispatch(updateDocumentAction(portfolio.id, updatedDocument));

    const request = idempotentId => ApiClient.renameDocument(idempotentId, updatedDocument.id, updatedDocument.name);
    const syncItem = new SyncItem(SyncItemType.UPDATE, updatedDocument.id, request, 0, false, apiData => {});
    dispatch(enqueueItem(syncItem));
  };
};

export const renameFolder = (updatedDocument, portfolioId = null) => {
  return (dispatch, getState) => {
    const portfolio = portfolioId ? portfolioSelector(getState(), portfolioId) : currentPortfolioSelector(getState());
    dispatch(updateDocumentAction(portfolio.id, updatedDocument));

    const request = idempotentId => ApiClient.renameFolder(idempotentId, updatedDocument.id, updatedDocument.name);
    const syncItem = new SyncItem(SyncItemType.UPDATE, updatedDocument.id, request, 0, false, apiData => {});
    dispatch(enqueueItem(syncItem));
  };
};

export const deleteDocuments = (documents, portfolioId = null) => {
  return (dispatch, getState) => {
    for (const doc of documents) {
      const portfolio = portfolioId ? portfolioSelector(getState(), portfolioId) : currentPortfolioSelector(getState());
      dispatch(deleteDocumentAction(portfolio.id, doc));

      const request = idempotentId => ApiClient.deleteDocument(idempotentId, doc.id);
      const syncItem = new SyncItem(SyncItemType.DELETE, doc.id, request, 0, false, apiData => {});
      dispatch(enqueueItem(syncItem));
    }
  };
};

export const deleteDocumentsFolder = (documents, portfolioId = null) => {
  return (dispatch, getState) => {
    for (const doc of documents) {
      const portfolio = portfolioId ? portfolioSelector(getState(), portfolioId) : currentPortfolioSelector(getState());
      dispatch(deleteDocumentAction(portfolio.id, doc));

      const request = idempotentId => ApiClient.deleteDocumentsFolder(idempotentId, doc.id);
      const syncItem = new SyncItem(SyncItemType.DELETE, doc.id, request, 0, false, apiData => {});
      dispatch(enqueueItem(syncItem));
    }
  };
};

export const deleteDocumentsCustodian = (documents, portfolioId = null) => {
  return (dispatch, getState) => {
    for (const doc of documents) {
      const portfolio = portfolioId ? portfolioSelector(getState(), portfolioId) : currentPortfolioSelector(getState());
      dispatch(deleteDocumentAction(portfolio.id, doc));

      const request = idempotentId => ApiClient.deleteDocumentsCustodian(idempotentId, doc.id);
      const syncItem = new SyncItem(SyncItemType.DELETE, doc.id, request, 0, false, apiData => {});
      dispatch(enqueueItem(syncItem));
    }
  };
};

export const exportPortfolio = (portfolioId, portfolioName) => {
  return dispatch => {
    const request = idempotentId => ApiClient.exportPortfolio(idempotentId, portfolioId);
    const syncItem = new SyncItem(SyncItemType.DOWNLOAD, `export_${portfolioId}`, request, 0, false, apiData => {});
    syncItem.onError = () => {
      const toast = new Toast(
        toastType.GENERIC_ERROR,
        `Sorry, the ${portfolioName} download failed. Please try again.`,
        undefined,
        null,
        null
      );
      dispatch(showToastAction(toast));
    };
    dispatch(enqueueItem(syncItem));
  };
};

export const exportPortfolioSafeDeposit = (portfolioId, portfolioName) => {
  return dispatch => {
    const request = idempotentId => ApiClient.exportPortfolioSafeDeposit(idempotentId, portfolioId);
    const syncItem = new SyncItem(
      SyncItemType.DOWNLOAD,
      `export_safe_deposit_${portfolioId}`,
      request,
      0,
      false,
      apiData => {}
    );
    syncItem.onError = () => {
      const toast = new Toast(
        toastType.GENERIC_ERROR,
        `Sorry, the ${portfolioName} download failed. Please try again.`,
        undefined,
        null,
        null
      );
      dispatch(showToastAction(toast));
    };
    dispatch(enqueueItem(syncItem));
  };
};

export const exportCustodianDocuments = (sectionId, custodianId, custodianName) => {
  return dispatch => {
    const request = idempotentId => ApiClient.exportCustodianDocuments(idempotentId, sectionId, custodianId);
    const syncItem = new SyncItem(
      SyncItemType.DOWNLOAD,
      `export_custodian_documents_${custodianId}`,
      request,
      0,
      false,
      apiData => {}
    );
    syncItem.onError = () => {
      const toast = new Toast(
        toastType.GENERIC_ERROR,
        `Sorry, the ${custodianName} download failed. Please try again.`,
        undefined,
        null,
        null
      );
      dispatch(showToastAction(toast));
    };
    dispatch(enqueueItem(syncItem));
  };
};

export const getCustodianLastUpdateDetailsSelector = (state, custodianId) => {
  if (!custodianId === true) {
    return { dateString: "", isStale: false };
  }

  const custodian = custodianSelector(state, custodianId);
  if (!custodian === true || !custodian.tsLastUpdateCheck === true) {
    return { dateString: "", isStale: false };
  }

  const lastUpdateTs = custodian.tsLastUpdateCheck;
  const stringLastUpdate = `Last update: ${getFriendlyDateString(new Date(lastUpdateTs * 1000))}`;

  let isStale = false;
  if (!custodian.linkType === false) {
    isStale = new Date().getTime() - lastUpdateTs * 1000 > 24 * 60 * 60 * 1000;
  }

  return {
    dateString: stringLastUpdate,
    isStale: isStale,
    isRefreshing: custodian.isRefreshingInBackground === true || custodian.isRefreshing === true
  };
};

export const isZaboMigrationCandidate = custodian => {
  if (custodian.linkType === accountLinkingService.ZABO) {
    return (
      isZaboToInHouseApiCandidate(custodian.linkProviderId) || isZaboToInHouseOauthCandidate(custodian.linkProviderId)
    );
  }
  return false;
};

export const shouldShowLinkingErrorForCustodian = custodian => {
  if (
    isAppInViewMode() === true ||
    !custodian === true ||
    !custodian.tsLastUpdateCheck === true ||
    !custodian.linkType === true ||
    ["ITEM_LOGIN_REQUIRED", "REAL_TIME_MFA_REQUIRED", "ADDL_AUTHENTICATION_REQUIRED", "RECONNECT_REQUIRED"].includes(
      custodian.statusInfo
    )
  ) {
    return false;
  }

  // Disabled Yellow icon for Zillow/cars/domains as this can create
  // issues if users are returning after 2 or more days
  const excludeErrorLinkTypes = [
    accountLinkingService.ZERION,
    accountLinkingService.CARS,
    accountLinkingService.ZILLOW,
    accountLinkingService.DOMAINS,
    accountLinkingService.AI_APPRAISER
  ];

  if (excludeErrorLinkTypes.includes(custodian.linkType)) {
    return false;
  }
  if (custodian.linkType === accountLinkingService.ZABO) {
    return true;
  }
  if (
    custodian.status === 1 &&
    (custodian.statusInfo === "MIGRATION_DUE" || custodian.statusInfo === "CONNECTION_BROKEN")
  ) {
    return true;
  }
  const lastFetchedTs = custodian.tsLastUpdateCheck;
  const lastFetchMaxAge = (custodian.holdingsCount > 0 ? LAST_FETCH_MAX_AGE * 4 : LAST_FETCH_MAX_AGE) / 1000;
  return new Date().getTime() / 1000 - lastFetchedTs > lastFetchMaxAge;
};

export const custodianLinkNeedsRefreshSelector = (state, custodianId) => {
  var custodian = custodianSelector(state, custodianId);

  if (!custodian) {
    return false;
  }

  if (custodian.linkType === accountLinkingService.YODLEE) {
    if (custodian.status === 1 && custodian.statusInfo === "REAL_TIME_MFA_REQUIRED") {
      return true;
    }
    return false;
  }
  // For Zabo accounts that need migration use EDIT flow and not REFRESH flow
  if (custodian.linkType === accountLinkingService.ZABO && isZaboMigrationCandidate(custodian) === true) {
    return false;
  }
  return custodian.status === 1;
};

export const custodianLinkNeedsReconnectSelector = (state, custodianId) => {
  var custodian = custodianSelector(state, custodianId);

  if (!custodian) {
    return false;
  }

  if (custodian.linkType === accountLinkingService.ZABO) {
    return isZaboMigrationCandidate(custodian);
  }
  return custodian.status === 1;
};

export const getYTDNetWorthData = (netWorthData, lastYearEndData, portfolioStartDate) => {
  let ytdNetWorthData;
  let baseNetWorthData;
  const ytdStartDate = getYTDStartDate();
  const ytdStartDateString = getYTDStartDateString();
  if (portfolioStartDate.getTime() > ytdStartDate.getTime()) {
    const ytdIndex = getYTDIndexInTimeRangeArray();
    return netWorthData[timeRanges[ytdIndex - 1]];
  } else {
    const dateDifferenceFromYTD = getCurrentDateDifferenceFromYTD();
    if (dateDifferenceFromYTD <= 7) {
      baseNetWorthData = netWorthData[chartTimeRange.WEEKLY];
    } else if (dateDifferenceFromYTD > 7 && dateDifferenceFromYTD <= 30) {
      baseNetWorthData = netWorthData[chartTimeRange.MONTHLY];
    } else if (dateDifferenceFromYTD > 30 && dateDifferenceFromYTD <= 90) {
      baseNetWorthData = netWorthData[chartTimeRange.QUARTERLY];
    } else if (dateDifferenceFromYTD > 90) {
      baseNetWorthData = netWorthData[chartTimeRange.YEARLY];
    }
    if (lastYearEndData && lastYearEndData.date) {
      // find index of immediate date greater than or equal to ytd start date
      const ytdActualStartDateIndex = baseNetWorthData.netWorth.findIndex(
        netWorthData => toCivilDateString(netWorthData.date) >= ytdStartDateString
      );
      ytdNetWorthData = ytdActualStartDateIndex !== -1 ? baseNetWorthData.netWorth.slice(ytdActualStartDateIndex) : [];
      // check if networth data already has a data point for 01/01/year, add last year value only if not present
      const firstYtdDateString = ytdNetWorthData.length ? toCivilDateString(ytdNetWorthData[0].date) : null;
      if (!firstYtdDateString === false && firstYtdDateString !== ytdStartDateString) {
        lastYearEndData.date = ytdStartDateString;
        ytdNetWorthData.unshift(lastYearEndData);
      }
    } else {
      ytdNetWorthData = baseNetWorthData.netWorth;
    }

    return {
      ...baseNetWorthData,
      netWorth: ytdNetWorthData
    };
  }
};

export const getQTDNetWorthData = netWorthData => {
  let qtdNetWorthData;
  let baseNetWorthData;
  const qtdStartDateString = getQTDStartDateString();

  const dateDifferenceFromQTD = getCurrentDateDifferenceFromQTD();
  if (dateDifferenceFromQTD <= 7) {
    baseNetWorthData = netWorthData[chartTimeRange.WEEKLY];
  } else if (dateDifferenceFromQTD > 7 && dateDifferenceFromQTD <= 30) {
    baseNetWorthData = netWorthData[chartTimeRange.MONTHLY];
  } else {
    baseNetWorthData = netWorthData[chartTimeRange.QUARTERLY];
  }

  const qtdActualStartDateIndex = baseNetWorthData.netWorth.findIndex(
    netWorthData => toCivilDateString(netWorthData.date) >= qtdStartDateString
  );
  qtdNetWorthData = qtdActualStartDateIndex !== -1 ? baseNetWorthData.netWorth.slice(qtdActualStartDateIndex) : [];

  return {
    ...baseNetWorthData,
    netWorth: qtdNetWorthData
  };
};

const filterNetWorthDataBasedOnPortfolioStartDate = netWorthData => {
  for (let i = 1; i < timeRanges.length; i++) {
    if (netWorthData[timeRanges[i]].netWorth.length < 2) {
      for (let j = i; j < timeRanges.length; j++) {
        netWorthData[timeRanges[j]].netWorth = netWorthData[timeRanges[i - 1]].netWorth;
      }
    }
  }
  return netWorthData;
};

var portfolioChangeTimer = null;
export const fetchPortfolioChangeData = (portfolioId, waitBeforeFetch = true, showPendingState = false) => {
  return (dispatch, getState) => {
    if (isAppInExtensionMode()) return;
    const portfolio = portfolioSelector(getState(), portfolioId);
    if (!portfolio || !portfolio.details) return;
    const isChangeDataMissing = !portfolio.details.changeData === true;

    const fetchData = () => {
      const startDateTs = Math.floor(getNetWorthChartStartDateForPortfolio(getState(), portfolio).getTime() / 1000);

      if (showPendingState || isChangeDataMissing) {
        dispatch(fetchPortfolioChangeDataPendingAction(portfolioId));
      }

      const changeContributors = getChangeTotalsWithContributorsForCurrentPortfolio(getState(), categoryType.ASSET);
      var dayRequestType = "day";
      if (
        changeContributors.changes.year.isInsidePortfolioStartDate &&
        !changeContributors.changes.year.total === false
      ) {
        dayRequestType = "day_year";
      } else if (
        changeContributors.changes.month.isInsidePortfolioStartDate &&
        !changeContributors.changes.month.total === false
      ) {
        dayRequestType = "day_month";
      } else if (
        changeContributors.changes.week.isInsidePortfolioStartDate &&
        !changeContributors.changes.week.total === false
      ) {
        dayRequestType = "day_week";
      }

      const fetchAllChangeData = () => {
        ApiClient.getPortfolioChangeData(getUuid(), portfolioId, "all", undefined, startDateTs)
          .then(apiData => {
            dispatch(fetchPortfolioChangeDataSuccessAction(portfolioId, false, apiData.payload));
          })
          .catch(apiError => {
            dispatch(fetchPortfolioChangeDataErrorAction(portfolioId, false, apiError));
          });
      };

      ApiClient.getPortfolioChangeData(getUuid(), portfolioId, dayRequestType, undefined, startDateTs)
        .then(apiData => {
          dispatch(fetchPortfolioChangeDataSuccessAction(portfolioId, true, apiData.payload));

          if (showRefreshingSelector(getState()) === true) {
            dispatch(fetchPortfolioChangeDataPendingAction(portfolioId));
          }

          fetchAllChangeData();
        })
        .catch(apiError => {
          dispatch(fetchPortfolioChangeDataErrorAction(portfolioId, true, apiError));
        });
    };

    if (waitBeforeFetch && isChangeDataMissing === false) {
      if (!portfolioChangeTimer === true) {
        portfolioChangeTimer = setTimeout(() => {
          fetchData();
          portfolioChangeTimer = null;
        }, 10000);
      }
    } else {
      fetchData();
    }
  };
};

export const fetchChartAndCagrData = (portfolioId, currency = undefined) => {
  return (dispatch, getState) => {
    if (isAppInExtensionMode()) return;
    const portfolio = portfolioSelector(getState(), portfolioId);
    if (!portfolio || !portfolio.details) return;

    ApiClient.getChartAndCagrData(getUuid(), portfolioId, currency)
      .then(apiData => {
        dispatch(fetchChartAndCagrDataSuccessAction(portfolioId, apiData.payload));
      })
      .catch(apiError => {
        captureError(getState(), apiError);
      });
  };
};

var reportsDataFetchTsPortfolioMap = {};

const getConsolidatedDataForOneYearAgo = networthPayload => {
  let consolidatedOneYearAgoData = null;
  for (let i = 0; i < networthPayload.portfolios.length; i++) {
    const currentPortfolioData = networthPayload.portfolios[i];
    const currentPortfolioOneYearAgoData = currentPortfolioData[chartTimeRange.ONE_YEAR_AGO];
    if (!currentPortfolioOneYearAgoData) {
      continue;
    }
    const linkedPortfolioOwnershipPercentageForAssets = calculateOwnerShipPercentageInLinkedPortfolio(
      currentPortfolioData.portfolioId,
      networthPayload.portfolios,
      RECAP_CATEGORY_TYPE_ASSET,
      currentPortfolioOneYearAgoData.date,
      currentPortfolioData.parentPortfolioId,
      currentPortfolioData.linkedAssetCustodianIdInCurrentPortfolio,
      currentPortfolioData.directLinkedAssetCustodianId
    );
    const linkedPortfolioOwnershipPercentageForDebts = calculateOwnerShipPercentageInLinkedPortfolio(
      currentPortfolioData.portfolioId,
      networthPayload.portfolios,
      RECAP_CATEGORY_TYPE_DEBT,
      currentPortfolioOneYearAgoData.date,
      currentPortfolioData.parentPortfolioId,
      currentPortfolioData.linkedDebtCustodianIdInCurrentPortfolio,
      currentPortfolioData.directLinkedDebtCustodianId
    );
    const assetTotalWithPercentageConsiderd = calcCustodianOwnershipValue(
      currentPortfolioOneYearAgoData.assetTotal,
      linkedPortfolioOwnershipPercentageForAssets
    );
    const debtTotalWithPercentageConsiderd = calcCustodianOwnershipValue(
      currentPortfolioOneYearAgoData.debtTotal,
      linkedPortfolioOwnershipPercentageForDebts
    );
    const investibleTotalWithPercentageConsiderd = calcCustodianOwnershipValue(
      currentPortfolioOneYearAgoData.investibleTotal,
      linkedPortfolioOwnershipPercentageForAssets
    );
    if (!consolidatedOneYearAgoData) {
      consolidatedOneYearAgoData = {
        ...currentPortfolioOneYearAgoData,
        assetTotal: assetTotalWithPercentageConsiderd,
        debtTotal: debtTotalWithPercentageConsiderd,
        value: assetTotalWithPercentageConsiderd - debtTotalWithPercentageConsiderd,
        investibleTotal: investibleTotalWithPercentageConsiderd,
        asset: (currentPortfolioOneYearAgoData.asset || []).map(assetData => {
          return {
            ...assetData,
            portfolioId: currentPortfolioData.linkedPortfolioId,
            value: calcCustodianOwnershipValue(assetData.value, linkedPortfolioOwnershipPercentageForAssets)
          };
        }),
        debt: (currentPortfolioOneYearAgoData.debt || []).map(debtData => {
          return {
            ...debtData,
            portfolioId: currentPortfolioData.linkedPortfolioId,
            value: calcCustodianOwnershipValue(debtData.value, linkedPortfolioOwnershipPercentageForDebts)
          };
        })
      };
    } else {
      consolidatedOneYearAgoData.assetTotal += assetTotalWithPercentageConsiderd;
      consolidatedOneYearAgoData.debtTotal += debtTotalWithPercentageConsiderd;
      consolidatedOneYearAgoData.value += assetTotalWithPercentageConsiderd - debtTotalWithPercentageConsiderd;
      consolidatedOneYearAgoData.investibleTotal += investibleTotalWithPercentageConsiderd;
      consolidatedOneYearAgoData.asset = consolidatedOneYearAgoData.asset.concat(
        (currentPortfolioOneYearAgoData.asset || []).map(assetData => {
          return {
            ...assetData,
            portfolioId: currentPortfolioData.linkedPortfolioId,
            value: calcCustodianOwnershipValue(assetData.value, linkedPortfolioOwnershipPercentageForAssets)
          };
        }) || []
      );
      consolidatedOneYearAgoData.debt = consolidatedOneYearAgoData.debt.concat(
        (currentPortfolioOneYearAgoData.debt || []).map(debtData => {
          return {
            ...debtData,
            portfolioId: currentPortfolioData.linkedPortfolioId,
            value: calcCustodianOwnershipValue(debtData.value, linkedPortfolioOwnershipPercentageForDebts)
          };
        }) || []
      );
    }
  }
  return consolidatedOneYearAgoData;
};

const getConsolidatedDataForYTD = networthPayload => {
  let consolidatedYTDData = null;
  for (let i = 0; i < networthPayload.portfolios.length; i++) {
    const currentPortfolioData = networthPayload.portfolios[i];
    const currentPortfoliolastYearEndData = currentPortfolioData[chartTimeRange.LAST_YEAR_END];
    if (!currentPortfoliolastYearEndData) {
      continue;
    }
    const linkedPortfolioOwnershipPercentageForAssets = calculateOwnerShipPercentageInLinkedPortfolio(
      currentPortfolioData.portfolioId,
      networthPayload.portfolios,
      RECAP_CATEGORY_TYPE_ASSET,
      currentPortfoliolastYearEndData.date,
      currentPortfolioData.parentPortfolioId,
      currentPortfolioData.linkedAssetCustodianIdInCurrentPortfolio,
      currentPortfolioData.directLinkedAssetCustodianId
    );

    const linkedPortfolioOwnershipPercentageForDebts = calculateOwnerShipPercentageInLinkedPortfolio(
      currentPortfolioData.portfolioId,
      networthPayload.portfolios,
      RECAP_CATEGORY_TYPE_DEBT,
      currentPortfoliolastYearEndData.date,
      currentPortfolioData.parentPortfolioId,
      currentPortfolioData.linkedDebtCustodianIdInCurrentPortfolio,
      currentPortfolioData.directLinkedDebtCustodianId
    );

    const assetTotalWithPercentageConsiderd = calcCustodianOwnershipValue(
      currentPortfoliolastYearEndData.assetTotal,
      linkedPortfolioOwnershipPercentageForAssets
    );
    const debtTotalWithPercentageConsiderd = calcCustodianOwnershipValue(
      currentPortfoliolastYearEndData.debtTotal,
      linkedPortfolioOwnershipPercentageForDebts
    );
    const investibleTotalWithPercentageConsiderd = calcCustodianOwnershipValue(
      currentPortfoliolastYearEndData.investibleTotal,
      linkedPortfolioOwnershipPercentageForAssets
    );
    if (!consolidatedYTDData) {
      consolidatedYTDData = {
        ...currentPortfoliolastYearEndData,
        assetTotal: assetTotalWithPercentageConsiderd,
        debtTotal: debtTotalWithPercentageConsiderd,
        value: assetTotalWithPercentageConsiderd - debtTotalWithPercentageConsiderd,
        investibleTotal: investibleTotalWithPercentageConsiderd,
        asset: (currentPortfoliolastYearEndData.asset || []).map(assetData => {
          return {
            ...assetData,
            portfolioId: currentPortfolioData.linkedPortfolioId,
            parentPortfolioId: currentPortfolioData.parentPortfolioId,
            directLinkedAssetCustodianId: currentPortfolioData.directLinkedAssetCustodianId,
            linkedAssetCustodianIdInCurrentPortfolio: currentPortfolioData.linkedAssetCustodianIdInCurrentPortfolio,
            value: calcCustodianOwnershipValue(assetData.value, linkedPortfolioOwnershipPercentageForAssets)
          };
        }),
        debt: (currentPortfoliolastYearEndData.debt || []).map(debtData => {
          return {
            ...debtData,
            portfolioId: currentPortfolioData.linkedPortfolioId,
            parentPortfolioId: currentPortfolioData.parentPortfolioId,
            directLinkedDebtCustodianId: currentPortfolioData.directLinkedDebtCustodianId,
            linkedDebtCustodianIdInCurrentPortfolio: currentPortfolioData.linkedDebtCustodianIdInCurrentPortfolio,
            value: calcCustodianOwnershipValue(debtData.value, linkedPortfolioOwnershipPercentageForDebts)
          };
        })
      };
    } else {
      consolidatedYTDData.assetTotal += assetTotalWithPercentageConsiderd;
      consolidatedYTDData.debtTotal += debtTotalWithPercentageConsiderd;
      consolidatedYTDData.value += assetTotalWithPercentageConsiderd - debtTotalWithPercentageConsiderd;
      consolidatedYTDData.investibleTotal += investibleTotalWithPercentageConsiderd;
      consolidatedYTDData.asset = consolidatedYTDData.asset.concat(
        (currentPortfoliolastYearEndData.asset || []).map(assetData => {
          return {
            ...assetData,
            portfolioId: currentPortfolioData.linkedPortfolioId,
            parentPortfolioId: currentPortfolioData.parentPortfolioId,
            directLinkedAssetCustodianId: currentPortfolioData.directLinkedAssetCustodianId,
            linkedAssetCustodianIdInCurrentPortfolio: currentPortfolioData.linkedAssetCustodianIdInCurrentPortfolio,
            value: calcCustodianOwnershipValue(assetData.value, linkedPortfolioOwnershipPercentageForAssets)
          };
        }) || []
      );
      consolidatedYTDData.debt = consolidatedYTDData.debt.concat(
        (currentPortfoliolastYearEndData.debt || []).map(debtData => {
          return {
            ...debtData,
            portfolioId: currentPortfolioData.linkedPortfolioId,
            parentPortfolioId: currentPortfolioData.parentPortfolioId,
            directLinkedDebtCustodianId: currentPortfolioData.directLinkedDebtCustodianId,
            linkedDebtCustodianIdInCurrentPortfolio: currentPortfolioData.linkedDebtCustodianIdInCurrentPortfolio,
            value: calcCustodianOwnershipValue(debtData.value, linkedPortfolioOwnershipPercentageForDebts)
          };
        }) || []
      );
    }
  }
  return consolidatedYTDData;
};

const checkIfDateIsLastDataPointForTimeRange = (date, timeRange) => {
  switch (timeRange) {
    case "groupByMonth": {
      let dateToCheck = new Date(getDateInKuberaFormat(date).getTime());
      dateToCheck.setDate(dateToCheck.getDate() + 1);
      return dateToCheck.getDate() === 1;
    }
    case "groupByYear": {
      let dateToCheck = new Date(getDateInKuberaFormat(date).getTime());
      const currentYear = dateToCheck.getFullYear();
      dateToCheck.setDate(dateToCheck.getDate() + 1);
      const nextDateyear = dateToCheck.getFullYear();
      return currentYear + 1 === nextDateyear;
    }
    case "groupByWeek":
      const dayOfWeek = getDateInKuberaFormat(date).getDay();
      return dayOfWeek === 6;
    default:
      return true;
  }
};

const calculateOwnerShipPercentageInLinkedPortfolio = (
  portfolioId,
  portfoliosData,
  custodianCategory,
  date,
  parentPortfolioId,
  linkedCustodianIdInCurrentPortfolio,
  directLinkedCustodianId
) => {
  let i = 0;
  let ownershipValue;
  //= value;
  let portfolioData = portfoliosData.find(
    portfolio =>
      portfolio.portfolioId === portfolioId &&
      portfolio.parentPortfolioId === parentPortfolioId &&
      (custodianCategory === "Assets"
        ? portfolio.linkedAssetCustodianIdInCurrentPortfolio === linkedCustodianIdInCurrentPortfolio &&
          portfolio.directLinkedAssetCustodianId === directLinkedCustodianId
        : portfolio.linkedDebtCustodianIdInCurrentPortfolio === linkedCustodianIdInCurrentPortfolio &&
          portfolio.directLinkedDebtCustodianId === directLinkedCustodianId)
  );
  for (let i = 0; i < portfoliosData.length; i++) {
    const linkedPortfolioOwnershipPercentageForAssetsIndex = portfolioData?.linkedAssetCustodianOwnershipHistory
      ? portfolioData.linkedAssetCustodianOwnershipHistory
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .findIndex(ownershipData => new Date(ownershipData.date) - new Date(date) <= 0)
      : -1;
    const linkedPortfolioOwnershipPercentageForDebtsIndex = portfolioData?.linkedDebtCustodianOwnershipHistory
      ? portfolioData.linkedDebtCustodianOwnershipHistory
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .findIndex(ownershipData => new Date(ownershipData.date) - new Date(date) <= 0)
      : -1;

    const linkedPortfolioOwnershipPercentageForAssets =
      linkedPortfolioOwnershipPercentageForAssetsIndex !== -1
        ? portfolioData?.linkedAssetCustodianOwnershipHistory[linkedPortfolioOwnershipPercentageForAssetsIndex]
            .percentage
        : 100;
    const linkedPortfolioOwnershipPercentageForDebts =
      linkedPortfolioOwnershipPercentageForDebtsIndex !== -1
        ? portfolioData?.linkedDebtCustodianOwnershipHistory[linkedPortfolioOwnershipPercentageForDebtsIndex].percentage
        : 100;
    const ownershipPercentage =
      custodianCategory === RECAP_CATEGORY_TYPE_ASSET
        ? linkedPortfolioOwnershipPercentageForAssets
        : linkedPortfolioOwnershipPercentageForDebts;
    ownershipValue = i === 0 ? ownershipPercentage : calcCustodianOwnershipValue(ownershipValue, ownershipPercentage);
    if (portfolioData?.parentPortfolioId) {
      portfolioData = portfoliosData
        .filter(portfolio => portfolio.portfolioId === portfolioData.parentPortfolioId)
        .find(portfolio => {
          if (portfolio.primary) {
            return true;
          }
          if (custodianCategory === RECAP_CATEGORY_TYPE_ASSET) {
            return portfolio.directLinkedAssetCustodianId === portfolioData.directLinkedAssetCustodianId;
          } else {
            return portfolio.directLinkedDebtCustodianId === portfolioData.directLinkedDebtCustodianId;
          }
        });
      continue;
    } else {
      break;
    }
  }
  return ownershipValue;
};

const getConsolidatedNetWorthDataOfTimeRangeForAllPortfolios = (networthPayload, timeRange, netWorthChartStartTime) => {
  // this function merges networth data for a time range for all linked portfolios.
  const consolidatedNetWorthData = networthPayload.portfolios
    .map(portfolio => {
      // remove duplicate data points in networth
      return portfolio[timeRange].netWorth
        .reduce((accumulator, current) => {
          if (!accumulator.find(item => item.date === current.date)) {
            accumulator.push(current);
          }
          return accumulator;
        }, [])
        .map(data => {
          const linkedPortfolioOwnershipPercentageForAssets = calculateOwnerShipPercentageInLinkedPortfolio(
            portfolio.portfolioId,
            networthPayload.portfolios,
            RECAP_CATEGORY_TYPE_ASSET,
            data.date,
            portfolio.parentPortfolioId,
            portfolio.linkedAssetCustodianIdInCurrentPortfolio,
            portfolio.directLinkedAssetCustodianId
          );

          const linkedPortfolioOwnershipPercentageForDebts = calculateOwnerShipPercentageInLinkedPortfolio(
            portfolio.portfolioId,
            networthPayload.portfolios,
            RECAP_CATEGORY_TYPE_DEBT,
            data.date,
            portfolio.parentPortfolioId,
            portfolio.linkedDebtCustodianIdInCurrentPortfolio,
            portfolio.directLinkedDebtCustodianId
          );

          const assetTotalWithPercentageConsiderd = calcCustodianOwnershipValue(
            data.assetTotal,
            linkedPortfolioOwnershipPercentageForAssets
          );
          const debtTotalWithPercentageConsiderd = calcCustodianOwnershipValue(
            data.debtTotal,
            linkedPortfolioOwnershipPercentageForDebts
          );
          const investibleTotalWithPercentageConsiderd = calcCustodianOwnershipValue(
            data.investibleTotal,
            linkedPortfolioOwnershipPercentageForAssets
          );
          return {
            ...data,
            assetTotal: assetTotalWithPercentageConsiderd,
            debtTotal: debtTotalWithPercentageConsiderd,
            value: assetTotalWithPercentageConsiderd - debtTotalWithPercentageConsiderd,
            investibleTotal: investibleTotalWithPercentageConsiderd,
            asset: (data.asset || []).map(assetData => {
              return {
                ...assetData,
                portfolioId: portfolio.linkedPortfolioId,
                parentPortfolioId: portfolio.parentPortfolioId,
                directLinkedAssetCustodianId: portfolio.directLinkedAssetCustodianId,
                linkedAssetCustodianIdInCurrentPortfolio: portfolio.linkedAssetCustodianIdInCurrentPortfolio,
                value: calcCustodianOwnershipValue(assetData.value, linkedPortfolioOwnershipPercentageForAssets)
              };
            }),
            debt: (data.debt || []).map(debtData => {
              return {
                ...debtData,
                portfolioId: portfolio.linkedPortfolioId,
                parentPortfolioId: portfolio.parentPortfolioId,
                directLinkedDebtCustodianId: portfolio.directLinkedDebtCustodianId,
                linkedDebtCustodianIdInCurrentPortfolio: portfolio.linkedDebtCustodianIdInCurrentPortfolio,
                value: calcCustodianOwnershipValue(debtData.value, linkedPortfolioOwnershipPercentageForDebts)
              };
            })
          };
        });
    })
    .flat();
  const latestDataPoint =
    consolidatedNetWorthData.length &&
    consolidatedNetWorthData.reduce((latest, current) =>
      new Date(current.date) > new Date(latest.date) ? current : latest
    );
  const dataPointsFromChartStartDate = consolidatedNetWorthData.filter(
    dataPoint => getDateInKuberaFormat(dataPoint.date).getTime() >= netWorthChartStartTime
  );
  const oldestDataPoint =
    dataPointsFromChartStartDate.length &&
    dataPointsFromChartStartDate.reduce((oldest, current) =>
      new Date(current.date) < new Date(oldest.date) ? current : oldest
    );
  const consolidatedNetWorthDataForAllPortfolios = [];
  for (let i = 0; i < consolidatedNetWorthData.length; i++) {
    const currentData = consolidatedNetWorthData[i];
    const existingData = consolidatedNetWorthDataForAllPortfolios.find(data => data.date === currentData.date);
    if (existingData) {
      existingData.value += currentData.value;
      existingData.assetTotal += currentData.assetTotal;
      existingData.debtTotal += currentData.debtTotal;
      existingData.investibleTotal += currentData.investibleTotal;
      existingData.asset = (existingData.asset || []).concat(currentData.asset || []);
      existingData.debt = (existingData.debt || []).concat(currentData.debt || []);
    } else if (
      (checkIfDateIsLastDataPointForTimeRange(currentData.date, timeRange) ||
        currentData.date === latestDataPoint.date ||
        currentData.date === oldestDataPoint.date) &&
      !existingData
    ) {
      consolidatedNetWorthDataForAllPortfolios.push(currentData);
    }
  }
  // sort the data points in ascending order of date
  return consolidatedNetWorthDataForAllPortfolios.sort((a, b) => new Date(a.date) - new Date(b.date));
};

const getConsolidatedCustodianFundamentalDataForRootPortfolio = networthPayload => {
  const consolidatedFundamentalDataForRootPortfolio = [];
  for (let i = 0; i < networthPayload.portfolios.length; i++) {
    const portfolioData = networthPayload.portfolios[i];
    const fundamentalDataForPortfolio = portfolioData.custodiansFundData
      ? portfolioData.custodiansFundData.map(data => {
          return { ...data, portfolioId: portfolioData.linkedPortfolioId };
        })
      : [];
    consolidatedFundamentalDataForRootPortfolio.push(fundamentalDataForPortfolio);
  }
  return consolidatedFundamentalDataForRootPortfolio.flat();
};

const getUpdatedPortfolios = (portfolioLinkedCustodians, portfolios, rootPortfolioId) => {
  const rootPortfolio = portfolios?.find(portfolio => {
    return portfolio.portfolioId === rootPortfolioId;
  });
  if (!rootPortfolio) return [];
  const updatedPortfolios = [{ ...rootPortfolio }];

  if (!portfolioLinkedCustodians) return updatedPortfolios;

  for (const portfolioLinkedCustodian of portfolioLinkedCustodians) {
    // check if portfolio networth data is already present in
    const portfolioDataForLinkedCustodian = portfolios.find(data => {
      if (data.portfolioId === portfolioLinkedCustodian.linkedPortfolioId) {
        return { ...data };
      }
    });
    // ****************** this code will be removed once the backend is updated to send only required data ********************************
    for (const key of Object.keys(portfolioDataForLinkedCustodian)) {
      if (
        key !== "groupByDay" &&
        key !== "groupByMonth" &&
        key !== "groupByWeek" &&
        key !== "groupByYear" &&
        key !== "custodiansFundData" &&
        key !== "portfolioId" &&
        key !== "lastYearEnd" &&
        key !== "oneYearAgo" &&
        key !== "linkedPortfolioId"
      ) {
        delete portfolioDataForLinkedCustodian[key];
      }
    }
    const updatedPortfoliosDataIndex = updatedPortfolios.findIndex(data => {
      if (portfolioLinkedCustodian.category === "Asset") {
        return (
          data.portfolioId === portfolioLinkedCustodian.linkedPortfolioId &&
          data.parentPortfolioId === portfolioLinkedCustodian.parentPortfolioId &&
          !data.directLinkedAssetCustodianId === true
        );
      } else {
        return (
          data.portfolioId === portfolioLinkedCustodian.linkedPortfolioId &&
          data.parentPortfolioId === portfolioLinkedCustodian.parentPortfolioId &&
          !data.directLinkedDebtCustodianId === true
        );
      }
    });

    if (updatedPortfoliosDataIndex !== -1) {
      const updatedPortfolioDataForLinkedCustodian = updatedPortfolios[updatedPortfoliosDataIndex];

      if (portfolioLinkedCustodian.category === "Asset") {
        updatedPortfolioDataForLinkedCustodian.directLinkedAssetCustodianId = portfolioLinkedCustodian.firstCustodianId;
        updatedPortfolioDataForLinkedCustodian.directLinkedAssetCustodianName =
          portfolioLinkedCustodian.linkedCustodianName;
        updatedPortfolioDataForLinkedCustodian.directLinkedAssetCustodianSectionId =
          portfolioLinkedCustodian.firstCustodianSectionId;
        updatedPortfolioDataForLinkedCustodian.directLinkedAssetCustodianOwnership = portfolioLinkedCustodian.ownership;
        updatedPortfolioDataForLinkedCustodian.linkedAssetCustodianOwnershipHistory =
          portfolioLinkedCustodian.ownershipHistoryTable;
        updatedPortfolioDataForLinkedCustodian.linkedAssetCustodianIdInCurrentPortfolio =
          portfolioLinkedCustodian.linkedCustodianId;
      } else {
        updatedPortfolioDataForLinkedCustodian.directLinkedDebtCustodianId = portfolioLinkedCustodian.firstCustodianId;
        updatedPortfolioDataForLinkedCustodian.directLinkedDebtCustodianName =
          portfolioLinkedCustodian.linkedCustodianName;
        updatedPortfolioDataForLinkedCustodian.directLinkedDebtCustodianSectionId =
          portfolioLinkedCustodian.firstCustodianSectionId;
        updatedPortfolioDataForLinkedCustodian.directLinkedDebtCustodianOwnership = portfolioLinkedCustodian.ownership;
        updatedPortfolioDataForLinkedCustodian.linkedDebtCustodianOwnershipHistory =
          portfolioLinkedCustodian.ownershipHistoryTable;
        updatedPortfolioDataForLinkedCustodian.linkedDebtCustodianIdInCurrentPortfolio =
          portfolioLinkedCustodian.linkedCustodianId;
      }
    } else {
      const updatedPortfolioDataForLinkedCustodian = JSON.parse(JSON.stringify(portfolioDataForLinkedCustodian));
      updatedPortfolioDataForLinkedCustodian.parentPortfolioId = portfolioLinkedCustodian.parentPortfolioId;
      if (portfolioLinkedCustodian.category === "Asset") {
        updatedPortfolioDataForLinkedCustodian.directLinkedAssetCustodianId = portfolioLinkedCustodian.firstCustodianId;
        updatedPortfolioDataForLinkedCustodian.directLinkedAssetCustodianName =
          portfolioLinkedCustodian.linkedCustodianName;
        updatedPortfolioDataForLinkedCustodian.directLinkedAssetCustodianSectionId =
          portfolioLinkedCustodian.firstCustodianSectionId;
        updatedPortfolioDataForLinkedCustodian.directLinkedAssetCustodianOwnership = portfolioLinkedCustodian.ownership;
        updatedPortfolioDataForLinkedCustodian.linkedAssetCustodianOwnershipHistory =
          portfolioLinkedCustodian.ownershipHistoryTable;
        updatedPortfolioDataForLinkedCustodian.linkedAssetCustodianIdInCurrentPortfolio =
          portfolioLinkedCustodian.linkedCustodianId;
      } else {
        updatedPortfolioDataForLinkedCustodian.directLinkedDebtCustodianId = portfolioLinkedCustodian.firstCustodianId;
        updatedPortfolioDataForLinkedCustodian.directLinkedDebtCustodianName =
          portfolioLinkedCustodian.linkedCustodianName;
        updatedPortfolioDataForLinkedCustodian.directLinkedDebtCustodianSectionId =
          portfolioLinkedCustodian.firstCustodianSectionId;
        updatedPortfolioDataForLinkedCustodian.directLinkedDebtCustodianOwnership = portfolioLinkedCustodian.ownership;
        updatedPortfolioDataForLinkedCustodian.linkedDebtCustodianOwnershipHistory =
          portfolioLinkedCustodian.ownershipHistoryTable;
        updatedPortfolioDataForLinkedCustodian.linkedDebtCustodianIdInCurrentPortfolio =
          portfolioLinkedCustodian.linkedCustodianId;
      }
      updatedPortfolios.push(updatedPortfolioDataForLinkedCustodian);
    }
  }
  return updatedPortfolios;
};

const getCustodianNameForCashOnHandDataInDashboardSelector = (state, custodianData, portfolioId, cashOnHandData) => {
  if (custodianData.parentId) {
    const parentCustodian = custodianSelector(state, custodianData.parentId);
    return parentCustodian?.name ? `${parentCustodian.name} - ${custodianData.name}` : custodianData.name;
  } else if (custodianData?.linkType === 0 && !custodianData.parentId) {
    // to be changed
    const custodianIndex = cashOnHandData.findIndex(custodian => {
      return custodian.name === custodianData.name;
    });
    if (custodianIndex === -1 || !custodianIndex) {
      return custodianData.name;
    }
    const portfolioData = portfoliosSelector(state).find(portfolio => portfolio.id === portfolioId);
    const sectionData = sectionSelector(state, custodianData.sectionId);
    const sectionNameForCustodian = sectionData?.name;
    const sheetData = sheetSelector(state, sectionData?.sheetId);
    const sheetName = sheetData?.name;
    const custodianName =
      sheetData?.portfolioId !== portfolioId
        ? ` ${portfolioData.name} / ${sheetName} / ${sectionNameForCustodian} / ${custodianData.name}`
        : `${sheetName} / ${sectionNameForCustodian} / ${custodianData.name}`;

    return custodianName;
  }
  return custodianData.name;
};

const checkIfCustodianHasTaxOnUnrealisedGains = custodianData => {
  if (!custodianData) {
    return false;
  }
  const taxDetails = custodianData.taxDetails ? JSON.parse(custodianData.taxDetails) : null;
  const isCustodianMarkedAsTaxFree =
    taxDetails && taxDetails.taxableAssetType ? taxDetails.taxableAssetType === custodianTaxTypes.TAX_FREE : false;
  return (
    !isCustodianMarkedAsTaxFree &&
    (!taxDetails ||
      (taxDetails && (taxDetails.costBasis === 0 || !taxDetails.costBasis === false)) ||
      custodianData.irrType === irrTypes.HOLDING ||
      (custodianData.irrType === irrTypes.COSTBASIS && (custodianData.cost === 0 || !custodianData.cost === false))) &&
    custodianData.type !== 2
  );
};

const checkIfCustodianIsUnfunded = custodianData => {
  if (!custodianData) {
    return false;
  }
  return !custodianData.cmtdCap === false && custodianData.hidden !== 1;
};

export const calculateDashboardDataForPortfolio = () => {
  return (dispatch, getState) => {
    const state = getState();
    const allPortfolios = portfoliosSelector(state);
    const currentPortfolio = currentPortfolioSelector(state);
    if (!allPortfolios || !currentPortfolio) {
      return;
    }

    const cashOnHandData = [];
    const taxEstimateData = [];
    const unfundedCustodiansData = [];
    const portfolioTreeData = portfolioTreeDataSelector(state);
    const currentPortfolioId = currentPortfolio.id;
    const currentPortfolioCurrencyTicker = getTickerUsingShortName(getState(), currentPortfolio.currency);

    // Create a Map for portfolio lookups
    const portfolioMap = new Map();
    allPortfolios.forEach(portfolio => {
      portfolioMap.set(portfolio.id, portfolio);
    });

    // Build custodian sheet cache to avoid repeated expensive selector calls
    const custodianSheetCache = new Map();
    allPortfolios.forEach(portfolio => {
      portfolio.details.custodian.forEach(custodian => {
        // Build the sheet lookup inline to avoid selector overhead
        const section = portfolio.details.section.find(s => s.id === custodian.sectionId);
        if (section) {
          const sheet = portfolio.details.sheet.find(s => s.id === section.sheetId);
          if (sheet) {
            custodianSheetCache.set(custodian.id, sheet);
          }
        }
      });
    });

    const globalClubbingNames = new Map(); // Map<tickerId, clubbingName>
    const investableDataMap = new Map(); // Map<name, custodianData>

    function processData(node, cumulativeOwnership = 1.0) {
      if (node?.id) {
        const portfolioData = portfolioMap.get(node.id);
        if (!portfolioData) return;

        const portfolioCustodians = portfolioData?.details?.custodian;
        for (const custodian of portfolioCustodians) {
          const custodianSheet = custodianSheetCache.get(custodian.id);
          const custodianCategory = custodianSheet?.category;
          if (custodianCategory !== categoryType.ASSET) {
            continue;
          }
          const isParentCustodianWithHoldings = !custodian.parentId && custodian.holdingsCount > 0;
          if ((custodian.type === 2 || custodian.type === 0) && !isParentCustodianWithHoldings) {
            const tickerDetails = getTickerUsingId(getState(), custodian.valueTickerId);

            let custodianDisplayName = custodian.name;
            if (custodian.holdingSubType === holdingSubTypes.AGGREGATED_HOLDING && custodian.parentId) {
              const parentCustodian = portfolioCustodians.find(c => c.id === custodian.parentId);
              if (parentCustodian) {
                custodianDisplayName = `${parentCustodian.name} - ${custodian.name}`;
              }
            }

            const clubbingName = getClubbingKeyForCustodian(tickerDetails, custodianDisplayName, globalClubbingNames);
            const existingCustodian = investableDataMap.get(clubbingName);
            const rawCustodianValue = getCustodianValue(
              getState(),
              custodian,
              categoryType.ASSET,
              currentPortfolioCurrencyTicker
            );
            const custodianValue = calcCustodianOwnershipValue(rawCustodianValue, cumulativeOwnership * 100);

            if (!existingCustodian) {
              investableDataMap.set(clubbingName, {
                ...custodian,
                name: clubbingName,
                value: custodianValue,
                valueTickerId: currentPortfolioCurrencyTicker.id,
                actualName: custodianDisplayName
              });
            } else {
              existingCustodian.value += custodianValue;
              existingCustodian.isClubbed = true;
            }
          }
          if (custodian.type === 2 && !isParentCustodianWithHoldings) {
            cashOnHandData.push({
              ...custodian,
              name: getCustodianNameForCashOnHandDataInDashboardSelector(
                getState(),
                custodian,
                portfolioData.id,
                cashOnHandData
              ),
              value: calcCustodianOwnershipValue(custodian.value, cumulativeOwnership * 100)
            });
          }
          if (checkIfCustodianHasTaxOnUnrealisedGains(custodian) && custodian.hidden === 0) {
            taxEstimateData.push({
              ...custodian,
              cumulativeOwnership: cumulativeOwnership * 100,
              portfolioId: portfolioData.id
            });
          }
          if (checkIfCustodianIsUnfunded(custodian)) {
            unfundedCustodiansData.push({
              ...custodian,
              value: calcCustodianOwnershipValue(custodian.value, cumulativeOwnership * 100),
              portfolioId: portfolioData.id
            });
          }
        }
      }
      for (const child of node?.children || []) {
        // Calculate the cumulative ownership for this specific child
        const childOwnership = child.ownership ? child.ownership / 100 : 1.0;
        const childCumulativeOwnership = cumulativeOwnership * childOwnership;
        processData(child, childCumulativeOwnership);
      }
    }
    if (portfolioTreeData.children.length > 0) {
      const currentPortfolioInTree = findPortfolioInTree(portfolioTreeData, currentPortfolioId);
      buildGlobalClubbingNames(getState(), currentPortfolioInTree, portfolioMap, globalClubbingNames);

      // Process data starting with 100% ownership for the current portfolio
      processData(currentPortfolioInTree, 1.0);
    }

    const investableDataArray = Array.from(investableDataMap.values());

    dispatch(
      calculateDashboardDataForPortfolioSuccessAction(currentPortfolioId, {
        cashOnHandData,
        investableData: investableDataArray,
        taxEstimateData,
        unfundedCustodiansData
      })
    );
  };
};

export const fetchNetWorthDataForPortfolio = (
  portfolioId,
  isInitialLoad = false,
  forNormalView = false,
  updatedCurrency = undefined,
  isRecapPage = false,
  shouldOverRideDelay = false,
  reportsToProcess = undefined
) => {
  return (dispatch, getState) => {
    if (isAppInExtensionMode()) return;
    // if (fetchPortfolioPendingSelector(getState())) {
    //   return;
    // }
    const recapWorkerKey = getRecapWorkerKey(portfolioId, isInitialLoad);
    console.log("activeRecapWorkerPromises before fetching recap data", activeRecapWorkerPromises.has(recapWorkerKey));
    if (activeRecapWorkerPromises.has(recapWorkerKey)) {
      return;
    }
    // Don't fetch reports data if it has been less than twenty seconds since last fetch
    if (
      !updatedCurrency === true &&
      !reportsDataFetchTsPortfolioMap[portfolioId] === false &&
      isInitialLoad &&
      !shouldOverRideDelay
    ) {
      const lastFetchTs = reportsDataFetchTsPortfolioMap[portfolioId];
      const age = Date.now() - lastFetchTs;
      if (age < 20 * 1000) {
        return;
      }
    }
    reportsDataFetchTsPortfolioMap[portfolioId] = Date.now();

    // Mark recap pipeline as in-flight BEFORE the API call so rapid re-clicks
    // during the API window are blocked too. calculateRecapDataWithWorkers
    // overwrites this with the real worker pool promise once workers spawn;
    // the .catch below clears it if the API itself fails.
    activeRecapWorkerPromises.set(recapWorkerKey, new Promise(() => {}));

    dispatch(fetchNetWorthDataPendingAction(portfolioId));
    dispatch(fetchRecapDataPendingAction(portfolioId));
    const currencyForRequest =
      updatedCurrency !== undefined
        ? updatedCurrency
        : isAppInViewMode()
        ? currentPortfolioCurrencySelector(getState())
        : undefined;
    Promise.all([ApiClient.getReportsData(getUuid(), portfolioId, currencyForRequest, isMobileDevice)])
      .then(async ([reportsDataReponse]) => {
        const { payload } = reportsDataReponse;
        const recapRawData = window.kbStructuredClone(payload);
        const reportData = window.kbStructuredClone(payload);
        const networthPayload = payload;
        const reportToBeProcessedMap = getChartAndTimeRangeToProcessSelector(
          getState(),
          isInitialLoad,
          false,
          reportsToProcess || (isRecapPage ? selectedRecapReportScopeSelector(getState()) : undefined)
        );

        const netWorthData = dispatch(
          processNetWorthDataFromCustodiansRecapResponse(portfolioId, networthPayload, recapRawData)
        );

        dispatch(
          processRecapDataFromCustodiansResponse(
            reportData,
            portfolioId,
            forNormalView,
            isInitialLoad,
            reportToBeProcessedMap,
            isRecapPage
          )
        );

        ApiClient.getVanityStat(getUuid(), portfolioId, getVanityMetrics(getState(), portfolioId, netWorthData))
          .then(summaryStatResponse => {
            dispatch(fetchVanityStatSuccessAction(portfolioId, summaryStatResponse.payload));
          })
          .catch(error => {
            captureError(getState(), error);
          });
      })
      .catch(apiError => {
        activeRecapWorkerPromises.delete(recapWorkerKey);
        console.log("eee", apiError);
        captureError(getState(), apiError);
        dispatch(fetchNetWorthDataErrorAction(portfolioId, apiError));
      });
  };
};

const getVanityMetrics = (state, portfolioId, netWorthData) => {
  const allRangeData = netWorthData?.[chartTimeRange.ALL];
  const startDataPoint = allRangeData?.netWorth?.[0];
  const portfolio = portfolioSelector(state, portfolioId);
  if (!startDataPoint || !portfolio) {
    return null;
  }
  const portfolioCurrency = portfolioCurrencySelector(state, portfolio);
  return {
    networth: portfolioNetWorth(state, portfolio),
    startDateTs: Math.floor(getNetWorthChartStartDateForPortfolio(state, portfolio).getTime() / 1000),
    startNetworth: convertCurrency(state, startDataPoint.value, allRangeData.currency, portfolioCurrency)
  };
};

const processNetWorthDataFromCustodiansRecapResponse = (portfolioId, networthPayload, recapRawData) => (
  dispatch,
  getState
) => {
  networthPayload.portfolios = getUpdatedPortfolios(
    networthPayload.portfolioLinkedCustodians,
    networthPayload.portfolios,
    portfolioId
  );
  const networthPayloadForRootPortfolio = networthPayload.portfolios.find(
    portfolio => portfolio.portfolioId === portfolioId
  );
  const netWorthChartStartDate = getNetWorthChartStartDateForPortfolio(getState(), undefined, networthPayload);
  const netWorthChartStartTime = netWorthChartStartDate.getTime();
  // for calculting networth, we should consider data of all linked portfolios as well.
  networthPayloadForRootPortfolio.groupByDay.netWorth = getConsolidatedNetWorthDataOfTimeRangeForAllPortfolios(
    networthPayload,
    "groupByDay",
    netWorthChartStartTime
  );
  networthPayloadForRootPortfolio.groupByWeek.netWorth = getConsolidatedNetWorthDataOfTimeRangeForAllPortfolios(
    networthPayload,
    "groupByWeek",
    netWorthChartStartTime
  );
  networthPayloadForRootPortfolio.groupByMonth.netWorth = getConsolidatedNetWorthDataOfTimeRangeForAllPortfolios(
    networthPayload,
    "groupByMonth",
    netWorthChartStartTime
  );
  networthPayloadForRootPortfolio.groupByYear.netWorth = getConsolidatedNetWorthDataOfTimeRangeForAllPortfolios(
    networthPayload,
    "groupByYear",
    netWorthChartStartTime
  );
  networthPayloadForRootPortfolio[chartTimeRange.LAST_YEAR_END] = getConsolidatedDataForYTD(networthPayload);
  networthPayloadForRootPortfolio[chartTimeRange.ONE_YEAR_AGO] = getConsolidatedDataForOneYearAgo(networthPayload);
  networthPayloadForRootPortfolio.custodiansFundData = getConsolidatedCustodianFundamentalDataForRootPortfolio(
    networthPayload
  );

  // Remove invalid data points with value as 0
  // Check if user has latest keys available. if not fall back to older keys
  let netWorthData = getChartDataFromResponse(
    networthPayloadForRootPortfolio,
    "netWorth",
    false,
    false,
    netWorthChartStartDate
  );

  const netWorthDataCurrency = netWorthData && netWorthData["groupByDay"] && netWorthData["groupByDay"]["currency"];

  for (const netWorthSectionKey in netWorthData) {
    if (netWorthSectionKey === chartTimeRange.LAST_YEAR_END || netWorthSectionKey === chartTimeRange.ONE_YEAR_AGO) {
      continue;
    }
    const netWorthSection = netWorthData[netWorthSectionKey];
    // filter only data points greater than or equal to networth chart start data
    netWorthSection.netWorth = (netWorthSection.netWorth || []).filter(
      dataPoint => getDateInKuberaFormat(dataPoint.date).getTime() >= netWorthChartStartTime
    );
    // sort the data points
    for (const dataPoint of netWorthSection.netWorth) {
      dataPoint.asset = dataPoint.asset && dataPoint.asset.sort((a, b) => b.value - a.value);
    }
  }

  netWorthData = filterNetWorthDataBasedOnPortfolioStartDate(netWorthData);

  for (const netWorthSectionKey in netWorthData) {
    if (
      netWorthSectionKey !== "groupByMonth" &&
      netWorthSectionKey !== "groupByWeek" &&
      netWorthSectionKey !== "groupByYear" &&
      netWorthSectionKey !== "groupByDay"
    ) {
      continue;
    }
    const netWorthSection = netWorthData[netWorthSectionKey];
    netWorthSection.netWorth = (netWorthSection.netWorth || []).filter(
      removeDuplicateDataPointsFromnetWorthResponse(netWorthSectionKey, netWorthSection.netWorth)
    );
  }
  const ytdNetWorthData = getYTDNetWorthData(
    netWorthData,
    netWorthData[chartTimeRange.LAST_YEAR_END],
    netWorthChartStartDate
  );
  netWorthData.ytd = ytdNetWorthData;

  const qtdNetWorthData = getQTDNetWorthData(netWorthData);
  netWorthData.qtd = qtdNetWorthData;

  // Create a lookup map for O(1) custodian access instead of O(C) find operations
  let custodianMap = new Map();
  for (const custodian of networthPayloadForRootPortfolio.custodiansFundData || []) {
    const mapKey = `${custodian.id}_${custodian.portfolioId}`;
    custodianMap.set(mapKey, custodian);
  }

  // Get names for asset and debt data points to be used when
  // calculating what contributed to the networth / investablel change
  for (const key in netWorthData) {
    if (netWorthData[key].netWorth) {
      for (const dataPoint of netWorthData[key].netWorth) {
        for (const asset of dataPoint.asset || []) {
          const mapKey = `${asset.id}_${asset.portfolioId}`;
          const custodian = custodianMap.get(mapKey);
          if (custodian) {
            asset.hidden = custodian.hdn;
            asset.type = custodian.tp;
            asset.name = custodian.n;
          }
        }
        for (const debt of dataPoint.debt || []) {
          const mapKey = `${debt.id}_${debt.portfolioId}`;
          const custodian = custodianMap.get(mapKey);
          if (custodian) {
            debt.hidden = custodian.hdn;
            debt.type = custodian.tp;
            debt.name = custodian.n;
          }
        }
      }
    }
  }
  custodianMap.clear();
  custodianMap = null;

  dispatch(
    fetchNetWorthDataSuccessAction(portfolioId, {
      ...netWorthData,
      market: networthPayloadForRootPortfolio.market,
      connErrors: networthPayloadForRootPortfolio.connErrors,
      custodiansFundData: networthPayloadForRootPortfolio.custodiansFundData,
      netWorthDataCurrency: netWorthDataCurrency,
      startDate: networthPayload.startDate,
      market: networthPayload.market, // eslint-disable-line no-dupe-keys
      recapRawData
    })
  );
  dispatch(updateDashboardAction([]));
  return netWorthData;
};

export const processRecapDataFromCustodiansResponse = (
  reportData,
  portfolioId,
  forNormalView,
  isInitialLoad,
  reportToBeProcessedMap,
  isRecapPage = false,
  processRecapDataOnLocalUpdate = false,
  shouldCalculateRecapFromPortfolioResponse = false
) => (dispatch, getState) => {
  reportData.portfolios = getUpdatedPortfolios(
    reportData.portfolioLinkedCustodians,
    reportData.portfolios,
    portfolioId
  );
  const netWorthChartStartDate = getNetWorthChartStartDateForPortfolio(getState(), undefined, reportData);
  const netWorthChartStartDateString = getKuberaDateString(netWorthChartStartDate);

  reportData.portfolios = reportData.portfolios.map(portfolio => {
    let reportsDataForPortfolio = getChartDataFromResponse(portfolio, "netWorth", false, true);
    for (const reportsDataKey in reportsDataForPortfolio) {
      if (
        reportsDataKey === chartTimeRange.LAST_YEAR_END ||
        reportsDataKey === chartTimeRange.ONE_YEAR_AGO ||
        reportsDataKey === "custodiansFundData" ||
        reportsDataKey === "tickerMcapList2" ||
        reportsDataKey === "tickerRegionList" ||
        reportsDataKey === "cryptoSectors" ||
        reportsDataKey === "stockSectors" ||
        reportsDataKey === "primary" ||
        reportsDataKey === "linkedPortfolioId" ||
        reportsDataKey == "directLinkedAssetCustodianId" ||
        reportsDataKey == "directLinkedDebtCustodianId" ||
        reportsDataKey == "directLinkedAssetCustodianName" ||
        reportsDataKey == "directLinkedDebtCustodianName" ||
        reportsDataKey == "directLinkedAssetCustodianSectionId" ||
        reportsDataKey == "directLinkedDebtCustodianSectionId" ||
        reportsDataKey === "directLinkedAssetCustodianOwnership" ||
        reportsDataKey === "directLinkedDebtCustodianOwnership" ||
        reportsDataKey === "linkedAssetCustodianOwnershipHistory" ||
        reportsDataKey === "linkedDebtCustodianOwnershipHistory" ||
        reportsDataKey === "linkedPortfolioId" ||
        reportsDataKey === "parentPortfolioId" ||
        reportsDataKey === "portfolioId" ||
        reportsDataKey === "linkedAssetCustodianIdInCurrentPortfolio" ||
        reportsDataKey === "linkedDebtCustodianIdInCurrentPortfolio"
      ) {
        continue;
      }
      const reportsSection = reportsDataForPortfolio[reportsDataKey];
      // filter only data points greater than or equal to networth chart start data
      if (reportsSection) {
        reportsSection.netWorth = (reportsSection.netWorth || []).filter(
          dataPoint => toCivilDateString(dataPoint.date) >= netWorthChartStartDateString
        );

        // sort the data points
        for (const dataPoint of reportsSection.netWorth) {
          dataPoint.asset = dataPoint.asset && dataPoint.asset.sort((a, b) => b.value - a.value);
        }
      }
    }
    const reportDataForPortfolio = filterNetWorthDataBasedOnPortfolioStartDate(reportsDataForPortfolio);
    for (const netWorthSectionKey in reportDataForPortfolio) {
      if (
        netWorthSectionKey !== "groupByMonth" &&
        netWorthSectionKey !== "groupByWeek" &&
        netWorthSectionKey !== "groupByYear" &&
        netWorthSectionKey !== "groupByDay"
      ) {
        continue;
      }
      const netWorthSection = reportDataForPortfolio[netWorthSectionKey];
      if (netWorthSection) {
        netWorthSection.netWorth = (netWorthSection.netWorth || [])
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .filter(removeDuplicateDataPointsFromnetWorthResponse(netWorthSectionKey, netWorthSection.netWorth));
      }
    }
    return reportDataForPortfolio;
  });

  dispatch(
    calculateRecapDataForPortfolio(
      reportData,
      portfolioId,
      forNormalView,
      isInitialLoad,
      reportToBeProcessedMap,
      isRecapPage,
      processRecapDataOnLocalUpdate,
      shouldCalculateRecapFromPortfolioResponse
    )
  );
};

export const RECAP_CATEGORY_TYPE_PERCENTAGE_ALLOCATION_ASSETS = "PercentageAllocationForAssets";
export const RECAP_CATEGORY_TYPE_PERCENTAGE_ALLOCATION_DEBTS = "PercentageAllocationForDebts";
export const RECAP_CATEGORY_TYPE_PERCENTAGE_ALLOCATION_TOTAL_INVESTABLE = "PercentageAllocationForTotalInvestable";
export const RECAP_CATEGORY_TYPE_INVESTABLE_TOTAL = "investableTotal";
export const RECAP_CATEGORY_TYPE_ARCHIVED = "Archived";
export const RECAP_CATEGORY_TYPE_ARCHIVED_ASSETS = "Archived Assets";
export const RECAP_CATEGORY_TYPE_ARCHIVED_DEBTS = "Archived Debts";
const RECAP_CATEGORY_TYPE_NETWORTH = "Net Worth";

export const RECAP_CELL_CATEGORY_TYPE_CURRENCY = "currency";
export const RECAP_CELL_CATEGORY_TYPE_PERCENTAGE = "percentage";
export const RECAP_CELL_CATEGORY_TYPE_TEXT = "text";
export const RECAP_CELL_CATEGORY_TYPE_DATE = "date";

export const RECAP_NETWORTH_OPTION_ID = "totals";
export const RECAP_PERCENTAGE_ALLOCATION_OPTION_ID = "percentageAllocation";

export const getColumnarGridColumnHeaderString = (date, timeRange) => {
  const month = date.getMonth();
  const year = date.getFullYear();
  if (timeRange === chartTimeRange.WEEKLY || timeRange === chartTimeRange.DAILY || timeRange === chartTimeRange.TODAY) {
    return `${date.getDate()} ${months[month]} ${year}`;
  } else if (timeRange === chartTimeRange.MONTHLY) {
    return `${date.getDate()} ${months[month]} ${year}`;
  } else if (timeRange === chartTimeRange.QUARTERLY) {
    return `${date.getDate()} ${months[month]} ${year}`;
  } else if (timeRange === chartTimeRange.YEARLY) {
    return `${date.getDate()} ${months[month]} ${year}`;
  }
};

const removeDuplicateDataPointsFromnetWorthResponse = (netWorthSectionKey, netWorth) => {
  // networth response will have more than one data point for a time range. remove the duplicates
  return function(dataPoint, index) {
    let isDuplicatePresent;
    if (index === 0) {
      return true;
    }
    if (netWorthSectionKey === "groupByDay") {
      isDuplicatePresent = false;
    }
    if (netWorthSectionKey === "groupByWeek") {
      const currentDataPointWeek = getDateInKuberaFormat(dataPoint.date).getDay();
      if (currentDataPointWeek !== 6 && index !== netWorth.length - 1) {
        isDuplicatePresent = true;
      }
    }
    if (netWorthSectionKey === "groupByMonth") {
      const currentDataPointMonth = getDateInKuberaFormat(dataPoint.date).getMonth();
      const nextDataPointMonth = getDateInKuberaFormat(netWorth[index - 1].date).getMonth();
      if (currentDataPointMonth === nextDataPointMonth) {
        // duplicates are present for same month
        isDuplicatePresent = true;
      }
    }
    if (netWorthSectionKey === "groupByYear") {
      const currentDataPointYear = getDateInKuberaFormat(dataPoint.date).getFullYear();
      const nextDataPointYear = getDateInKuberaFormat(netWorth[index - 1].date).getFullYear();
      if (currentDataPointYear === nextDataPointYear) {
        isDuplicatePresent = true;
      }
    }
    return !isDuplicatePresent;
  };
};

export const getRecapChartOptionFromId = id => {
  for (const key in recapChartOptions) {
    if (key && recapChartOptions[key].id === id) {
      return recapChartOptions[key];
    }
  }
  return null;
};

export const getChartLabelFromIdSelector = (state, id, shouldReturnLabel) => {
  try {
    const chartParams = parseParams(id);
    const chartName = chartParams.chart_name;
    const recapData = recapDataSelector(state);
    if (chartParams.chart_content === chartContent.CONNECTIVITY_WIDGET) {
      return "Connectivity Center";
    }
    if (
      chartParams.chart_content === chartContent.CONTENTS_GROUPED_BY_SHEETS_AND_SECTION ||
      chartParams.chart_content === chartContent.INVESTABLE_ASSETS_GROUPED_BY_SECTION ||
      chartParams.chart_content === chartContent.INVESTABLE_ASSETS_WITHOUT_CASH_GROUPED_BY_SECTION ||
      chartParams.chart_content === chartContent.INVESTABLE_ASSETS_WITHOUT_CASH_GROUPED_BY_SHEETS_AND_SECTION ||
      chartParams.chart_content === chartContent.ASSETS_GROUPED_BY_SECTIONS
    ) {
      return chartName;
    }

    const reportId = chartParams.report_id;
    const shouldCompareAgainstInvestableAssets = chartParams.should_compare_with_investable_assets === "true";
    const shouldCompareAgainstSheet = chartParams.should_compare_with_sheet === "true";
    const shouldCompareAgainstTotalAssetsOrDebts = chartParams.should_compare_with_total_assets_or_debts === "true";
    const reportParams = parseParams(reportId);
    const selectedChartOptions = reportParams.chart_option;
    let chartLabel;
    if (selectedChartOptions === recapChartOptions.SHEETS_AND_SECTIONS.id) {
      const reportName = recapReportNameSelector(state, reportId);
      if (chartParams.chart_content === "reports") {
        chartLabel = getComparisonReportsTabTitleSelector(
          state,
          reportName,
          reportParams.report_node_id,
          reportParams.report_path,
          selectedChartOptions,
          shouldCompareAgainstInvestableAssets,
          reportId,
          shouldReturnLabel,
          shouldCompareAgainstTotalAssetsOrDebts,
          shouldCompareAgainstSheet
        );
      } else if (chartParams.chart_content === "contents") {
        chartLabel = getContentsTabTitle(
          state,
          selectedChartOptions,
          reportParams.report_node_id,
          reportParams.report_path,
          reportName,
          reportId,
          shouldReturnLabel
        );
      } else {
        chartLabel = chartName;
      }
    } else if (
      selectedChartOptions === recapChartOptions.ASSETS_AND_CURRENCY.id &&
      reportParams.report_node_id === "Fiat Assets" &&
      chartParams.chart_style === chartStyle.DOUGHNUT
    ) {
      if (chartParams.chart_content === "contents") {
        chartLabel = checkIfAChartOptionHasNoDataToShow(
          recapChartOptions.CRYPTO.id,
          chartTimeRange.TODAY,
          recapData.data
        )
          ? "Assets x Currency"
          : "Fiat Assets x Currency";
      } else {
        chartLabel = checkIfAChartOptionHasNoDataToShow(
          recapChartOptions.CRYPTO.id,
          chartTimeRange.TODAY,
          recapData.data
        )
          ? "Assets to Total Assets"
          : "Fiat Assets to Total Assets";
      }
    } else if (selectedChartOptions === recapChartOptions.NETWORTH.id) {
      const reportName = recapReportNameSelector(state, reportId);
      if (chartParams.chart_content === "reports") {
        chartLabel = getComparisonReportsTabTitleSelector(
          state,
          reportName,
          reportParams.report_node_id,
          reportParams.report_path,
          selectedChartOptions,
          shouldCompareAgainstInvestableAssets,
          reportId,
          shouldReturnLabel,
          shouldCompareAgainstTotalAssetsOrDebts,
          shouldCompareAgainstSheet
        );
      } else {
        chartLabel = chartName;
      }
    } else {
      chartLabel = chartName;
    }

    return chartLabel;
  } catch (e) {
    console.log("e", e);
  }
};

window.recapWorker = {};

export const selectedRecapReportScopeSelector = state => ({
  timeRange: getRecapChartTimeRangeForPortfolio(state),
  reportIds: [getRecapChartOptionForPortfolio(state)]
});

const getChartAndTimeRangeToProcessSelector = (state, isInitialLoad, forPortfolioResponse, reportsToProcess) => {
  const reportToBeProcessedMap = new Map();
  if (!isInitialLoad && !forPortfolioResponse && reportsToProcess) {
    reportToBeProcessedMap.set(reportsToProcess.timeRange, reportsToProcess.reportIds);
    const todayReports = new Set([
      ...(reportToBeProcessedMap.get(chartTimeRange.TODAY) || []),
      ...Object.values(recapChartOptions).map(option => option.id)
    ]);
    if (!isMobileDevice) {
      todayReports.add("investable_by_sheets_and_sections");
      todayReports.add("investable_without_cash_by_sheets_and_sections");
    }
    reportToBeProcessedMap.set(chartTimeRange.TODAY, [...todayReports]);
    return reportToBeProcessedMap;
  }
  if (isInitialLoad) {
    let recapReportToProcessForInitialLoad = Object.values(recapChartOptions).map(option => option.id);
    if (!isMobileDevice) {
      recapReportToProcessForInitialLoad.push(
        "investable_by_sheets_and_sections",
        "investable_without_cash_by_sheets_and_sections"
      );
      const chartIds = window.kbStructuredClone(dashboardChartIdsSelector(state));
      let dashboardChartIds = chartIds.columns["column1"].chartIds.concat(chartIds.columns["column2"].chartIds);
      dashboardChartIds.forEach(chartId => {
        const parsedId = parseParams(chartId);
        const reportParams = parseParams(parsedId.report_id);
        if (parsedId.is_checked === "true" && reportParams.chart_option !== "null") {
          if (
            parsedId.chart_style === chartStyle.DOUGHNUT &&
            recapReportToProcessForInitialLoad.includes(reportParams.chart_option) === false
          ) {
            recapReportToProcessForInitialLoad.push(reportParams.chart_option);
          } else if (parsedId.chart_style === chartStyle.LINE) {
            if (reportToBeProcessedMap.has(chartTimeRange.DAILY)) {
              let dailyReports = reportToBeProcessedMap.get(chartTimeRange.DAILY);
              if (dailyReports.includes(reportParams.chart_option) === false) {
                dailyReports.push(reportParams.chart_option);
                reportToBeProcessedMap.set(chartTimeRange.DAILY, dailyReports);
              }
            } else {
              reportToBeProcessedMap.set(chartTimeRange.DAILY, [reportParams.chart_option]);
            }
            if (reportToBeProcessedMap.has(chartTimeRange.WEEKLY)) {
              let weeklyReports = reportToBeProcessedMap.get(chartTimeRange.WEEKLY);
              if (weeklyReports.includes(reportParams.chart_option) === false) {
                weeklyReports.push(reportParams.chart_option);
                reportToBeProcessedMap.set(chartTimeRange.WEEKLY, weeklyReports);
              }
            } else {
              reportToBeProcessedMap.set(chartTimeRange.WEEKLY, [reportParams.chart_option]);
            }
            if (reportToBeProcessedMap.has(chartTimeRange.MONTHLY)) {
              let monthlyReports = reportToBeProcessedMap.get(chartTimeRange.MONTHLY);
              if (monthlyReports.includes(reportParams.chart_option) === false) {
                monthlyReports.push(reportParams.chart_option);
                reportToBeProcessedMap.set(chartTimeRange.MONTHLY, monthlyReports);
              }
            } else {
              reportToBeProcessedMap.set(chartTimeRange.MONTHLY, [reportParams.chart_option]);
            }
            if (reportToBeProcessedMap.has(chartTimeRange.YEARLY)) {
              let yearlyReports = reportToBeProcessedMap.get(chartTimeRange.YEARLY);
              if (yearlyReports.includes(reportParams.chart_option) === false) {
                yearlyReports.push(reportParams.chart_option);
                reportToBeProcessedMap.set(chartTimeRange.YEARLY, yearlyReports);
              }
            } else {
              reportToBeProcessedMap.set(chartTimeRange.YEARLY, [reportParams.chart_option]);
            }
            if (reportToBeProcessedMap.has(chartTimeRange.QUARTERLY)) {
              let quarterlyReports = reportToBeProcessedMap.get(chartTimeRange.QUARTERLY);
              if (quarterlyReports.includes(reportParams.chart_option) === false) {
                quarterlyReports.push(reportParams.chart_option);
                reportToBeProcessedMap.set(chartTimeRange.QUARTERLY, quarterlyReports);
              }
            } else {
              reportToBeProcessedMap.set(chartTimeRange.QUARTERLY, [reportParams.chart_option]);
            }
          }
        }
      });
    }
    reportToBeProcessedMap.set(chartTimeRange.TODAY, recapReportToProcessForInitialLoad);
  } else if (forPortfolioResponse) {
    reportToBeProcessedMap.set(chartTimeRange.TODAY, Object.values(recapChartOptions).map(option => option.id));
  } else {
    const recapReportList = Object.values(recapChartOptions).map(option => option.id);
    reportToBeProcessedMap.set(chartTimeRange.DAILY, recapReportList);
    reportToBeProcessedMap.set(chartTimeRange.WEEKLY, recapReportList);
    reportToBeProcessedMap.set(chartTimeRange.MONTHLY, recapReportList);
    reportToBeProcessedMap.set(chartTimeRange.YEARLY, recapReportList);
    reportToBeProcessedMap.set(chartTimeRange.QUARTERLY, recapReportList);
    reportToBeProcessedMap.set(chartTimeRange.TODAY, [
      ...recapReportList,
      "investable_by_sheets_and_sections",
      "investable_without_cash_by_sheets_and_sections"
    ]);
  }
  return reportToBeProcessedMap;
};

let RecapWorkerClass = null;

export const setRecapWorkerClass = WorkerClass => {
  RecapWorkerClass = WorkerClass;
};

const createWorkerPool = poolSize => {
  const workers = [];
  for (let i = 0; i < poolSize; i++) {
    workers.push(new RecapWorkerClass());
  }
  return workers;
};

// Returns a pruned copy of `portfolios` containing only the fields the recap
// worker reads. Reduces postMessage clone size + worker memory by dropping
// planning rules, UI state, chart caches, permission metadata, etc. that the
// worker never touches. Does NOT mutate the original portfolios array.
const pickCustodianFieldsForRecapWorker = custodian => {
  if (!custodian) return custodian;
  return {
    id: custodian.id,
    name: custodian.name,
    sectionId: custodian.sectionId,
    tsModified: custodian.tsModified,
    linkType: custodian.linkType,
    type: custodian.type,
    tp: custodian.tp,
    sTp: custodian.sTp,
    vTId: custodian.vTId,
    acId: custodian.acId,
    clubbingName: custodian.clubbingName,
    isArchived: custodian.isArchived,
    hidden: custodian.hidden,
    cost: custodian.cost,
    cmtdCap: custodian.cmtdCap,
    irrType: custodian.irrType,
    linkedOwnership: custodian.linkedOwnership,
    portfolioId: custodian.portfolioId,
    taxDetails: custodian.taxDetails,
    value: custodian.value
  };
};

const pickSectionFieldsForRecapWorker = section => {
  if (!section) return section;
  return {
    id: section.id,
    sheetId: section.sheetId,
    name: section.name
  };
};

const pickSheetFieldsForRecapWorker = sheet => {
  if (!sheet) return sheet;
  return {
    id: sheet.id,
    category: sheet.category,
    name: sheet.name,
    portfolioId: sheet.portfolioId
  };
};

const pickPortfolioFieldsForRecapWorker = portfolios => {
  if (!Array.isArray(portfolios)) return portfolios;
  return portfolios.map(portfolio => {
    if (!portfolio) return portfolio;
    const details = portfolio.details;
    return {
      id: portfolio.id,
      name: portfolio.name,
      currency: portfolio.currency,
      primary: portfolio.primary,
      parentPortfolioId: portfolio.parentPortfolioId,
      linkedAssetCustodianIdInCurrentPortfolio: portfolio.linkedAssetCustodianIdInCurrentPortfolio,
      linkedDebtCustodianIdInCurrentPortfolio: portfolio.linkedDebtCustodianIdInCurrentPortfolio,
      directLinkedAssetCustodianId: portfolio.directLinkedAssetCustodianId,
      directLinkedDebtCustodianId: portfolio.directLinkedDebtCustodianId,
      directLinkedAssetCustodianOwnership: portfolio.directLinkedAssetCustodianOwnership,
      linkedAssetCustodianOwnershipHistory: portfolio.linkedAssetCustodianOwnershipHistory,
      linkedDebtCustodianOwnershipHistory: portfolio.linkedDebtCustodianOwnershipHistory,
      details: details
        ? {
            custodian: Array.isArray(details.custodian)
              ? details.custodian.map(pickCustodianFieldsForRecapWorker)
              : details.custodian,
            section: Array.isArray(details.section)
              ? details.section.map(pickSectionFieldsForRecapWorker)
              : details.section,
            sheet: Array.isArray(details.sheet) ? details.sheet.map(pickSheetFieldsForRecapWorker) : details.sheet
          }
        : details
    };
  });
};

const activeRecapWorkerPromises = new Map();
const getRecapWorkerKey = (portfolioId, isInitialLoad) => `${portfolioId}:${!!isInitialLoad}`;

export const calculateRecapDataWithWorkers = (
  recapRawData,
  portfolioId,
  forNormalView,
  isInitialLoad,
  reportToBeProcessedMap,
  processRecapDataOnLocalUpdate
) => (_, getState) => {
  const recapWorkerKey = getRecapWorkerKey(portfolioId, isInitialLoad);
  const portfolios = portfoliosSelector(getState());
  const portfoliosForWorker = pickPortfolioFieldsForRecapWorker(portfolios);

  const portfolioTreeData = portfolioTreeDataSelector(getState());
  const currentPortfolio = currentPortfolioSelector(getState());
  const supportedTickerIdMap = supportedTickerIdMapSelector(getState());
  const WORKER_CONSTS = getRecapWorkerConsts();
  const timeRangeForRecapReport = Array.from(reportToBeProcessedMap.keys());
  const workerPool = createWorkerPool(timeRangeForRecapReport.length);
  let activeWorkers = 0;
  let index = 0;
  let result = {};
  const poolStartTime = Date.now();
  console.log("[recap-debug] pool start", JSON.stringify({ timeRanges: timeRangeForRecapReport }));

  const poolPromise = new Promise(resolve => {
    workerPool.forEach(worker => {
      worker.addEventListener("message", async e => {
        if (e.data instanceof ArrayBuffer) {
          const { type, failedChunks, currency } = decodeArrayBufferToObject(e.data);

          if (type === UPDATE_RECAP) {
            failedChunks.forEach(chunk => {
              const decompressedData = decompressObject(chunk);
              result.currency = currency;
              result = merge(result, decompressedData);
            });
          }
        } else {
          const { type } = e.data;
          if (type === UPDATE_RECAP) {
            const { chunkKeys, currency, currentTimeRange, error } = e.data;
            if (chunkKeys) {
              //chunkKeysForAllTimeRanges = chunkKeysForAllTimeRanges.concat(chunkKeys);
              const decompressedData = await reconstructData(chunkKeys);
              if (!decompressedData) {
                captureError(getState(), `Recap chunk read failed for time range ${currentTimeRange}`);
              }
              result.currency = currency;
              result = merge(result, decompressedData);
            } else if (error) {
              console.log("recap worker error", e);
              captureError(getState(), error);
            }
          }
        }

        activeWorkers--;
        if (activeWorkers === 0) {
          console.log("[recap-debug] pool done", JSON.stringify({ totalMs: Date.now() - poolStartTime }));
          workerPool.forEach(w => {
            try {
              w.terminate();
            } catch (_) {}
          });
          activeRecapWorkerPromises.delete(recapWorkerKey);
          resolve(result);
        }
      });
      if (index < timeRangeForRecapReport.length) {
        const recapReportToProcess = reportToBeProcessedMap.get(timeRangeForRecapReport[index]);
        console.log(
          "[recap-debug] post",
          JSON.stringify({
            timeRange: timeRangeForRecapReport[index],
            activeWorkers: activeWorkers + 1
          })
        );
        worker.postMessage({
          type: UPDATE_RECAP,
          isMobileDevice,
          portfolios: portfoliosForWorker,
          currentPortfolioId: currentPortfolio.id,
          recapRawData,
          supportedTickerIdMap,
          WORKER_CONSTS,
          recapReportToProcess,
          timeRangeForRecapReport: [timeRangeForRecapReport[index]],
          forNormalView,
          isInitialLoad,
          isLastWorker: index === timeRangeForRecapReport.length - 1,
          processRecapDataOnLocalUpdate,
          portfolioTreeData
        });
        index++;
        activeWorkers++;
      }
    });
  });
  activeRecapWorkerPromises.set(recapWorkerKey, poolPromise);
  return poolPromise;
};

const mergeRecapDataWithoutDuplicates = (objValue, srcValue, key) => {
  // Handle the 'rows' array specifically
  if (key === "rows" && Array.isArray(objValue) && Array.isArray(srcValue)) {
    const mergedRows = [...objValue];
    srcValue.forEach(srcRow => {
      if (srcRow && srcRow.id) {
        const existingRowIndex = mergedRows?.findIndex(row => row?.id === srcRow.id);

        if (existingRowIndex >= 0) {
          // Replace existing row with new row (or merge properties if needed)
          mergedRows[existingRowIndex] = srcRow;
        } else {
          // Add new row if ID doesn't exist
          mergedRows.push(srcRow);
        }
      } else {
        // Add row without ID (shouldn't happen but handle it)
        mergedRows.push(srcRow);
      }
    });

    return mergedRows;
  }

  // Handle the 'investableAssetsList' array - take latest from worker
  if (
    (key === "investableAssetsList" || key === "taxOnUnrealisedGainsList" || key === "unfundedCustodiansList") &&
    Array.isArray(srcValue)
  ) {
    return srcValue;
  }
  const recapReportList = Object.values(recapChartOptions).map(option => option.id);
  recapReportList.push("investable_by_sheets_and_sections", "investable_without_cash_by_sheets_and_sections");
  if (recapReportList.includes(key)) {
    return srcValue;
  }

  // Let lodash handle default merging for other cases
  return undefined;
};

export const calculateRecapDataForPortfolio = (
  recapRawData,
  portfolioId,
  forNormalView,
  isInitialLoad,
  reportToBeProcessedMap,
  isRecapPage = false,
  processRecapDataOnLocalUpdate = false,
  shouldCalculateRecapFromPortfolioResponse = false
) => {
  return async (dispatch, getState) => {
    // console.log("raw data", JSON.stringify(recapRawData));
    const portfolioCurrency = currentPortfolioCurrencySelector(getState());
    const recapDataFromWorker = await dispatch(
      calculateRecapDataWithWorkers(
        recapRawData,
        portfolioId,
        forNormalView,
        isInitialLoad,
        reportToBeProcessedMap,
        processRecapDataOnLocalUpdate
      )
    );
    // Read the existing entry only after the worker finishes: hydration or a
    // concurrent calculation may have written this portfolio's entry mid-await,
    // and merging over a pre-await snapshot would discard that data.
    const existingRecapData = recapDataForPortfolioSelector(getState(), portfolioId);
    const currencyForCalculatedPortfolio = portfolioSelector(getState(), portfolioId)?.currency || portfolioCurrency;
    dispatch(
      fetchRecapDataSuccessAction(portfolioId, {
        recapData: mergeWith({}, existingRecapData?.data, recapDataFromWorker, mergeRecapDataWithoutDuplicates),
        currency:
          (shouldCalculateRecapFromPortfolioResponse || !recapRawData
            ? currencyForCalculatedPortfolio
            : recapRawData.portfolios?.find(portfolio => portfolio.portfolioId === portfolioId)?.groupByDay
                ?.currency) || currencyForCalculatedPortfolio,
        isRecapPage
      })
    );
  };
};

export const fetchPercentageAllocationData = (chartOption, timeRange) => {
  return (dispatch, getState) => {
    const recapData = recapDataSelector(getState());
    const totalsForSelectedOption = recapData?.data?.[timeRange]?.[chartOption]?.totals;
    if (!totalsForSelectedOption) {
      return;
    }
    dispatch(fetchPercentageAllocationDataPendingAction(true));
    const recapTotalsDataForSelectedOption = window.kbStructuredClone(totalsForSelectedOption);
    const percentageAllocationData =
      chartOption === recapChartOptions.SHEETS_AND_SECTIONS.id
        ? getPercentageAllocationDataForSheetsAndSectionsReports(recapTotalsDataForSelectedOption)
        : getPercentageAllocationDataForAReport(recapTotalsDataForSelectedOption, chartOption);
    recapData.data[timeRange][chartOption][recapChartTypes.PERCENTAGE_ALLOCATION] = percentageAllocationData;

    const { portfolioId } = currentPortfolioSelector(getState());

    dispatch(
      fetchRecapDataSuccessAction(portfolioId, {
        recapData: recapData
      })
    );
  };
};

const getPercentageAllocationDataForSheetsAndSectionsReports = totalsData => {
  for (const [key, category] of Object.entries(totalsData)) {
    const percentageAllocationCategory = { ...category };
    if (key === RECAP_CATEGORY_TYPE_NETWORTH) {
      continue;
    }
    const sheets = category.sheets.map((sheet, sheetIndex) => {
      const percentageAllocationSheet = { ...sheet };
      const values = sheet.values.map((cellValue, cellIndex) => {
        const sheetValue = (totalsData[key].values[cellIndex] && totalsData[key].values[cellIndex].value) || 0;
        return {
          ...cellValue,
          value: getPercentageValue(cellValue.value, sheetValue, false, 2),
          actualValue: getPercentageValueForRecap(cellValue.value, sheetValue)
        };
      });
      percentageAllocationSheet.values = values;
      const sections = sheet.sections.map((section, sectionIndex) => {
        const percentageAllocationSection = { ...section };
        const values = section.values.map((cellValue, cellIndex) => {
          const sectionValue =
            (totalsData[key].sheets[sheetIndex].values[cellIndex] &&
              totalsData[key].sheets[sheetIndex].values[cellIndex].value) ||
            0;
          return {
            ...cellValue,
            value: getPercentageValue(cellValue.value, sectionValue, false, 2),
            actualValue: getPercentageValueForRecap(cellValue.value, sectionValue)
          };
        });
        percentageAllocationSection.values = values;
        const rows = section.rows.map((row, rowIndex) => {
          const percentageAllocationRow = { ...row };
          const values = row
            ? row.values.map((cellValue, cellIndex) => {
                const rowValue =
                  (totalsData[key].sheets[sheetIndex].sections[sectionIndex].values[cellIndex] &&
                    totalsData[key].sheets[sheetIndex].sections[sectionIndex].values[cellIndex].value) ||
                  0;
                let value;
                if (cellValue.value && rowValue) {
                  value = getPercentageValue(cellValue.value, rowValue, false, 2);
                } else {
                  value = 0;
                }

                return {
                  ...cellValue,
                  value: value,
                  actualValue: getPercentageValueForRecap(cellValue.value, rowValue)
                };
              })
            : [];
          percentageAllocationRow.values = values;
          return percentageAllocationRow;
        });
        percentageAllocationSection.rows = rows;
        return percentageAllocationSection;
      });
      percentageAllocationSheet.sections = sections;
      return percentageAllocationSheet;
    });
    percentageAllocationCategory.sheets = sheets;
    totalsData[key] = percentageAllocationCategory;
  }
  return totalsData;
};

const getPercentageAllocationDataForAReport = (totalsData, chartOption) => {
  for (const [key, rows] of Object.entries(totalsData)) {
    if (key === RECAP_CATEGORY_TYPE_NETWORTH) {
      continue;
    }
    totalsData[key] = rows.map(row => {
      if (!shouldConvertRowValuesToPercentages(row, chartOption)) {
        return row;
      } else {
        const rowObj = { ...row };
        rowObj.values = row.values.map((cellValue, index) => {
          const totalValueOfParent =
            row.type === "header" &&
            (chartOption !== recapChartOptions.CASH_ON_HAND.id && chartOption !== recapChartOptions.NETWORTH.id)
              ? getParentValueForACustodian({ ...totalsData }, index, chartOption)
              : (totalsData[key][0].values[index] && totalsData[key][0].values[index].value) || 0;
          return {
            ...cellValue,
            value: totalValueOfParent === 0 ? 0 : getPercentageValue(cellValue.value, totalValueOfParent, false, 2),
            actualValue: totalValueOfParent === 0 ? 0 : getPercentageValueForRecap(cellValue.value, totalValueOfParent)
          };
        });
        return rowObj;
      }
    });
  }
  return totalsData;
};

const shouldConvertRowValuesToPercentages = (row, chartOption) => {
  if (
    row.type === "AssetsTotal" ||
    row.type === "stocksTotal" ||
    row.type === "investableAssetsTotal" ||
    row.type === "cryptoTotal" ||
    row.type === "brokeragesTotal" ||
    row.type === "taxableAssetsTotal" ||
    (row.type === "header" &&
      (chartOption === recapChartOptions.CASH_ON_HAND.id || chartOption === recapChartOptions.NETWORTH.id))
  ) {
    return false;
  }
  return true;
};

const getParentValueForACustodian = (totalsData, index, chartOption) => {
  if (chartOption === recapChartOptions.ASSET_CLASSES.id) {
    return (totalsData.Assets[0].values[index] && totalsData.Assets[0].values[index].value) || 0;
  }
  if (chartOption === recapChartOptions.ASSETS_AND_CURRENCY.id) {
    return (
      (totalsData[RECAP_CATEGORY_TYPE_FIAT_ASSET][0].values[index] &&
        totalsData[RECAP_CATEGORY_TYPE_FIAT_ASSET][0].values[index].value) ||
      0
    );
  }
  if (
    chartOption === recapChartOptions.STOCKS_AND_SECTOR.id ||
    chartOption === recapChartOptions.STOCKS_AND_MARKETCAP.id
  ) {
    return totalsData["Stocks"][0].values[index].value;
  }
  if (chartOption === recapChartOptions.STOCKS_AND_GEOGRAPHY.id) {
    return totalsData["Stocks"][0].values[index].value;
  }
  if (chartOption === recapChartOptions.CRYPTO.id) {
    return totalsData.Crypto[0].values[index].value;
  }
  if (chartOption === recapChartOptions.INVESTABLE.id) {
    return totalsData["Investable Assets"][0].values[index].value;
  }
  if (chartOption === recapChartOptions.NETWORTH.id) {
    return totalsData["Net Worth"][0].values[index].value;
  }
  if (chartOption === recapChartOptions.BROKERAGES.id) {
    return (totalsData["Brokerages"][0].values[index] && totalsData["Brokerages"][0].values[index].value) || 0;
  }
  if (chartOption === recapChartOptions.INVESTABLE_WITHOUT_CASH.id) {
    return totalsData["Investable Assets ex Cash"][0].values[index].value;
  }
  if (chartOption === recapChartOptions.TAXABLE_ASSETS.id) {
    return totalsData["Assets"][0].values[index].value;
  }
};

export const updateCustodianBulk = (custodians, limit = 50) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const currentPortfolio = currentPortfolioSelector(getState());
      const updateList = list => {
        for (const item of list) {
          const custodian = custodianSelector(getState(), item.id);
          if (custodian) {
            // While updating a custodian if value field is non-null ensure that the valueTickerId
            // and valueExchangeRate fields are also set correctly
            if (item.value != null) {
              if (item.valueTickerId == null) {
                item.valueTickerId = custodian.valueTickerId;
              }
              if (item.valueExchangeRate == null) {
                const rate = getExchangeRate(
                  getState(),
                  getTickerUsingId(getState(), item.valueTickerId).shortName,
                  currentPortfolio.currency
                );
                item.valueExchangeRate = getExchangeRateDetails(
                  getTickerUsingShortName(getState(), currentPortfolio.currency).id,
                  rate
                );
              }
            }
          }
        }

        const request = idempotentId =>
          ApiClient.updateCustodianBulk(idempotentId, list, custodians.cancelToken).catch(_ => {});
        const syncItem = new SyncItem(
          SyncItemType.BULK_UPDATE,
          currentPortfolio.id + "bulk_update",
          request,
          0,
          true,
          apiData => {
            if (!apiData?.payload) return;
            const updatedCustodians = apiData.payload.custodian;
            var custodiansWithUpdatedIrr = [];
            for (const custodian of updatedCustodians) {
              const currentCustodian = custodianSelector(getState(), custodian.id);
              if (getIrrValue(currentCustodian?.irr) !== getIrrValue(custodian?.irr)) {
                custodiansWithUpdatedIrr.push(custodian);
              }
            }
            dispatch(updateSectionIrr(custodiansWithUpdatedIrr.map(item => item.sectionId)));
            resolve(apiData.payload);
          }
        );
        dispatch(enqueueItem(syncItem));
      };

      dispatch(changeCustodianSortKey(custodians.hashMap));
      if (
        (!custodians.hasOrderChanged && custodians.hasOrderChanged !== false) ||
        custodians.hasOrderChanged === true
      ) {
        requestIdleCallback(() => {
          for (let startIndex = 0, endIndex = limit; ; startIndex += limit, endIndex += limit) {
            const currentList = custodians.list.slice(startIndex, endIndex);
            if (currentList.length <= 0) {
              break;
            }
            updateList(currentList);
          }
        });
      }
    });
  };
};

export const updateSectionBulk = (sections, isLocal = false) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const currentPortfolio = currentPortfolioSelector(getState());
      dispatch(updateSectionAction({ [currentPortfolio.id]: sections.list }));
      dispatch(changeSectionSortKey(sections.hashMap));
      dispatch(updateDashboardAction(sections.list.map(item => item.id)));
      if (!isLocal) {
        const request = idempotentId => ApiClient.updateSectionBulk(idempotentId, sections.list);
        const syncItem = new SyncItem(SyncItemType.BULK_UPDATE, currentPortfolio.id, request, 0, true, apiData => {
          resolve(apiData.payload);
        });
        dispatch(enqueueItem(syncItem));
      }
    });
  };
};

export function mergeSortSection(array, prop, index, getValue, isDecending) {
  if (array.length <= 1) return { sortedArray: array, hasOrderChanged: false };
  const middleIdx = Math.floor(array.length / 2);
  const leftHalf = array.slice(0, middleIdx);
  const rightHalf = array.slice(middleIdx);

  const {
    sortedArray: leftSorted,
    hasOrderChanged: leftHasOrderChanged,
    idAndSortKeyHash: leftIdAndSortKeyHash,
    idAndSortKeyList: leftIdAndSortKeyList = []
  } = mergeSortSection(leftHalf, prop, index, getValue, isDecending);
  const {
    sortedArray: rightSorted,
    hasOrderChanged: rightHasOrderChanged,
    idAndSortKeyHash: rightIdAndSortKeyHash,
    idAndSortKeyList: rightIdAndSortKeyList = []
  } = mergeSortSection(rightHalf, prop, index, getValue, isDecending);
  return mergeSortedArrays({
    leftHalf: leftSorted,
    rightHalf: rightSorted,
    prop,
    index,
    getValue,
    isDecending,
    hasOrderChanged: leftHasOrderChanged || rightHasOrderChanged,
    idAndSortKeyHash: { ...leftIdAndSortKeyHash, ...rightIdAndSortKeyHash },
    idAndSortKeyList: [...leftIdAndSortKeyList, ...rightIdAndSortKeyList]
  });
}

export function mergeSortedArrays({
  leftHalf,
  rightHalf,
  prop,
  index,
  getValue = item => item.value,
  isDecending = false,
  hasOrderChanged: hasOrderChangedParam = false,
  idAndSortKeyHash: idAndSortKeyHashParam = {},
  idAndSortKeyList: idAndSortKeyListParam = []
}) {
  const sortedArray = new Array(leftHalf.length + rightHalf.length);
  let k = 0;
  let i = 0;
  let j = 0;
  let hasOrderChanged = false;
  const idAndSortKeyHash = idAndSortKeyHashParam;
  const idAndSortKeyList = idAndSortKeyListParam;

  const setSortRelatedValues = () => {
    sortedArray[k].sortKey = setSortKey(sortedArray, k);
    idAndSortKeyList[k] = { id: sortedArray[k].id, sortKey: sortedArray[k].sortKey };
    idAndSortKeyHash[sortedArray[k].id] = sortedArray[k].sortKey;
    k++;
  };

  while (i < leftHalf.length && j < rightHalf.length) {
    const leftCell = leftHalf[i][prop][index];
    const rightCell = rightHalf[j][prop][index];
    const leftVal = getValue(leftCell);
    const rightVal = getValue(rightCell);

    if (leftVal >= rightVal && isDecending) {
      sortedArray[k] = leftHalf[i++];
    } else if (leftVal <= rightVal && !isDecending) {
      sortedArray[k] = leftHalf[i++];
    } else {
      hasOrderChanged = true;
      sortedArray[k] = rightHalf[j++];
    }

    setSortRelatedValues();
  }

  while (i < leftHalf.length) {
    sortedArray[k] = leftHalf[i++];

    setSortRelatedValues();
  }
  while (j < rightHalf.length) {
    sortedArray[k] = rightHalf[j++];

    setSortRelatedValues();
  }

  return {
    sortedArray,
    idAndSortKeyList,
    idAndSortKeyHash,
    hasOrderChanged: hasOrderChangedParam || hasOrderChanged
  };
}

export function changeCustodianSortKey(custodianSortArray) {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());

    if (!currentPortfolio) {
      return;
    }

    const currentPortfolioCustodians = currentPortfolio.details.custodian;
    const custodianIds = [];
    const custodiansToBulkUpdate = [];

    currentPortfolioCustodians.forEach(custodian => {
      if (custodianSortArray && custodianSortArray[custodian.id]) {
        custodian.sortKey = custodianSortArray[custodian.id];
        custodiansToBulkUpdate.push(custodian);
        custodianIds.push(custodian.id);
      }
    });

    dispatch(updateCustodianInBulkAction(currentPortfolio.id, custodiansToBulkUpdate));
    sortPortfolio(currentPortfolio);
    dispatch(updateDashboardAction(custodianIds));
  };
}

export function changeSectionSortKey(sectionsSortMap) {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioSections = currentPortfolio.details.section;
    const sectionIds = [];

    const portfolioSectionsMap = {};
    currentPortfolioSections.forEach(section => {
      if (sectionsSortMap[section.id]) {
        section.sortKey = sectionsSortMap[section.id];
        if (!portfolioSectionsMap[currentPortfolio.id]) {
          portfolioSectionsMap[currentPortfolio.id] = [];
        }
        portfolioSectionsMap[currentPortfolio.id].push(section);
        sectionIds.push(section.id);
      }
    });

    if (sectionIds.length > 0) {
      dispatch(updateSectionAction(portfolioSectionsMap));
      sortPortfolio(currentPortfolio);
      dispatch(updateDashboardAction(sectionIds));
    }
  };
}

export function updateCustodianLocal(custodian) {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioCustodians = currentPortfolio.details.custodian;

    const custodianFound =
      currentPortfolioCustodians.find(eachCustodian => eachCustodian.id === custodian.id) || custodian;
    if (!custodianFound || (custodian.tsModified && custodian.tsModified <= custodianFound.tsModified)) return;

    Object.keys(custodian).forEach(key => {
      custodianFound[key] = custodian[key];
    });
    custodianFound.tsModified = parseInt(new Date().getTime() / 1000);
    dispatch(updateCustodianInBulkAction(currentPortfolio.id, [custodianFound]));
    dispatch(updateDashboardAction([custodianFound.id]));
  };
}

export function expandHoldingsForCustodian(custodianId, onSuccess, onError, shouldShowTip = false, sectionName = null) {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const custodian = custodianSelector(getState(), custodianId);
    const custodianSection = sectionSelector(getState(), custodian.sectionId);
    const sectionsInSheet = currentPortfolio.details.section.filter(
      section => section.sheetId === custodianSection.sheetId
    );
    sectionsInSheet.sort((a, b) => a.sortKey.localeCompare(b.sortKey));

    const custodianSectionIndex = sectionsInSheet.findIndex(section => section.id === custodian.sectionId);
    if (custodianSectionIndex === -1) {
      onError();
      return;
    }

    const sortKeyBefore = custodianSection.sortKey;
    const sortKeyAfter =
      custodianSectionIndex + 1 === sectionsInSheet.length ? null : sectionsInSheet[custodianSectionIndex + 1].sortKey;
    const holdingsSectionSortKey = getSortKeyBetween(sortKeyBefore, sortKeyAfter);
    const holdingsSectionId = getUuid();
    const holdingsSection = {
      id: holdingsSectionId,
      name: sectionName || custodian.name,
      sheetId: custodianSection.sheetId,
      sortKey: holdingsSectionSortKey,
      expanded: 1
    };
    holdingsSection.sectionId = holdingsSection.id;
    ApiClient.expandCustodianHoldings(getUuid(), custodianSection.id, custodianId, holdingsSection).then(
      apiData => {
        custodian.hidden = 1;
        dispatch(updateCustodianInBulkAction(currentPortfolio.id, [custodian]));
        dispatch(updateDashboardAction([custodian.id]));
        const holdingsCustodians = apiData.payload;
        dispatch(insertCustodianInBulkAction(currentPortfolio.id, holdingsCustodians));
        if (holdingsSection.id !== custodianSection.id) {
          dispatch(insertSectionAction(currentPortfolio.id, holdingsSection));
        }

        const userPrefereces = userPreferencesSelector(getState());
        if (
          shouldShowTip === true &&
          !userPrefereces.showRevertHoldingsTipForCustodianId === true &&
          userPrefereces.isRevertHoldingsTipShown === false
        ) {
          const children = holdingsCustodians.filter(item => !item.parentId === false);
          if (children.length > 0) {
            dispatch(updateUserPreferences({ showRevertHoldingsTipForCustodianId: children[0].id }));
          }
        }

        // If the original section became empty after the holdings moved
        // insert 3 dummy rows so that its not empty
        const custodiansInOriginalSection = currentPortfolio.details.custodian
          .filter(item => item.sectionId === custodian.sectionId)
          .filter(item => item.hidden !== 1);
        if (custodiansInOriginalSection.length === 0) {
          let custodians = [];
          for (var i = 0; i < 3; i++) {
            custodians.push({
              id: getUuid(),
              sectionId: custodian.sectionId,
              sortKey: `${i + 1}`
            });
          }
          dispatch(insertCustodianInBulkAction(currentPortfolio.id, custodians));
        }
        dispatch(updateDashboardAction([holdingsSection.id, custodian.sectionId]));

        var unknownTickerIds = new Set();
        for (const item of holdingsCustodians) {
          if (getTickerUsingId(getState(), item.valueTickerId).shortName === UNKNOWN_TICKER_SHORT_NAME) {
            unknownTickerIds.add(item.valueTickerId);
          }
        }

        if (unknownTickerIds.size === 0) {
          onSuccess(holdingsSection.id);
        } else {
          dispatch(
            getTickersForIds(
              Array.from(unknownTickerIds),
              () => {
                onSuccess(holdingsSection.id);
              },
              apiError => {
                onSuccess(holdingsSection.id);
              }
            )
          );
        }
      },
      apiError => {
        onError(apiError);
      }
    );
  };
}

export function deleteChildrenForCustodian(custodianId) {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const custodian = custodianSelector(getState(), custodianId);

    const parentId = custodian.parentId || custodianId;

    const custodiansWithSameParentId = custodiansWithSameParentIdSelector(getState(), currentPortfolio.id, parentId);

    if (custodiansWithSameParentId.length === 0) {
      return;
    }

    const parentCustodian = custodianSelector(getState(), parentId);

    for (const item of custodiansWithSameParentId) {
      dispatch(deleteCustodianAction(currentPortfolio.id, item.id));
    }

    dispatch(
      updateCustodian(
        false,
        parentCustodian.id,
        {
          sectionId: custodiansWithSameParentId[0].sectionId,
          sortKey: custodiansWithSameParentId[0].sortKey,
          hidden: 0
        },
        false,
        null,
        false
      )
    );
    sortPortfolio(currentPortfolio);
    dispatch(updateDashboardAction([parentCustodian.id]));
  };
}

export function collapseHoldingsForCustodian(custodianId, onSuccess, onError) {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const custodian = custodianSelector(getState(), custodianId);
    const custodiansWithSameParentId = custodiansWithSameParentIdSelector(
      getState(),
      currentPortfolio.id,
      custodian.parentId
    );
    const parentCustodian = custodianSelector(getState(), custodian.parentId);

    for (const item of custodiansWithSameParentId) {
      item.hidden = 1;
    }
    dispatch(updateCustodianInBulkAction(currentPortfolio.id, custodiansWithSameParentId));

    const parentDestinationSectionId = custodian.sectionId;

    dispatch(
      updateCustodian(false, parentCustodian.id, {
        sectionId: parentDestinationSectionId,
        sortKey: custodian.sortKey,
        hidden: 0
      })
    );
    sortPortfolio(currentPortfolio);
    dispatch(updateDashboardAction([]));
    onSuccess(parentDestinationSectionId);

    ApiClient.collapseCustodianHoldings(
      getUuid(),
      parentCustodian.sectionId,
      parentCustodian.id,
      parentDestinationSectionId
    ).then(apiData => {}, apiError => {});
  };
}

export const validatePhone = phone => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      if (!(phone || "").trim()) {
        resolve({
          valid: false,
          empty: true
        });
      } else {
        ApiClient.validatePhone(getUuid(), phone)
          .then(apiData => {
            resolve({
              valid: apiData && apiData.payload && apiData.payload.valid,
              empty: false
            });
          })
          .catch(() => {
            resolve({
              valid: false,
              empty: false
            });
          });
      }
    });
  };
};

// localOnly=true updates the parent value in the store (and, via the details
// reducer, details.info) without a server write — for the optimistic holding
// archive, where the deferred archive sync persists the change server-side.
export const updateParentCustodianValueWithHoldingsTotal = (parentId, localOnly = false) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const parent = custodianSelector(getState(), parentId);
    const children = custodiansWithSameParentIdSelector(getState(), currentPortfolio.id, parentId);
    var total = 0;

    for (const child of children) {
      total += convertCurrency(
        getState(),
        child.value,
        getTickerUsingId(getState(), child.valueTickerId).shortName,
        getTickerUsingId(getState(), parent.valueTickerId).shortName
      );
    }

    if (parent.value !== total) {
      if (localOnly === true) {
        dispatch(
          updateCustodianInBulkAction(
            currentPortfolio.id,
            [{ id: parentId, value: total, tsModified: Math.floor(new Date().getTime() / 1000) }],
            true,
            null,
            ["value", "tsModified"]
          )
        );
      } else {
        dispatch(updateCustodian(false, parentId, { value: total }, true));
      }
    }
  };
};

export const createProof = (portfolioId, proofData, onSuccess = () => null, onError = () => null) => {
  return (dispatch, getState) => {
    ApiClient.createProof(getUuid(), portfolioId, proofData)
      .then(apiData => {
        dispatch(upsertProofAction(portfolioId, apiData.payload));
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        captureError(getState(), apiError);
        onError(apiError);
      });
  };
};

export const updateProof = (portfolioId, proofId, proofData, onSuccess = () => null, onError = () => null) => {
  return (dispatch, getState) => {
    ApiClient.updateProof(getUuid(), portfolioId, proofId, proofData)
      .then(apiData => {
        dispatch(upsertProofAction(portfolioId, apiData.payload));
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        if (apiError.errorCode === apiErrorCodes.CANCELLED) {
          return;
        }
        captureError(getState(), apiError);
        onError(apiError);
      });
  };
};

export const listProofs = (portfolioId, onSuccess, onError) => {
  return (dispatch, getState) => {
    ApiClient.listProofs(getUuid(), portfolioId)
      .then(apiData => {
        dispatch(setProofsAction(portfolioId, apiData.payload || []));
        if (onSuccess) onSuccess(apiData.payload);
      })
      .catch(apiError => {
        captureError(getState(), apiError);
        if (onError) onError(apiError);
      });
  };
};

export const deleteProof = (portfolioId, proofId, onSuccess, onError) => {
  return (dispatch, getState) => {
    ApiClient.deleteProof(getUuid(), portfolioId, proofId)
      .then(apiData => {
        dispatch(removeProofAction(portfolioId, proofId));
        if (onSuccess) onSuccess(apiData.payload);
      })
      .catch(apiError => {
        captureError(getState(), apiError);
        if (onError) onError(apiError);
      });
  };
};

export const fetchProofConfig = (proofId, onSuccess, onError) => {
  return (dispatch, getState) => {
    runShareChallenge(PROOF_CHALLENGE_ACTION, (recaptchaToken, type) =>
      ApiClient.getProofConfig(getUuid(), proofId, recaptchaToken, type)
    )
      .then(apiData => {
        if (onSuccess) onSuccess(apiData.payload);
      })
      .catch(apiError => {
        captureError(getState(), apiError);
        if (onError) onError(apiError);
      });
  };
};

export const fetchProofByToken = (proofId, passcode, onSuccess, onError) => {
  return (dispatch, getState) => {
    ApiClient.getProofByToken(getUuid(), proofId, passcode)
      .then(apiData => {
        if (onSuccess) onSuccess(apiData.payload);
      })
      .catch(apiError => {
        captureError(getState(), apiError);
        if (onError) onError(apiError);
      });
  };
};

export const fetchDownloadUrl = (proofId, passcode, onSuccess, onError) => {
  return (dispatch, getState) => {
    const downloadMethod = isAppInProofViewMode() ? "getDownloadUrlForViewMode" : "getDownloadUrl";
    const thirdParam = isAppInProofViewMode() ? passcode : currentPortfolioSelector(getState()).id;
    ApiClient[downloadMethod](getUuid(), proofId, thirdParam)
      .then(apiData => {
        if (onSuccess) onSuccess(apiData.payload);
      })
      .catch(apiError => {
        captureError(getState(), apiError);
        if (onError) onError(apiError);
      });
  };
};

export const createPortfolioShareLink = (portfolio, linkDetails, onSuccess, onError) => {
  return (dispatch, getState) => {
    ApiClient.createPortfolioShareLink(getUuid(), portfolio.id, linkDetails)
      .then(apiData => {
        const linkDetails = apiData.payload;
        portfolio.details.share.unshift(linkDetails);
        dispatch(updatePortfolioAction(portfolio));

        onSuccess();
      })
      .catch(apiError => {
        captureError(getState(), apiError);
        onError(apiError);
      });
  };
};

export const updatePortfolioShareLink = (portfolio, linkDetails, onSuccess = null, onError = null) => {
  return dispatch => {
    const linkIndex = portfolio.details.share.findIndex(item => item.id === linkDetails.id);
    if (linkIndex !== -1) {
      portfolio.details.share[linkIndex] = linkDetails;
    }
    dispatch(updatePortfolioAction({ ...portfolio }));

    ApiClient.updatePortfolioShareLink(getUuid(), portfolio.id, linkDetails.id, linkDetails)
      .then(apiData => {
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch(apiError => {
        if (onError) {
          onError(apiError);
        }
      });
  };
};

export const deletePortfolioShareLink = (portfolio, linkId, onSuccess = null, onError = null) => {
  return dispatch => {
    portfolio.details.share = portfolio.details.share.filter(item => item.id !== linkId);
    dispatch(updatePortfolioAction({ ...portfolio }));

    ApiClient.deletePortfolioShareLink(getUuid(), portfolio.id, linkId)
      .then(apiData => {
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch(apiError => {
        if (onError) {
          onError(apiError);
        }
      });
  };
};

export const getUpdatedIrr = (portfolioId = null) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    if (!portfolioId === true) {
      if (!currentPortfolio === true) {
        return;
      }
      portfolioId = currentPortfolio.id;
    }

    const maxRetry = 3;
    var retryCount = 0;

    const fetchUpdatedIrr = (error = null) => {
      retryCount++;
      if (retryCount > maxRetry) {
        return;
      }

      const sectionsTobeUpdated = [];
      for (const section of currentPortfolio.details.section) {
        if (!section.irr === true) {
          const sectionCustodians = sectionCustodiansSelector(
            getState(),
            currentPortfolio.id,
            section.id,
            false,
            true
          ).filter(item => isCustodianEmpty(item) === false);

          if (sectionCustodians.length === 0) {
            continue;
          }

          const anyCustodianHasIrr = sectionCustodians.find(
            item => isCustodianEmpty(item) === false && !!item.irr === true
          );
          if (anyCustodianHasIrr) {
            sectionsTobeUpdated.push(section.id);
          }
        } else {
          sectionsTobeUpdated.push(section.id);
        }
      }
      dispatch(updateSectionIrr(sectionsTobeUpdated));

      ApiClient.getUpdatedIrr(getUuid(), portfolioId)
        .then(apiData => {
          if (!apiData === true) {
            return;
          }

          const updatedCustodians = apiData.payload;
          if (updatedCustodians.length === 0) {
            return;
          }

          var custodiansWithUpdatedIrr = [];
          for (const custodian of updatedCustodians) {
            const currentCustodian = custodianSelector(getState(), custodian.id, currentPortfolio.id);
            if (
              !currentCustodian === false &&
              !custodian === false &&
              getIrrValue(currentCustodian.irr) !== getIrrValue(custodian.irr)
            ) {
              custodiansWithUpdatedIrr.push(custodian);
            }
          }
          dispatch(updateSectionIrr(custodiansWithUpdatedIrr.map(item => item.sectionId)));

          const updateCustodianIds = updatedCustodians.map(custodian => custodian.id);
          dispatch(updateCustodianInBulkAction(portfolioId, updatedCustodians, false, undefined, ["irr", "irrType"]));
          dispatch(updateDashboardAction(updateCustodianIds));

          if (isAppInViewMode() || isMobileDevice || currentPortfolio.write === 0) {
            ApiClient.getSectionIrr(getUuid(), currentPortfolio.id)
              .then(apiData => {
                if (!apiData === true) {
                  return;
                }

                const udpatedSections = apiData.payload;
                if (udpatedSections.length > 0) {
                  const portfolioSectionsMap = {};
                  for (const section of udpatedSections) {
                    const portfolio = sectionPortfolioSelector(getState(), section.id);
                    if (!portfolio === false) {
                      if (!portfolioSectionsMap[portfolio.id]) {
                        portfolioSectionsMap[portfolio.id] = [];
                      }
                      portfolioSectionsMap[portfolio.id].push(section);
                    }
                  }
                  dispatch(updateSectionAction(portfolioSectionsMap));
                  dispatch(updateDashboardAction([udpatedSections.map(item => item.id)]));
                }
              })
              .catch(_ => {
                // captureError(getState(), apiError);
              });

            ApiClient.getSheetIrr(getUuid(), currentPortfolio.id)
              .then(apiData => {
                if (!apiData === true) {
                  return;
                }

                const updatedSheets = apiData.payload;
                if (updatedSheets.length > 0) {
                  const portfolioSheetsMap = {};
                  for (const sheet of updatedSheets) {
                    const portfolio = sheetPortfolioSelector(getState(), sheet.id);
                    if (!portfolio === false) {
                      if (!portfolioSheetsMap[portfolio.id]) {
                        portfolioSheetsMap[portfolio.id] = [];
                      }
                      portfolioSheetsMap[portfolio.id].push(sheet);
                    }
                  }
                  dispatch(updateSheetAction(portfolioSheetsMap));
                  dispatch(updateDashboardAction([updatedSheets.map(item => item.id)]));
                }
              })
              .catch(_ => {
                // captureError(getState(), apiError);
              });
          }
        })
        .catch(apiError => {
          if (apiError.errorCode === apiErrorCodes.PRODUCT_NOT_READY) {
            setTimeout(() => {
              fetchUpdatedIrr(apiError);
            }, 5000 * retryCount);
          } else {
            captureError(getState(), apiError);
          }
        });
    };

    setTimeout(() => {
      fetchUpdatedIrr();
    }, 10 * 1000);
  };
};

export const searchPortfolioForText = (
  searchText,
  searchLinkedPortfolios,
  categoryAllowed = null,
  includeHoldingsOnProviderMatch,
  onSuccess
) => {
  return (dispatch, getState) => {
    const searchQuery = searchText.trim().toLowerCase();
    const data = { searchText: searchText, results: [] };
    const tokens = searchQuery.split(/\s+/);
    const currentPortfolio = currentPortfolioSelector(getState());
    const portfoliosToSearch = [
      currentPortfolio,
      ...(searchLinkedPortfolios ? connectedPortfoliosSelector(getState()) : [])
    ];

    const insertCustodianResult = (custodian, portfolio) => {
      const result = custodian;
      const sheet = custodianSheetSelector(getState(), result.id);

      if (
        sheet &&
        sheet.category !== categoryType.INSURANCE &&
        (!categoryAllowed === true || sheet.category === categoryAllowed)
      ) {
        const section = sectionSelector(getState(), result.sectionId);

        result.total = getCustodianValue(
          getState(),
          custodian,
          sheet.category,
          getTickerUsingShortName(getState(), currentPortfolio.currency)
        );
        result.sheet = sheet;
        result.section = section;
        result.isCustodian = true;

        if (!result.parentId === false) {
          result.parent = custodianSelector(getState(), result.parentId);
        }
        result.portfolioId = portfolio.id;
        result.portfolioName = portfolio.name;
        data.results.push(result);
      }
    };

    const insertedSectionIds = new Set();
    const insertSectionResult = (section, portfolio) => {
      const result = section;
      const sheet = sheetSelector(getState(), result.sheetId);

      if (insertedSectionIds.has(result.id)) {
        return;
      }

      if (
        sheet &&
        sheet.category !== categoryType.INSURANCE &&
        (!categoryAllowed === true || sheet.category === categoryAllowed)
      ) {
        insertedSectionIds.add(result.id);
        result.total = convertCurrency(
          getState(),
          getTotalForSection(getState(), portfolio, section),
          portfolio.currency,
          currentPortfolio.currency
        );
        result.sheet = sheet;
        result.isSection = true;
        result.portfolioId = portfolio.id;
        result.portfolioName = portfolio.name;
        data.results.push(result);
      }
    };

    const insertSheetResult = (sheet, portfolio) => {
      const result = sheet;
      if (
        result.category !== categoryType.INSURANCE &&
        (!categoryAllowed === true || sheet.category === categoryAllowed)
      ) {
        result.total = convertCurrency(
          getState(),
          getTotalForSheet(getState(), portfolio, sheet),
          portfolio.currency,
          currentPortfolio.currency
        );
        result.isSheet = true;
        result.portfolioId = portfolio.id;
        result.portfolioName = portfolio.name;
        data.results.push(result);
      }
    };

    for (const portfolioToSearch of portfoliosToSearch) {
      for (const custodian of portfolioToSearch.details.custodian) {
        let matching = true;
        for (const token of tokens) {
          let ticker;
          if (custodian.valueTickerId) {
            ticker = getTickerUsingId(getState(), custodian.valueTickerId);
          }
          if (
            (!custodian.parentId === false || custodian.hidden !== 1) &&
            ((custodian.name && custodian.name.toLowerCase().includes(token)) ||
              (custodian.description && custodian.description.toLowerCase().includes(token)) ||
              (custodian.note && custodian.note.toLowerCase().includes(token)) ||
              ((custodian.hidden !== 1 || includeHoldingsOnProviderMatch === true) &&
                custodian.linkProviderName &&
                custodian.linkProviderName.toLowerCase().includes(token)) ||
              (ticker &&
                ticker.shortName !== currentPortfolio.currency &&
                (ticker.shortName.toLowerCase().includes(token) || ticker.name.toLowerCase().includes(token))) ||
              (custodian.linkProviderAccountId &&
                custodian.linkType === accountLinkingService.ZERION &&
                custodian.linkProviderAccountId.toLowerCase().includes(token)) ||
              (custodian.symbol && custodian.symbol.toLowerCase().includes(token)))
          ) {
            continue;
          }
          matching = false;
          break;
        }
        if (!matching) {
          continue;
        }
        insertCustodianResult(custodian, portfolioToSearch);
      }

      for (const section of portfolioToSearch.details.section) {
        const sectionName = section.name ? section.name.toLowerCase() : "";
        let matching = true;
        for (const token of tokens) {
          if (!sectionName.includes(token)) {
            matching = false;
            break;
          }
        }
        if (!matching) {
          continue;
        }
        insertSectionResult(section, portfolioToSearch);
      }

      for (const sheet of portfolioToSearch.details.sheet) {
        const sheetName = sheet.name ? sheet.name.toLowerCase() : "";
        let matching = true;
        for (const token of tokens) {
          if (!sheetName.includes(token)) {
            matching = false;
            break;
          }
        }
        if (!matching) {
          continue;
        }
        insertSheetResult(sheet, portfolioToSearch);

        const sections = sheetSectionsSelector(getState(), portfolioToSearch.id, sheet.id);
        for (const section of sections) {
          insertSectionResult(section, portfolioToSearch);
        }
      }
    }

    // Match weight: exact = 3, starts-with = 2, contains = 1, no match = 0.
    const matchWeight = str => {
      if (!str) return 0;
      const s = String(str).toLowerCase();
      if (s === searchQuery) return 3;
      if (s.startsWith(searchQuery)) return 2;
      if (s.includes(searchQuery)) return 1;
      return 0;
    };

    // Assign each result to a tier: 0 = exact ticker match, 1 = exact name match, 2 = rest.
    // Within a tier, only value matters.
    const hasExactTickerMatch = result => {
      if (result.isCustodian === true) {
        const ticker = result.valueTickerId ? getTickerUsingId(getState(), result.valueTickerId) : null;
        const symbol = ticker ? getSymbolForTickerUsingShortName(getState(), ticker.shortName) : null;
        if (symbol && matchWeight(symbol) === 3) return true;
        if (ticker && ticker.code && matchWeight(ticker.code) === 3) return true;
        if (ticker && matchWeight(ticker.shortName) === 3) return true;
        if (ticker && matchWeight(ticker.name) === 3) return true;
      }
      return false;
    };

    const hasExactNameMatch = result => matchWeight(result.name) === 3;

    const tier = result => {
      if (hasExactTickerMatch(result)) return 0;
      if (hasExactNameMatch(result)) return 1;
      return 2;
    };

    const groupRank = result => (result.isCustodian === true ? 0 : 1);

    data.results.sort((a, b) => {
      // Custodians always first
      const ag = groupRank(a);
      const bg = groupRank(b);
      if (ag !== bg) return ag - bg;
      // Tier: exact ticker > exact name > rest
      const at = tier(a);
      const bt = tier(b);
      if (at !== bt) return at - bt;
      // Sort by value within all tiers
      return (b.total || 0) - (a.total || 0);
    });

    onSuccess(data);
  };
};

export const updateCustodianOwnership = (custodianId, ownership) => {
  return (dispatch, getState) => {
    return new Promise(resolve => {
      const custodian = custodianSelector(getState(), custodianId);
      const currentPortfolio = currentPortfolioSelector(getState());
      const children =
        !custodian.parentId === false
          ? custodiansWithSameParentIdSelector(getState(), currentPortfolio.id, custodian.parentId)
          : [];
      const affectedCustodianIds =
        !custodian.parentId === false ? [custodian.parentId, ...children.map(child => child.id)] : [custodianId];

      const valuesBeforeUpdate = {};
      for (const id of affectedCustodianIds) {
        valuesBeforeUpdate[id] = getCustodianValueById(getState(), id);
      }

      if (!custodian.parentId === false) {
        dispatch(
          updateCustodian(
            false,
            custodian.parentId,
            {
              ownership
            },
            false,
            newCustodian => {
              resolve(newCustodian);
            }
          )
        );
        const custodiansToBulkUpdate = [];
        for (let child of children) {
          child = { ...child, ownership };
          custodiansToBulkUpdate.push(child);
        }
        dispatch(updateCustodianInBulkAction(currentPortfolio.id, custodiansToBulkUpdate));
        dispatch(updateDashboardAction(children.map(child => child.id)));
      } else {
        dispatch(
          updateCustodian(
            false,
            custodianId,
            {
              ownership
            },
            true,
            newCustodian => {
              resolve(newCustodian);
            }
          )
        );
      }

      const deltasByCustodianId = {};
      for (const id of affectedCustodianIds) {
        deltasByCustodianId[id] = getCustodianValueById(getState(), id) - valuesBeforeUpdate[id];
      }
      dispatch(applyChangeDataDeltas(deltasByCustodianId));
    });
  };
};

export const updateTargetForReportsCustodian = (reportId, tabName, contentId, targetValue) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const reportPreferences = reportPreferencesSelector(getState(), currentPortfolio);
    const reportParams = parseParams(decodeURIComponent(reportId));

    const chartOption = reportParams.chart_option;
    const reportNodeId = reportParams.report_node_id;
    const reportPreferencesForSelectedReport = reportPreferences.find(
      preference => preference.reportType === chartOption
    );
    var updatedReportPreferencesData =
      reportPreferencesForSelectedReport && reportPreferencesForSelectedReport.data
        ? reportPreferencesForSelectedReport.data
        : {};
    updatedReportPreferencesData[reportNodeId] = updatedReportPreferencesData[reportNodeId] || {};
    updatedReportPreferencesData[reportNodeId][tabName] = updatedReportPreferencesData[reportNodeId][tabName] || {};
    updatedReportPreferencesData[reportNodeId][tabName][contentId] =
      updatedReportPreferencesData[reportNodeId][tabName][contentId] || {};

    if (targetValue === "") {
      delete updatedReportPreferencesData[reportNodeId][tabName][contentId].target;
    } else {
      updatedReportPreferencesData[reportNodeId][tabName][contentId].target = parseNumberStringToFloat(
        getState(),
        targetValue
      );
    }
    const updatedReportPreferences = {};
    updatedReportPreferences.data = updatedReportPreferencesData;
    updatedReportPreferences.portfolioId = currentPortfolio.id;
    updatedReportPreferences.reportType = chartOption;
    dispatch(updateReportPreference(currentPortfolio.id, chartOption, updatedReportPreferences));
  };
};

export const setUndoQueue = fn => {
  undoQueue.unshift(fn);
  undoQueue.length = Math.min(undoQueue.length, 5);
};

export const setRedoQueue = fn => {
  redoQueue.unshift(fn);
  redoQueue.length = Math.min(redoQueue.length, 5);
};

(function undoRedoHandler() {
  const inputs = ["input", "select", "button", "textarea", "div"];
  document.addEventListener("keydown", evt => {
    const activeElement = document.activeElement;
    if (
      activeElement &&
      inputs.indexOf(activeElement.tagName.toLowerCase()) !== -1 &&
      (activeElement.getAttribute("data-cell-mode") !== "select" ||
        activeElement.getAttribute("data-cell-mode") === null)
    ) {
      return false;
    }

    const undoOperation = (undoFn = () => false) => {
      const isUndoApproved = undoFn();
      if (isUndoApproved) {
        undoQueue.shift();
      }
    };

    const redoOperation = (redoFn = () => false) => {
      const isRedoApproved = redoFn();
      if (isRedoApproved) {
        redoQueue.shift();
      }
    };

    if (evt.metaKey || evt.ctrlKey) {
      switch (evt.which) {
        case 89:
          evt.preventDefault();
          evt.stopPropagation();
          redoOperation(redoQueue[0]);
          break;
        case 90:
          if (evt.shiftKey) {
            evt.preventDefault();
            evt.stopPropagation();
            redoOperation(redoQueue[0]);
          } else {
            evt.preventDefault();
            evt.stopPropagation();
            undoOperation(undoQueue[0]);
          }
          break;
      }
    }
  });
})();

export const getRecommendationForReports = (
  state,
  total,
  custodianValue,
  actualPercentage,
  targetPercentage,
  currency
) => {
  const parsedTP = parseNumberStringToFloat(state, targetPercentage);
  if (actualPercentage === parsedTP || targetPercentage === "") {
    return null;
  }
  const targetValue = (total * parsedTP) / 100;
  const diff = Math.floor(targetValue - custodianValue);
  if (diff > 0) {
    // return `+${formatNumberWithCurrency(state, Math.abs(diff), currency)}`;
    return `+${shortFormatNumberWithCurrency(state, Math.abs(diff), currency, true, true, false)}`;
  } else if (diff < 0) {
    // return `-${formatNumberWithCurrency(state, Math.abs(diff), currency)}`;
    return `-${shortFormatNumberWithCurrency(state, Math.abs(diff), currency, true, true, false)}`;
  } else {
    return null;
  }
};

export const updateDashboardCharts = (dashboardCharts, addToQueue = true) => {
  return (dispatch, getState) => {
    if (dashboardCharts.columns.column1.chartIds?.length || dashboardCharts.columns.column2.chartIds?.length) {
      const currentPortfolio = currentPortfolioSelector(getState());
      const portfolio = portfolioSelector(getState(), currentPortfolio.id);
      const portfolioId = portfolio.id;
      const updatedOptions = { ...dashboardCharts };
      dispatch(updateDiyChartsAction(portfolioId, updatedOptions));
      if (isAppInViewMode() === false && currentPortfolio.write === 1) {
        const queueUpdate = () => {
          if (addToQueue) {
            const request = idempotentId =>
              ApiClient.updateDiyCharts(idempotentId, portfolioId, {
                portfolioId: portfolioId,
                data: updatedOptions
              });
            const syncItem = new SyncItem(
              SyncItemType.UPDATE,
              portfolioId + "options",
              request,
              3000,
              true,
              apiData => {}
            );
            dispatch(enqueueItem(syncItem));
          } else {
            ApiClient.updateDiyCharts(getUuid(), portfolioId, {
              portfolioId: portfolioId,
              data: updatedOptions
            });
          }
        };

        queueUpdate();
      }
    }
  };
};

export const getPortfolioFundScheduleData = () => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    if (!currentPortfolio === true) {
      return;
    }

    ApiClient.getPortfolioFundSchedule(getUuid(), currentPortfolio.id)
      .then(apiData => {
        dispatch(getFundScheduleDataSuccessAction(currentPortfolio.id, apiData.payload));
      })
      .catch(_ => {});
  };
};

const CONNECTIVITY_CENTER_EXCLUDED_LINK_TYPES = [
  accountLinkingService.ZILLOW,
  accountLinkingService.DOMAINS,
  accountLinkingService.CARS,
  accountLinkingService.AI_APPRAISER
];

export const getConnectivityCenterData = () => {
  return (dispatch, getState) => {
    if (fetchPortfolioPendingSelector(getState())) {
      return;
    }
    Promise.all([ApiClient.getConnectivityCenterData()])
      .then(([response]) => {
        if (!response?.payload) {
          return;
        }

        const connectivityCenterData = response.payload.map(portfolioData => {
          portfolioData.connectivityCenter = portfolioData.connectivityCenter
            .filter(data => CONNECTIVITY_CENTER_EXCLUDED_LINK_TYPES.includes(data.linkType) === false)
            .sort((currentData, nextData) => {
              if (
                (isCryptoLinkingService(currentData.linkType) && isCryptoLinkingService(nextData.linkType)) ||
                (!isCryptoLinkingService(currentData.linkType) && !isCryptoLinkingService(nextData.linkType))
              ) {
                // sort by alphabetical order with fiat or crypto
                return currentData.providerName < nextData.providerName ? -1 : 1;
              } else {
                // handling scenerio when adjacent data are of different types
                if (isCryptoLinkingService(currentData.linkType) && !isCryptoLinkingService(nextData.linkType)) {
                  return 1;
                } else if (!isCryptoLinkingService(currentData.linkType) && isCryptoLinkingService(nextData.linkType)) {
                  return -1;
                }
              }
            });
          return portfolioData;
        });
        dispatch(getConnectivityCenterDataSuccessAction(connectivityCenterData));
      })
      .catch(error => {
        captureError(getState(), error);
        dispatch(getConnectivityCenterDataErrorAction(error));
      });
  };
};

export const updateShowLoaderStatusForAConnection = (connection, portfolioId, showLoader = true) => {
  return (dispatch, getState) => {
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioId = portfolioId || currentPortfolio.id;
    const connectivityCenterDataForAPortfolio = connectivityCenterDataForPortfolioSelector(
      getState(),
      currentPortfolioId
    );
    const connectionIndex = connectivityCenterDataForAPortfolio.findIndex(
      data => data.custodianId === connection.custodianId
    );
    if (connectionIndex !== -1) {
      const connectionData = connectivityCenterDataForAPortfolio[connectionIndex];
      connectionData.showLoader = showLoader;
      connectivityCenterDataForAPortfolio[connectionIndex] = connectionData;
      const updatedConnectivityCenterDataForAPortfolio = [...connectivityCenterDataForAPortfolio];
      dispatch(
        updateConnectivityCenterDataForAPortfolioAction(currentPortfolioId, updatedConnectivityCenterDataForAPortfolio)
      );
    }
  };
};

export const updateCustodianRate = (custodian, data, uuid = getUuid(), onSuccess = () => null) => {
  return (dispatch, getState) => {
    const parsedRate = JSON.parse(data?.rate || null);
    if (!parsedRate?.p && parsedRate.p !== 0) {
      return;
    }
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentCustodian = custodianSelector(getState(), custodian.id);
    const custodianDetails = custodianDetailsSelector(getState());
    const rateHistory = custodianDetails?.rate;

    const foundIndex = !rateHistory ? -1 : rateHistory.findIndex(rate => rate.id === uuid || rate.date === data.date);
    if (foundIndex === 0) {
      currentCustodian.rate = data?.rate;
      dispatch(updateCustodianLocal(currentCustodian));
      dispatch(updateDashboardAction([currentCustodian.id]));
    }

    if (custodianDetails) {
      const newCustodianDetails = custodianDetails;
      if (!rateHistory) {
        newCustodianDetails.rate = [];
      }
      if (foundIndex === -1) {
        const newEntry = {
          ...data,
          id: data.id || uuid,
          custodianId: currentCustodian.id
        };

        newCustodianDetails.rate = [...newCustodianDetails.rate];
        newCustodianDetails.rate.push(newEntry);
      } else {
        newCustodianDetails.rate = [...newCustodianDetails.rate];
        newCustodianDetails.rate[foundIndex].rate = data.rate;
        newCustodianDetails.rate[foundIndex].date = data.date;
      }
      newCustodianDetails.rate.sort((a, b) => b.date.localeCompare(a.date));
      dispatch(updateCustodianDetailsAction(newCustodianDetails));
    }

    const handlePayload = payload => {
      const updatedCustodian = payload.info;
      const custodian = custodianSelector(getState(), updatedCustodian.id);
      if (getIrrValue(custodian?.irr, true) !== getIrrValue(updatedCustodian.irr, true)) {
        dispatch(updateSectionIrr([updatedCustodian.sectionId]));
      }

      dispatch(updateCustodianInBulkAction(currentPortfolio.id, [updatedCustodian]));

      payload.holdings?.forEach(holding => {
        eventBus.dispatch("updateCustodianHolding", [holding, holding]);
      });
    };

    if (foundIndex === -1) {
      const request = idempotentId =>
        ApiClient.createCustodianRate(idempotentId, currentCustodian.sectionId, currentCustodian.id, uuid, data);
      const syncItem = new SyncItem(SyncItemType.CREATE, currentCustodian.id, request, 0, false, apiData => {
        handlePayload(apiData.payload);
        const newCustodian = { ...currentCustodian, ...apiData.payload.info };
        const custodianDetails = custodianDetailsSelector(getState());
        if (custodianDetails?.info?.id === apiData.payload.info.id) {
          dispatch(updateCustodianDetailsAction({ ...custodianDetails, ...apiData.payload }));
        }
        dispatch(updateCustodianLocal(newCustodian));
        dispatch(updateDashboardAction([newCustodian.id]));
        onSuccess(newCustodian);
      });
      dispatch(enqueueItem(syncItem));
    } else {
      const request = idempotentId =>
        ApiClient.updateCustodianRate(
          idempotentId,
          currentCustodian.sectionId,
          currentCustodian.id,
          rateHistory[foundIndex].id,
          data
        );
      const syncItem = new SyncItem(SyncItemType.CREATE, currentCustodian.id, request, 0, false, apiData => {
        handlePayload(apiData.payload);
        const newCustodian = { ...currentCustodian, ...apiData.payload.info };
        const custodianDetails = custodianDetailsSelector(getState());
        if (custodianDetails?.info?.id === apiData.payload.info.id) {
          dispatch(updateCustodianDetailsAction({ ...custodianDetails, ...apiData.payload }));
        }
        dispatch(updateCustodianLocal(newCustodian));
        dispatch(updateDashboardAction([newCustodian.id]));
        onSuccess(newCustodian);
      });
      dispatch(enqueueItem(syncItem));
    }
  };
};

export const deleteCustodianRate = (custodian, uuid = getUuid(), onSuccess = () => null) => {
  return (dispatch, getState) => {
    const currentCustodian = custodianSelector(getState(), custodian.id);
    const custodianDetails = custodianDetailsSelector(getState());
    const rateHistory = custodianDetails?.rate;

    const foundIndex = !rateHistory ? -1 : rateHistory.findIndex(rate => rate.id === uuid);
    if (foundIndex === 0) {
      currentCustodian.rate = rateHistory[0].rate;
      dispatch(updateCustodianLocal(currentCustodian));
    }

    if (custodianDetails && foundIndex !== -1) {
      const newCustodianDetails = custodianDetails;
      newCustodianDetails.rate = [...newCustodianDetails.rate];
      newCustodianDetails.rate.splice(foundIndex, 1);
      dispatch(updateCustodianDetailsAction(newCustodianDetails));
    }
    const request = idempotentId =>
      ApiClient.deleteCustodianRate(idempotentId, custodian.sectionId, custodian.id, uuid);
    const syncItem = new SyncItem(SyncItemType.DELETE, custodian.id, request, 0, false, apiData => {
      onSuccess();
    });
    dispatch(enqueueItem(syncItem));
  };
};

export const canShowCommittedCapital = (state, custodian) => {
  if (custodian.linkType === accountLinkingService.KUBERA_PORTFOLIO) {
    return false;
  }
  const isManualCustodian = !custodian.linkType === true;
  const accountIsNotAHolding = !custodian.parentId;
  const isHoldingAndShownOutside = custodian.parentId && !custodian.hidden;
  if (isManualCustodian || accountIsNotAHolding || isHoldingAndShownOutside) {
    return true;
  }
  return false;
};

export const unmarkPrimaryResidenceCustodianForPortfolio = (portfolioId, custodianId) => {
  return (dispatch, getState) => {
    const primaryResidenceCustodian = primaryResidenceCustodianSelector(getState());
    for (const custodian of primaryResidenceCustodian) {
      if (custodian.id === custodianId) {
        continue;
      }
      custodian.subType = custodianSubTypes.HOME;
      if (!custodian.tsModified === false) {
        custodian.tsModified = Math.floor(new Date().getTime() / 1000);
      }
      dispatch(updateCustodianLocal(custodian));
    }
  };
};

const custodianFundamentalDataMap = {
  type: "tp",
  taxDetails: "td"
};

export const applyChangeDataDeltas = deltasByCustodianId => {
  return (dispatch, getState) => {
    const portfolioId = currentPortfolioSelector(getState()).id;
    const portfolioChangeData = currentPortfolioChangeDataSelector(getState());
    const changeData = portfolioChangeData?.data?.change;
    const custodianIds = Object.keys(deltasByCustodianId || {}).filter(id => !deltasByCustodianId[id] === false);
    if (!changeData || custodianIds.length === 0) {
      return;
    }

    const categoryByCustodianId = {};
    for (const id of custodianIds) {
      categoryByCustodianId[id] = custodianSheetSelector(getState(), id)?.category?.toLowerCase();
    }

    const updatedChangeData = {};
    Object.keys(changeData).forEach(key => {
      const changeDataForTimeRange = changeData[key];
      if (!changeDataForTimeRange) {
        updatedChangeData[key] = changeDataForTimeRange;
        return;
      }

      updatedChangeData[key] = { ...changeDataForTimeRange };

      for (const category of [categoryType.ASSET, categoryType.DEBT]) {
        const custodianCategory = category.toLowerCase();
        const idsForCategory = custodianIds.filter(id => categoryByCustodianId[id] === custodianCategory);
        if (idsForCategory.length === 0) {
          continue;
        }

        let changeTotalDelta = 0;
        updatedChangeData[key][custodianCategory] = (changeDataForTimeRange[custodianCategory] || []).map(custodian => {
          const changeValue = idsForCategory
            .filter(id => custodian?.cids?.includes(id))
            .reduce((total, id) => total + deltasByCustodianId[id], 0);
          if (changeValue === 0) {
            return custodian;
          }
          changeTotalDelta += changeValue;
          return { ...custodian, value: custodian.value + changeValue };
        });

        const totalKey = category === categoryType.ASSET ? "assetChangeTotal" : "debtChangeTotal";
        updatedChangeData[key][totalKey] = (updatedChangeData[key][totalKey] || 0) + changeTotalDelta;
      }
    });

    dispatch(
      updatePortfolioChangeDataAction(portfolioId, {
        ...portfolioChangeData,
        data: { ...portfolioChangeData.data, change: updatedChangeData }
      })
    );
  };
};

export const updateChangeData = (custodianId, propertiesToUpdate, custodianValueBeforeUpdate) => {
  return (dispatch, getState) => {
    if (!propertiesToUpdate) {
      return;
    }
    const valueExchangeRate = propertiesToUpdate?.valueExchangeRate
      ? JSON.parse(propertiesToUpdate?.valueExchangeRate)
      : null;
    const rawValue = propertiesToUpdate?.value * (valueExchangeRate?.rate || 1);
    const ownership =
      propertiesToUpdate?.ownership !== undefined
        ? propertiesToUpdate.ownership
        : custodianSelector(getState(), custodianId)?.ownership;
    const updatedValue = calcCustodianOwnershipValue(rawValue, ownership);
    dispatch(applyChangeDataDeltas({ [custodianId]: updatedValue - custodianValueBeforeUpdate }));
  };
};

export const updateRecapRawData = (
  custodianId,
  propertiesToUpdate,
  shouldUpdateCustodianValueForToday = false,
  custodianValueBeforeUpdate
) => {
  return (dispatch, getState) => {
    const updatedProperties = {};
    dispatch(updateChangeData(custodianId, propertiesToUpdate, custodianValueBeforeUpdate));
    for (const property of Object.keys(propertiesToUpdate)) {
      updatedProperties[custodianFundamentalDataMap[property]] = propertiesToUpdate[property];
    }
    const currentPortfolioId = currentPortfolioSelector(getState()).id;
    const recapRawData = recapRawDataSelector(getState());
    if (!recapRawData.portfolios) return;
    const portfolioData = recapRawData.portfolios.find(portfolio => portfolio.portfolioId === currentPortfolioId);
    const custodianFundamentalDataForPortfolio = portfolioData?.custodiansFundData;
    if (!custodianFundamentalDataForPortfolio) {
      return;
    }
    const custodianIndex = custodianFundamentalDataForPortfolio.findIndex(custodian => custodian.cid === custodianId);
    if (custodianIndex !== -1) {
      const custodianFundamentalDataForACustodian = custodianFundamentalDataForPortfolio[custodianIndex];
      const updatedCustodianFundamentalDataForACustodian = {
        ...custodianFundamentalDataForACustodian,
        ...updatedProperties
      };

      if (shouldUpdateCustodianValueForToday) {
        const groupByDayData = portfolioData?.groupByDay?.netWorth;
        if (!groupByDayData) {
          return;
        }
        const currentDayData = groupByDayData[groupByDayData.length - 1];
        const custodianDataIndex = currentDayData?.asset?.findIndex(
          data => data.id === custodianFundamentalDataForACustodian.id
        );
        if (custodianDataIndex !== -1) {
          let custodianData = currentDayData?.asset[custodianDataIndex];
          if (!custodianData) {
            return;
          }
          const valueExchangeRate = propertiesToUpdate?.valueExchangeRate
            ? JSON.parse(propertiesToUpdate?.valueExchangeRate)
            : null;
          const updatedValue = propertiesToUpdate.value * (valueExchangeRate?.rate || 1);
          custodianData.value = updatedValue;
          custodianData.rv = updatedValue;
          currentDayData.asset[custodianDataIndex] = custodianData;
          groupByDayData[groupByDayData.length - 1] = currentDayData;
          portfolioData.groupByDay.netWorth = groupByDayData;

          const tickerDetails = getTickerUsingId(getState(), updatedCustodianFundamentalDataForACustodian.vTId);

          updatedCustodianFundamentalDataForACustodian.vTTp = tickerDetails.type;
          updatedCustodianFundamentalDataForACustodian.vTSn = tickerDetails.shortName;
          updatedCustodianFundamentalDataForACustodian.vTCd = tickerDetails.code;
          updatedCustodianFundamentalDataForACustodian.vTCn = tickerDetails.currency;
        }
      }

      custodianFundamentalDataForPortfolio[custodianIndex] = updatedCustodianFundamentalDataForACustodian;
    }
    portfolioData.custodiansFundData = custodianFundamentalDataForPortfolio;

    const portfolioIndex = recapRawData.portfolios.findIndex(portfolio => portfolio.id === currentPortfolioId);
    if (portfolioIndex !== -1) {
      recapRawData.portfolios[portfolioIndex] = portfolioData;
    }

    const reportToBeProcessedMap = new Map();
    reportToBeProcessedMap.set(chartTimeRange.TODAY, Object.values(recapChartOptions).map(option => option.id));
    dispatch(
      processRecapDataFromCustodiansResponse(
        window.kbStructuredClone(recapRawData),
        currentPortfolioId,
        false,
        false,
        reportToBeProcessedMap,
        false,
        true
      )
    );
    reportsDataFetchTsPortfolioMap[currentPortfolioId] = Date.now();
    dispatch(updateRecapRawDataAction(recapRawData));
  };
};

export const updateRecapRawDataAction = updatedRecapRawData => ({
  type: UPDATE_RECAP_RAW_DATA_ACTION,
  updatedRecapRawData
});

const traversePortfolioTreeAndGetRecapData = recapDataFromPortfolioResponse => {
  return (dispatch, getState) => {
    const portfolioTreeData = portfolioTreeDataSelector(getState());
    const portfolioCurrency = currentPortfolioCurrencySelector(getState());
    const currentPortfolio = currentPortfolioSelector(getState());
    const currentPortfolioId = currentPortfolio.id;
    const currentPortfolioName = currentPortfolio.name;
    let portfolioLinkedCustodians = [];
    let portfolios = [];
    let directLinkedAssetCustodianId; // eslint-disable-line no-unused-vars
    let directLinkedAssetSectionId;
    let directLinkedDebtCustodianId; // eslint-disable-line no-unused-vars
    let directLinkedDebtSectionId;
    let directLinkedAssetOwnership;
    let directLinkedDebtOwnership;

    const portfoliosData = portfoliosSelector(getState());
    let parentPortfolioId = null; // eslint-disable-line no-unused-vars
    let rootChildrenPortfolioId;
    const tickerAdditionalMetaData = tickerAdditionalMetaDataSelector(getState());
    const globalClubbingNames = [];

    function dfs(node) {
      if (node?.id) {
        const portfolioData = portfoliosData.find(portfolio => portfolio.id === node.id);
        // root portfolio

        const portfolioDataForRecap = {};
        const groupByDayData = {};
        const groupByDayNetworthData = [];
        const custodiansFundData = [];
        const portfolioCustodians = portfolioData?.details?.custodian;
        const recapDataForCurrentDay = {};
        const assetListForCurrentDay = [];
        const debtListForCurrentDay = [];

        let linkedAssetCustodianIdForPortfolio;
        let linkedAssetSectionIdForPortfolio;
        let linkedAssetCustodianNameForPortfolio;
        let linkedAssetOwnershipForPortfolio;
        let linkedDebtCustodianIdForPortfolio;
        let linkedDebtSectionIdForPortfolio;
        let linkedDebtCustodianNameForPortfolio;
        let linkedDebtOwnershipForPortfolio;

        for (const [index, custodian] of portfolioCustodians.entries()) {
          const isParentCustodianWithHoldings = !custodian.parentId && custodian.holdingsCount > 0;
          if (custodian.autoAddedForEmptySections || isParentCustodianWithHoldings) {
            continue;
          }
          if (custodian.linkType === accountLinkingService.KUBERA_PORTFOLIO) {
            if (custodian.linkContainer === categoryType.ASSET) {
              linkedAssetCustodianIdForPortfolio = custodian.id;
              linkedAssetSectionIdForPortfolio = custodian.sectionId;
              linkedAssetCustodianNameForPortfolio = custodian.linkAccountName;
              linkedAssetOwnershipForPortfolio = custodian.ownership;
              if (currentPortfolioId === node.id) {
                directLinkedAssetCustodianId = custodian.linkProviderAccountId;
                directLinkedAssetSectionId = custodian.sectionId;
                directLinkedAssetOwnership = custodian.ownership;
              }
            } else {
              linkedDebtCustodianIdForPortfolio = custodian.id;
              linkedDebtSectionIdForPortfolio = custodian.sectionId;
              linkedDebtCustodianNameForPortfolio = custodian.linkAccountName;
              linkedDebtOwnershipForPortfolio = custodian.ownership;
              if (currentPortfolioId === node.id) {
                directLinkedDebtCustodianId = custodian.linkProviderAccountId;
                directLinkedDebtSectionId = custodian.sectionId;
                directLinkedDebtOwnership = custodian.ownership;
              }
            }

            const existingLinkedCustodian = portfolioLinkedCustodians.find(linkedCustodian => {
              if (rootChildrenPortfolioId) {
                return (
                  linkedCustodian.linkedPortfolioId === node.id &&
                  linkedCustodian.category === custodian.linkContainer &&
                  linkedCustodian.rootChildrenPortfolioId === rootChildrenPortfolioId
                );
              }
              return (
                linkedCustodian.linkedPortfolioId === node.id && linkedCustodian.category === custodian.linkContainer
              );
            });

            let portfolioLinkedCustodianItem = {
              category: custodian.linkContainer,
              linkedCustodianId: custodian.id,
              linkedCustodianName: custodian.linkAccountName,
              linkedCustodianSectionId: custodian.sectionId,
              linkedPortfolioId: custodian.linkProviderAccountId,
              linkedPortfolioName: custodian.linkProviderName,
              parentPortfolioId: portfolioData.id,
              parentPortfolioName: portfolioData.name,
              ownership: custodian.ownership,
              firstCustodianId: existingLinkedCustodian ? existingLinkedCustodian.firstCustodianId : custodian.id,
              firstCustodianSectionId: existingLinkedCustodian
                ? existingLinkedCustodian.firstCustodianSectionId
                : custodian.sectionId,
              rootChildrenPortfolioId: existingLinkedCustodian
                ? rootChildrenPortfolioId
                : custodian.linkProviderAccountId
            };

            portfolioLinkedCustodians.push(portfolioLinkedCustodianItem);
          } else {
            const custodianSheet = custodianSheetSelector(getState(), custodian.id);
            const custodianCategory = custodianSheet?.category;
            const tickerDetails = getTickerUsingId(getState(), custodian.valueTickerId);
            //
            if (tickerDetails.type !== "fiat") {
              const dataIndexInGlobalClubbingNames = globalClubbingNames.findIndex(
                data => data.tId === tickerDetails.id
              );
              if (dataIndexInGlobalClubbingNames === -1) {
                globalClubbingNames.push({
                  tId: tickerDetails.id,
                  gCn: custodian.name
                });
              } else {
                globalClubbingNames[dataIndexInGlobalClubbingNames].gCn = tickerDetails.name;
              }
            }
            const fundamentalData = {
              aCls: custodian.aCls,
              acId: custodian.assetClassId,
              cid: custodian.id,
              ctr: custodianCategory,
              hdn: custodian.hidden,
              id: index,
              n: custodian.name,
              sId: custodian.sectionId,
              sTp: custodian.subType,
              tp: custodian.type,
              vTCd: tickerDetails.code,
              vTCn: tickerDetails.currency,
              vTId: tickerDetails.id,
              vTSn: tickerDetails.shortName,
              vTTp: tickerDetails.type,
              vTSTp: tickerDetails.subType,
              cntN: tickerDetails.countryCode
            };
            const tickerAdditionalMetaDataItem = tickerAdditionalMetaData.find(ticker => ticker.i === tickerDetails.id);

            if (tickerAdditionalMetaDataItem) {
              fundamentalData.adlM = tickerAdditionalMetaDataItem.amt;
            }
            custodiansFundData.push(fundamentalData);
            if (custodianCategory === categoryType.ASSET) {
              assetListForCurrentDay.push({
                id: index,
                value: getCustodianValue(
                  getState(),
                  custodian,
                  custodianCategory,
                  getTickerUsingShortName(getState(), currentPortfolio.currency)
                )
              });
            } else if (custodianCategory === categoryType.DEBT) {
              debtListForCurrentDay.push({
                id: index,
                value: getCustodianValue(
                  getState(),
                  custodian,
                  custodianCategory,
                  getTickerUsingShortName(getState(), currentPortfolio.currency)
                )
              });
            }
          }
        }
        let assetTotalForDay = assetListForCurrentDay.reduce((acc, curr) => acc + curr.value, 0);
        let debtTotalForDay = debtListForCurrentDay.reduce((acc, curr) => acc + curr.value, 0);
        recapDataForCurrentDay.asset = assetListForCurrentDay;
        recapDataForCurrentDay.debt = debtListForCurrentDay;
        recapDataForCurrentDay.date = getKuberaDateString(toUserTimezoneDate());
        recapDataForCurrentDay.assetTotal = assetTotalForDay;
        recapDataForCurrentDay.debtTotal = debtTotalForDay;
        recapDataForCurrentDay.value = assetTotalForDay - debtTotalForDay;
        recapDataForCurrentDay.investibleTotal = assetListForCurrentDay
          .filter(custodian => {
            const isParentCustodianWithHoldings = !custodian.parentId && custodian.holdingsCount > 0;
            return (custodian.type === 2 || custodian.type === 0) && !isParentCustodianWithHoldings;
          })
          .reduce((acc, curr) => acc + curr.value, 0);
        recapDataForCurrentDay.currency = portfolioCurrency;

        groupByDayNetworthData.push(recapDataForCurrentDay);

        groupByDayData.netWorth = groupByDayNetworthData;
        groupByDayData.currency = portfolioCurrency;

        portfolioDataForRecap.groupByDay = groupByDayData;
        portfolioDataForRecap.custodiansFundData = custodiansFundData;
        portfolioDataForRecap.portfolioId = node.id;
        portfolioDataForRecap.rootPortfolioId = currentPortfolioId;
        portfolioDataForRecap.rootPortfolioName = currentPortfolioName;

        portfolioDataForRecap.linkedPortfolioId = currentPortfolio.linkedPortfolioId;
        portfolioDataForRecap.linkedPortfolioName = currentPortfolio.linkedPortfolioName;
        portfolioDataForRecap.parentPortfolioId = currentPortfolio.parentPortfolioId;
        portfolioDataForRecap.parentPortfolioName = currentPortfolio.parentPortfolioName;
        if (currentPortfolioId === node.id) {
          portfolioDataForRecap.root = true;
          portfolioDataForRecap.primary = true;
        } else {
          portfolioDataForRecap.linkedDebtCustodianId = linkedDebtCustodianIdForPortfolio;
          portfolioDataForRecap.linkedDebtCustodianName = linkedDebtCustodianNameForPortfolio;
          portfolioDataForRecap.linkedDebtCustodianSectionId = linkedDebtSectionIdForPortfolio;
          portfolioDataForRecap.linkedDebtCustodianOwnership = linkedDebtOwnershipForPortfolio;
          portfolioDataForRecap.directLinkedDebtCustodianId = rootChildrenPortfolioId;
          // portfolioDataForRecap.directLinkedDebtCustodianName = directLinkedDebtCustodianName;
          portfolioDataForRecap.directLinkedDebtCustodianSectionId = directLinkedDebtSectionId;
          portfolioDataForRecap.directLinkedDebtCustodianOwnership = directLinkedDebtOwnership;

          portfolioDataForRecap.linkedAssetCustodianId = linkedAssetCustodianIdForPortfolio;
          portfolioDataForRecap.linkedAssetCustodianName = linkedAssetCustodianNameForPortfolio;
          portfolioDataForRecap.linkedAssetCustodianSectionId = linkedAssetSectionIdForPortfolio;
          portfolioDataForRecap.linkedAssetCustodianOwnership = linkedAssetOwnershipForPortfolio;
          portfolioDataForRecap.directLinkedAssetCustodianId = rootChildrenPortfolioId;
          // portfolioDataForRecap.directLinkedAssetCustodianName;
          portfolioDataForRecap.directLinkedAssetCustodianSectionId = directLinkedAssetSectionId;
          portfolioDataForRecap.directLinkedAssetCustodianOwnership = directLinkedAssetOwnership;
        }
        if (!portfolios.find(portfolio => portfolio.portfolioId === node.id)) {
          portfolios.push(portfolioDataForRecap);
        }
      }
      for (const child of node?.children || []) {
        if (node.id === currentPortfolioId) {
          rootChildrenPortfolioId = child.id;
        }
        dfs(child);
      }
      parentPortfolioId = node?.id || null;
    }
    if (portfolioTreeData.children.length > 0) {
      const currentPortfolioInTree = portfolioTreeData.children.find(child => child.id === currentPortfolioId);
      dfs(currentPortfolioInTree);
    }
    recapDataFromPortfolioResponse.portfolioLinkedCustodians = portfolioLinkedCustodians;
    recapDataFromPortfolioResponse.portfolios = portfolios;
    recapDataFromPortfolioResponse.globalClubbingNames = globalClubbingNames;
    const reportToBeProcessedMap = getChartAndTimeRangeToProcessSelector(getState(), false, true);
    console.log("reportToBeProcessedMap", reportToBeProcessedMap);
    dispatch(
      processRecapDataFromCustodiansResponse(
        recapDataFromPortfolioResponse,
        currentPortfolioId,
        true,
        true,
        reportToBeProcessedMap,
        false,
        false,
        true
      )
    );
  };
};

export const getRecapDataFromPortfolioResponseForDashboard = () => {
  return (dispatch, getState) => {
    const stockSectorList = stockSectorListSelector(getState());
    const tickerMarketCapList = stockMarketCapListSelector(getState());
    const tickerRegionList = stockRegionListSelector(getState());
    const cryptoSectorList = cryptoSectorListSelector(getState());
    let recapDataFromPortfolioResponse = {};
    recapDataFromPortfolioResponse.stockSectors = stockSectorList;
    recapDataFromPortfolioResponse.cryptoSectors = cryptoSectorList;
    recapDataFromPortfolioResponse.tickerRegionList = tickerRegionList;
    recapDataFromPortfolioResponse.tickerMcapList2 = tickerMarketCapList;

    dispatch(traversePortfolioTreeAndGetRecapData(recapDataFromPortfolioResponse));
  };
};

const eventMethods = { fetchPortfolios };

eventBus.addEventListener("any", event => {
  const { eventName, data } = event.detail;
  if (eventMethods[eventName]) {
    store.dispatch(eventMethods[eventName].apply(this, data));
  }
});
